import { useState, useEffect, useCallback, useMemo, useRef, useReducer } from "react";
import { filterToSheet, groupBySheet } from "../../utils/sheetScope";
import { sheetTakeoffCounts } from "../../utils/sheetNavigation";
import FeetInchesInput from "../../components/FeetInchesInput";
import { snapToSixteenth } from "../../utils/feetInches";
import { applyTypeOverrides } from "../../utils/conditionTypeOverrides";
import { oneOffPayload } from "../../utils/oneOffCondition";
import { useTakeoffUiTheme } from "../../utils/takeoffTheme";
import { initialHealth, loadHealthReducer, loadStatus, describeLoadFailure } from "../../utils/loadHealth";
import { isVersionConflict, planConflictRetry, changedFields, describeConflict } from "../../utils/versionConflict";
import { pointsOfRow } from "../../utils/conditionClipboard";
import { planTransform, transformNotice } from "../../utils/rotateConditions";
import { duplicateSheetName, duplicateSheetRequest, canDuplicateSheet } from "../../utils/duplicateSheet";

// The room fields a save can change — a whole-row save is diffed against the row as loaded to find what it touches.
const ROOM_SAVE_KEYS = ["RoomName", "RoomNumber", "RoomType", "LengthFt", "WidthFt", "HeightFt", "ACH", "PressureTarget", "AHUZoneID", "ClassificationID", "RoomTemplateID", "WallKind", "WallItemID", "WallAssemblyID", "WallSystemsJson", "CanvasX", "CanvasY", "CanvasRot", "SheetID", "DetectionStatus", "PositionLocked"];
import { sheetNeedsScale, SCALE_REQUIRED_MESSAGE } from "../../utils/scaleDetect";
import { scaleFactor, isMaterialChange, buildSheetRescale } from "../../utils/rescaleTakeoffs";
import TakeoffExportMenu from "./TakeoffExportMenu";
import TakeoffReport from "./TakeoffReport";
import TakeoffFileMenu from "./TakeoffFileMenu";
import { buildTakeoffFile, serializeTakeoffFile, takeoffFileName } from "../../utils/takeoffFile";
import { planArchive, buildArchive, archiveFileName } from "../../utils/takeoffArchive";
import { templateAch, achMismatch } from "../../utils/roomAch";
import { sha256Hex } from "../../utils/fileHash";
import { isBlobPointer, blobPointer, drawingsConfigured } from "../../utils/blobStorage";
import DrawingSetDialog from "./DrawingSetDialog";
import TakeoffSnapshotsMenu from "./TakeoffSnapshotsMenu";
import { gridTileTakeoff, gridResultsEqual } from "../../utils/gridTakeoff";
import { useSearchParams } from "react-router-dom";
import { sqlReadRaw as sqlRead, payloadOf } from "../../utils/sqlClient";
import { CommandFailed, CommandStale } from "../../utils/commandHistory";
import { provisionalConditionRow, isProvisionalId } from "../../utils/optimisticCondition";
import { useCommandHistory } from "../../utils/useCommandHistory";
import { correctionStamp, roomCorrectionPatch, summarizeUnreviewed, describeUnreviewed } from "../../utils/machineReview";
import { conditionRowToPayload, conditionPatchPayload, pickFields, patchRowById, roomMovePatch, roomResizePatch, ROOM_MOVE_KEYS, ROOM_RESIZE_KEYS, ROOM_RESHAPE_KEYS } from "../../utils/takeoffCommands";
import { usePolledJob } from "../../hooks/usePolledJob";
import { authHeader } from "../../utils/apiToken";
import { TAKEOFF_ANALYSIS_URL, WALL_SEGMENTS_URL } from "../../config/api";
import { AHU_TYPES, RETURN_FPM, calcRoom, calcAreas, roundDia, DEFAULT_DUCT_TUNABLES } from "../../utils/takeoffMath";
import { calcRoomSystems, buildBodOptionAttrs, DEFAULT_SYSTEMS_TUNABLES } from "../../utils/roomSystemsEngine";
import { buildWallModel, predictWallConstruction } from "../../utils/learning/wallLearning";
import { DEFAULT_LEARNING_TUNABLES } from "../../utils/learning/learningCore";
import { computeAutoSystemPlan, resolveAutoSystemEntity, DEFAULT_LAYOUT_TUNABLES, AUTO_SYSTEMS } from "../../utils/autoLayoutEngine";
import { groupElementsByRoom } from "../../config/roomElements";
import { openingKindOf, suggestOpeningMark, symbolGeometryWithWidth } from "../../config/openings";
import { publicationPayload } from "../../utils/schedulePublication";
import { buildScenarioModel, scenarioEquipmentDispositions } from "../../utils/designScenarios";
import { polylineLengthFt, polylineTurns, routeSegmentPayload, LEVEL_BY_KEY } from "../../utils/pipeSizing";
import { utilityLabel } from "../../config/utilityTypes";
import { useMsal } from "@azure/msal-react";
import { roomsAlongPolyline, roomContainingPolygon, detectEdges, wallTakeoff, edgeOverrideKey, polygonOf, polygonArea, polygonPerimeter, computeAutoWallSegments, pairKey, simplifyPolygon, classifyConfidence, resolveConstructionRule, effectiveWallSystems, parseWallSystemsJson, encodeWallSystems, DEFAULT_TUNABLES } from "../../utils/takeoffGeometry";
import TakeoffCanvas from "./TakeoffCanvas";
import MechanicalSystemsPanel from "./MechanicalSystemsPanel";
import TakeoffCriteriaPanel, { RoomCriteriaExpand } from "./TakeoffCriteriaPanel";
import { DisciplineView, SystemView, GridView } from "./TakeoffViews";
import DesignSchedulesView from "./DesignSchedulesView";
import RevisionCompare from "./RevisionCompare";
import { criterionKey, buildBodOptionsMap } from "../../config/takeoffCriteria";
import { optionSetForItem } from "../../config/scopeOptions";
import { takeoffChannelName } from "../../utils/takeoffLiveLink";

// Room Takeoff (Phase 1) — the room schedule that drives the parametric
// estimate: everything is a function of room dimensions. Rooms are entered
// here (type sets the ACH default), assigned to AHU zones, and the derived
// quantities (areas, CFM, HEPA counts, per-zone totals) update live using the
// same math as the standalone Duct Estimator (src/utils/takeoffMath.js).
// Revision-keyed like the BOD checklist and scope register; carried forward
// by sp_CreateRevision. Phase 2 adds the drag-and-drop layout canvas
// (shared-wall dedup, adjacency); Phase 3 adds equipment anchors + single-line
// routing. Estimate-line generation arrives with the reference-pricing work —
// explicitly regenerated, never automatic.

const fmt = (n, d = 0) => (n === null || n === undefined || isNaN(n)) ? "—"
  : Number(n).toLocaleString("en-US", { maximumFractionDigits: d, minimumFractionDigits: d });

// By Room schedule headers — key: null means not sortable (the trailing
// delete-icon column). Room # sorts numerically when every value parses as
// a number, otherwise falls back to plain text so an overwritten "101A"-style
// number still sorts sensibly among the auto-numbered ones.
const SCHEDULE_COLUMNS = [
  { label: "#",       key: "roomNumber" },
  { label: "Room",    key: "room" },
  { label: "Class",   key: "class" },
  { label: "Template", key: "template" },
  { label: "L (ft)",  key: "length" },
  { label: "W (ft)",  key: "width" },
  { label: "H (ft)",  key: "height" },
  { label: "ACH",     key: "ach" },
  { label: "AHU",     key: "ahu" },
  { label: "Derived", key: "derived" },
  { label: "",        key: null },
];

// Condition Library / drawable-Items-Assemblies TypeKey (Phase B of
// consolidating Condition Library into Items/Assemblies — see project
// memory): a drawable "type" can now come from either catalog, and their
// numeric PKs can collide (an ItemID of 7 and a TakeoffConditionLibraryID
// of 7 are different things), so every entry in the merged `conditionTypes`
// list carries one opaque string identity instead — "ct:<id>" for a
// Condition Library type, "item:<id>"/"asm:<id>" for a catalog row. This is
// the only place that encoding/decoding happens; TakeoffCanvas.jsx and
// ConditionDetailFlyout.jsx just carry the string around unchanged.
// decodeTypeKey() is the inverse, used right before building a
// sp_SaveTakeoffConditionDrawn payload — returns exactly one of
// {condition_type_id} or {kind, item_id, assembly_id} populated.
function decodeTypeKey(key) {
  if (!key) return { condition_type_id: null, kind: null, item_id: null, assembly_id: null };
  const [prefix, idStr] = key.split(":");
  const id = +idStr;
  if (prefix === "item") return { condition_type_id: null, kind: "item", item_id: id, assembly_id: null };
  if (prefix === "asm") return { condition_type_id: null, kind: "assembly", item_id: null, assembly_id: id };
  return { condition_type_id: id, kind: null, item_id: null, assembly_id: null };
}

// PolygonJson stores ABSOLUTE world-feet points — see moveRoom's own
// comment for why this has to run on every drag of an already-polygon room.
function translatePolygonJsonForSave(polygonJson, dx, dy) {
  if (!polygonJson || (!dx && !dy)) return polygonJson;
  try {
    const geo = JSON.parse(polygonJson);
    if (!Array.isArray(geo.points)) return polygonJson;
    return JSON.stringify({
      ...geo,
      points: geo.points.map(p => ({ x: Math.round((p.x + dx) * 10) / 10, y: Math.round((p.y + dy) * 10) / 10 })),
    });
  } catch { return polygonJson; }
}

// Derivations guard on complete dimensions so half-entered rows show "—".
// A room with an AI-traced polygon (non-rectangular boundary) feeds its real
// area/perimeter into the same math instead of the bounding-box L*W/2*(L+W)
// every polygon room still carries for rect-only canvas features.
//
// HEPA / lighting counts come from the engineering layer
// (src/utils/roomSystemsEngine.js — airflow + coverage constraints, lumen
// method, explicit overrides); calcRoom() is then handed that EFFECTIVE HEPA
// count and the selected filter's design airflow so the duct sizing follows
// the same answer the estimator sees everywhere else.
function deriveRoom(r, criteria = {}, systemsCtx = {}) {
  const L = +r.LengthFt, W = +r.WidthFt, H = +r.HeightFt, ach = +r.ACH;
  if (!(L > 0) || !(W > 0) || !(H > 0)) return null;
  const poly = polygonOf(r);
  const overrides = poly
    ? { areaOverrideFt2: polygonArea(poly), perimeterOverrideFt: polygonPerimeter(poly) }
    : {};
  const areas = calcAreas({ length: L, width: W, height: H, ...overrides });
  const systems = calcRoomSystems(r, criteria, systemsCtx);
  const hepa = systems.hvac.hepa;
  const air = ach > 0
    ? calcRoom({
        length: L, width: W, height: H, ach, ...overrides,
        designCfmPerHepa: hepa.designAirflowPerUnit,
        hepaCountOverride: hepa.effectiveQuantity,
      }, systemsCtx.tunables)
    : null;
  return { ...areas, ...(air || {}), systems, lightCount: systems.electrical.lighting.effectiveQuantity };
}

// ── Persisted engineering results (TakeoffRoomEngineering, db/69) ─────────────
// The live calcRoomSystems() result reshaped into sp_SaveTakeoffRoomEngineering's
// payload row, plus the generated-symbol counts the auto-layout sync just
// decided on (autoLayoutEngine.js). Numbers rounded to 3 places so a float
// wobble never reads as a change.
const r3 = (v) => v == null || !Number.isFinite(+v) ? null : Math.round(+v * 1000) / 1000;
function buildEngineeringRow(room, systems, generated) {
  const g = systems.geometry, h = systems.hvac.hepa, l = systems.electrical.lighting;
  return {
    room_id: room.RoomID,
    floor_area_sf: r3(g.floorAreaSf), room_volume_cf: r3(g.volumeCf), target_ach: r3(systems.hvac.targetAch), supply_cfm: r3(h.requiredCfm),
    hepa_filter_type: h.filterType || null, hepa_design_cfm: r3(h.designAirflowPerUnit), hepa_face_area_sf: r3(h.faceAreaSf),
    hepa_coverage_pct_target: r3(h.coveragePercent), hepa_count_by_airflow: h.countByAirflow, hepa_count_by_coverage: h.countByCoverage,
    calculated_hepa_count: h.calculatedQuantity, hepa_count_override: h.overrideQuantity, effective_hepa_count: h.effectiveQuantity,
    hepa_cfm_per_unit: r3(h.actualAirflowPerUnit), hepa_face_velocity_fpm: r3(h.faceVelocityFpm), hepa_coverage_pct_actual: r3(h.actualCoveragePercent),
    hepa_generated_count: generated?.hepa ?? null,
    lighting_fixture_type: l.fixtureType || null, lighting_target_fc: l.targetFc, fixture_lumens: l.fixtureLumens, lighting_required_lumens: r3(l.requiredLumens),
    lighting_count_by_lumens: l.countByLumens, lighting_count_by_spacing: l.countBySpacing, calculated_lighting_fixture_count: l.calculatedQuantity,
    lighting_fixture_count_override: l.overrideQuantity, effective_lighting_fixture_count: l.effectiveQuantity, lighting_quantity_source: l.quantitySource,
    lighting_watts: r3(l.totalWatts), lighting_va: r3(l.totalVa), lighting_generated_count: generated?.light ?? null,
    warnings_json: systems.warnings,
    inputs_json: {
      L: g.L, W: g.W, H: g.H, ach: systems.hvac.targetAch, polygon: !!room.PolygonJson,
      filterType: h.filterType, designAirflowSource: h.designAirflowSource, faceAreaSource: h.faceAreaSource,
      fixtureType: l.fixtureType, cu: l.coefficientOfUtilization, llf: l.lightLossFactor, spacingCriterion: l.spacingCriterion, mountingHeightFt: l.mountingHeightFt, vaSource: l.vaSource,
    },
    calculation_version: systems.version,
  };
}
// payload key -> stored column; *_generated_count compared only when the
// client has a value (the proc preserves the stored marker on NULL).
const ENG_COLUMNS = {
  floor_area_sf: "FloorAreaSf", room_volume_cf: "RoomVolumeCF", target_ach: "TargetACH", supply_cfm: "SupplyCFM",
  hepa_filter_type: "HEPAFilterType", hepa_design_cfm: "HEPADesignCfm", hepa_face_area_sf: "HEPAFaceAreaSf", hepa_coverage_pct_target: "HEPACoveragePctTarget",
  hepa_count_by_airflow: "HEPACountByAirflow", hepa_count_by_coverage: "HEPACountByCoverage", calculated_hepa_count: "CalculatedHEPACount",
  hepa_count_override: "HEPACountOverride", effective_hepa_count: "EffectiveHEPACount", hepa_cfm_per_unit: "HEPACfmPerUnit",
  hepa_face_velocity_fpm: "HEPAFaceVelocityFpm", hepa_coverage_pct_actual: "HEPACoveragePctActual", hepa_generated_count: "HEPAGeneratedCount",
  lighting_fixture_type: "LightingFixtureType", lighting_target_fc: "LightingTargetFc", fixture_lumens: "FixtureLumens", lighting_required_lumens: "LightingRequiredLumens",
  lighting_count_by_lumens: "LightingCountByLumens", lighting_count_by_spacing: "LightingCountBySpacing", calculated_lighting_fixture_count: "CalculatedLightingFixtureCount",
  lighting_fixture_count_override: "LightingFixtureCountOverride", effective_lighting_fixture_count: "EffectiveLightingFixtureCount",
  lighting_quantity_source: "LightingQuantitySource", lighting_watts: "LightingWatts", lighting_va: "LightingVA", lighting_generated_count: "LightingGeneratedCount",
  calculation_version: "CalculationVersion",
};
function engineeringRowDiffers(row, stored) {
  if (!stored) return true;
  for (const [key, col] of Object.entries(ENG_COLUMNS)) {
    const a = row[key], b = stored[col];
    if (key.endsWith("_generated_count") && a == null) continue;
    if (a == null && b == null) continue;
    if (a == null || b == null) return true;
    if (typeof a === "number") { if (Math.abs(a - +b) > 0.0015) return true; }
    else if (String(a) !== String(b)) return true;
  }
  let storedWarnings = [];
  try { storedWarnings = JSON.parse(stored.WarningsJson || "[]"); } catch { storedWarnings = []; }
  return JSON.stringify(row.warnings_json || []) !== JSON.stringify(storedWarnings);
}

export default function RoomTakeoff({ projectId }) {
  const [revisions, setRevisions]     = useState([]);
  const [activeRevId, setActiveRevId] = useState(null);
  // Mirrors activeRevId synchronously so a load that resolves AFTER the estimator
  // switched revisions can tell it is stale and drop its result instead of
  // painting the previous revision's rooms / sheets / conditions over the new one.
  const activeRevRef = useRef(null);
  const legendSaveWarned = useRef(false);
  const [uiTheme] = useTakeoffUiTheme();   // the engine's light / dark interface (per viewer)
  activeRevRef.current = activeRevId;
  const [loading, setLoading]         = useState(true);
  const [header, setHeader]           = useState(null);
  // Declared right after the header state: hooks declared further down (route
  // geometry saves, Phase G) list it in their deps arrays, and a `const` read
  // before its declaration throws at render ("Cannot access before initialization").
  // Load health (utils/loadHealth.js): while a critical load (rooms, drawn conditions, drawings, room elements) has
  // failed — or has not completed yet — the revision is read-only, exactly like a locked one, so an edit or an
  // automatic sync can never land on state that did not load and overwrite what is saved.
  const [health, dispatchHealth] = useReducer(loadHealthReducer, null, () => initialHealth(null));
  const loadState = loadStatus(health, activeRevId);
  const isLocked = !!header?.IsLocked || loadState.blocked;
  const [zones, setZones]             = useState([]);
  const [rooms, setRooms]             = useState([]);
  const [savingId, setSavingId]       = useState(null);
  const [newRoom, setNewRoom]         = useState({ name: "", templateId: null });
  const [adding, setAdding]           = useState(false);
  const [reportOpen, setReportOpen]   = useState(false);          // the printable Takeoff Report overlay
  const [addingZone, setAddingZone]   = useState(false);
  const [scheduleSort, setScheduleSort] = useState({ key: null, dir: 1 }); // By Room header click-to-sort
  const [searchParams]                = useSearchParams();
  // The Layout Canvas ALWAYS lives in its own dedicated window (opened via
  // openCanvasWindow() below) for a two-monitor workflow — canvasOnly is
  // true for that window's own page load (?view=canvas), which renders
  // ONLY <TakeoffCanvas>, none of this page's own chrome (header/tabs/
  // totals/AHU strip) or the app's outer Nav/sidebar (see App.jsx,
  // EstimateDetail.jsx). It is never rendered inline in the main window —
  // "view" here now only ever toggles between the non-canvas tabs.
  const canvasOnly = searchParams.get("view") === "canvas";
  const [view, setView]               = useState("grid");   // 'grid' | 'schedule' | 'discipline' | 'system' | 'mechanical'
  const [sheets, setSheets]                 = useState([]);       // named drawing sheets for this revision
  const [activeSheetId, setActiveSheetId]   = useState(null);      // client-side view state (like the PDF-page selector)
  const [wallSegmentsBySheet, setWallSegmentsBySheet] = useState({}); // sheetId -> {hSegs,vSegs} | null (see effect below)
  const activeSheetRotation = +(sheets.find(s => s.SheetID === activeSheetId)?.ImageRotation) || 0;
  const { accounts } = useMsal();
  const currentUserEmail = accounts[0]?.username || null; // recorded as TakeoffEdgeOverrides.ChangedBy on an override
  const [edgeOverrides, setEdgeOverrides]   = useState({});     // edgeOverrideKey(a,b,runKey) -> type
  const [chaseAttrs, setChaseAttrs]         = useState({});     // edgeOverrideKey(a,b,runKey) -> { chaseType, clearWidthFt, isAccessible, notes }
  const [criteria, setCriteria]             = useState({});     // criterionKey -> value
  const [expandedRooms, setExpandedRooms]   = useState({});     // RoomID -> bool
  const [scopeExclusions, setScopeExclusions] = useState(new Set()); // "roomId|scopeItemId"
  const [scopeByDiscipline, setScopeByDiscipline] = useState({}); // Discipline -> CS-responsible items
  const [scopeItems, setScopeItems]         = useState([]);       // full register items (for placeable scope on canvas)
  const [scopeApps, setScopeApps]           = useState([]);       // scope applications incl. drawn runs
  const [catalogConditionTypes, setCatalogConditionTypes] = useState([]); // the global catalog (Items / Assemblies with a Style, + the retired Condition Library)
  const [typeOverrides, setTypeOverrides] = useState([]);                // this estimate's customised type settings (db/37 TakeoffConditionOverrides)
  // What every tool, label and engine reads: the catalog with THIS estimate's overrides applied (the server resolves drawn rows the same way).
  const conditionTypes = useMemo(() => applyTypeOverrides(catalogConditionTypes, typeOverrides), [catalogConditionTypes, typeOverrides]);
  const [takeoffConditions, setTakeoffConditions] = useState([]); // placed conditions for this revision
  // Undo / redo of committed canvas edits (Takeoff Canvas 2.0 A2; src/utils/
  // undoStack.js): one op per persisted edit whose inverse is a real write
  // through the same proc. Session-scoped — cleared on revision change.
  // Command history (src/utils/commandHistory.js): a step is recorded only after it SAVED, a gesture is one step,
  // undo / redo are serialized, and a step reverses only the fields it changed on the row as it is now.
  const [history, historyView] = useCommandHistory();
  useEffect(() => { history.clear(); }, [activeRevId, history]);
  // The newest rows, for commands: undo / redo read what exists NOW, never a stale snapshot.
  const roomsRef = useRef(rooms); roomsRef.current = rooms;
  const conditionsRef = useRef(takeoffConditions); conditionsRef.current = takeoffConditions;
  // A different revision is a different set of drawings and takeoffs: drop the
  // previous revision's sheets / rooms / conditions / lock state immediately
  // (the loads below repopulate) so nothing stale, including the old
  // revision's locked flag, is visible or editable while they load.
  useEffect(() => {
    setHeader(null); setRooms([]); setTakeoffConditions([]); setSheets([]); setActiveSheetId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRevId]);
  async function undoLast() {
    try { const r = await history.undo(); if (r?.dropped) alert(r.message + " That step was removed from the history."); }
    catch (e) { alert("Undo failed: " + e.message); }
  }
  async function redoLast() {
    try { const r = await history.redo(); if (r?.dropped) alert(r.message + " That step was removed from the history."); }
    catch (e) { alert("Redo failed: " + e.message); }
  }
  const roomLabelOf = (room) => room?.RoomName || (room?.RoomNumber ? `Room ${room.RoomNumber}` : `Room ${room?.RoomID}`);
  // Re-create / delete a drawn row for undo & redo; a created row gets a NEW
  // id, so ops carry a mutable ref that always points at the current row.
  async function createConditionFromPayload(payloadStr, { reloadRooms = true } = {}) {
    const r = await sqlRead(`EXEC sp_SaveTakeoffConditionDrawn @payload = '${payloadStr}'`);
    loadConditions(); if (reloadRooms) load(); broadcastChange();
    return (r?.Table1 || [])[0]?.TakeoffConditionDrawnID ?? null;
  }
  async function deleteConditionById(id) {
    if (id == null) return;
    await sqlRead(`EXEC sp_SaveTakeoffConditionDrawn @payload = '${payloadOf({ takeoff_condition_id: id, revision_id: activeRevId, is_delete: 1 })}'`);
    loadConditions(); load(); broadcastChange();
  }
  // A drawn / placed condition as ONE undoable step: run creates it, undo deletes the row, redo creates it again
  // (it gets a NEW id each time, so the command keeps the current one in its own closure). Nothing is recorded
  // unless the save succeeded; a failure is reported once and the history is left alone.
  // `optimistic` ({ typeRow, fields }) shows the row on the drawing at once instead of after the save + re-read; the
  // re-read replaces the whole list (dropping the stand-in), a failed save removes it. A re-run (redo) shows it again.
  const provisionalSeq = useRef(0);
  function createDrawnCondition(label, payloadStr, { errorPrefix = "Error saving condition", reloadRooms = false, optimistic = null } = {}) {
    const ref = { id: null };
    return history.execute({
      label,
      run: async () => {
        const tempId = optimistic ? -(++provisionalSeq.current) : null;
        if (optimistic) setTakeoffConditions(prev => [...prev, provisionalConditionRow(optimistic.typeRow, optimistic.fields, tempId)]);
        try { ref.id = await createConditionFromPayload(payloadStr, { reloadRooms }); }
        catch (e) {
          if (tempId != null) setTakeoffConditions(prev => prev.filter(c => c.TakeoffConditionDrawnID !== tempId));
          alert(`${errorPrefix}: ${e.message}`); throw new CommandFailed();
        }
      },
      undo: async () => { await deleteConditionById(ref.id); },
    });
  }

  // Change ONE drawn condition's geometry / room. The patch is laid over the row as it is NOW (so undoing a
  // move cannot revert a later edit), applied to the screen at once (no snap-back while the save is in
  // flight) and rolled back if the save fails.
  async function applyConditionPatch(id, patch, opts) {
    const latest = conditionsRef.current.find(c => c.TakeoffConditionDrawnID === id);
    if (!latest) throw new CommandStale("That symbol no longer exists.");
    if (isProvisionalId(id)) throw new CommandStale("That symbol is still saving — try again in a moment.");
    // A person's edit of a machine row (generated symbol, auto wall) reviews it: stamped 'corrected' by this save.
    const stamp = correctionStamp(latest, "condition", currentUserEmail);
    const localPatch = stamp.review_status ? { ...patch, ReviewStatus: "corrected", ReviewedBy: currentUserEmail } : patch;
    const before = pickFields(latest, Object.keys(localPatch));
    setTakeoffConditions(prev => patchRowById(prev, "TakeoffConditionDrawnID", id, localPatch));
    // The save carries the version this row was loaded at; if the row moved on (another tab, or another writer in
    // this one) the row is re-read and the edit retried on top of it when nothing it changes has moved — see
    // utils/versionConflict.js. A refused edit that really collides leaves what is saved untouched.
    const send = (row) => sqlRead(`EXEC sp_SaveTakeoffConditionDrawn @payload = '${payloadOf({ ...conditionPatchPayload(row, patch, activeRevId, opts), ...stamp, ...(row.RowVer != null ? { expected_version: row.RowVer } : {}) })}'`);
    let res;
    try {
      try {
        res = await send(latest);
      } catch (e) {
        if (!isVersionConflict(e)) throw e;
        const list = (await sqlRead(`EXEC sp_GetTakeoffConditionsDrawn @revision_id=${activeRevId}`))?.Table1 || [];
        const fresh = list.find(c => c.TakeoffConditionDrawnID === id) || null;
        const plan = planConflictRetry({ touchedKeys: Object.keys(localPatch), baseline: before, fresh, mine: { ...latest, ...localPatch } });
        setTakeoffConditions(list); // what is saved now
        if (!plan.retry) { alert(describeConflict("drawn condition", plan)); throw new CommandFailed(); }
        setTakeoffConditions(prev => patchRowById(prev, "TakeoffConditionDrawnID", id, localPatch));
        res = await send(plan.merged);
      }
    } catch (e) {
      if (e instanceof CommandFailed) throw e;
      setTakeoffConditions(prev => patchRowById(prev, "TakeoffConditionDrawnID", id, before));
      alert("Error saving symbol: " + e.message);
      throw new CommandFailed();
    }
    const newVer = (res?.Table1 || [])[0]?.RowVer;
    if (newVer != null) setTakeoffConditions(prev => patchRowById(prev, "TakeoffConditionDrawnID", id, { RowVer: newVer }));
    loadConditions();
    broadcastChange();
  }
  const conditionPatchCommand = (label, id, before, after, opts) => ({
    label,
    run: () => applyConditionPatch(id, after, opts),
    undo: () => applyConditionPatch(id, before),
  });
  const [roomScopeItems, setRoomScopeItems] = useState([]);       // Room Detail Flyout scope-list overrides/custom lines
  const [reviewStates, setReviewStates]     = useState([]);       // Track J — per-revision review workflow state (db/80 TakeoffReviewStates), laid over the computed queue
  const [haloColors, setHaloColors]         = useState(null);     // per-revision Edge Halo tier color overrides — see db/61_takeoff_halo_settings.sql
  const [costCodes, setCostCodes]           = useState([]);       // CostCodes catalog — groups the Flyout's scope list
  const [classifications, setClassifications] = useState([]);     // Cleanroom Classification catalog (global) — drives ACH
  const [bodOptionRows, setBodOptionRows] = useState([]);          // Basis of Design option rows (global) — Discipline Criteria dropdown choices
  const [takeoffVariableRows, setTakeoffVariableRows] = useState([]); // Takeoff Variables (global) — room-boundary/snap tuning constants, see db/65_takeoff_variables.sql
  const [costSettings, setCostSettings]     = useState(null);     // EstimateCostSettings row (per revision) — VaPerLightingFixture fallback for the lighting engine
  const [engineeringRows, setEngineeringRows] = useState(null);   // TakeoffRoomEngineering rows (db/69) — null until the first load so the sync effect never runs blind
  const [roomTemplateEquipment, setRoomTemplateEquipment] = useState([]); // Room Templates' Standard Elements rows — seeded onto a room as RoomElements (db/70)
  const [roomElements, setRoomElements] = useState([]);           // Room Build-Up: this revision's RoomElements (db/70) — gates the HEPA/lighting engines per room
  const [catalogItems, setCatalogItems] = useState([]);           // Estimate Engine catalog (global) — the "+ Add Element" picker's pool (IsRoomElement rows)
  const [catalogFormulas, setCatalogFormulas] = useState([]);     // EstimateCostFormulas (global) — a room element's optional quantity formula
  const [catalogAssemblies, setCatalogAssemblies] = useState([]);
  const [takeoffLayers, setTakeoffLayers] = useState([]);           // Takeoff Layers (global, active only) — the canvas's Layers dock catalog, see db/66_takeoff_layers.sql
  const [roomTemplates, setRoomTemplates]   = useState([]);       // Room Templates catalog (global) — see db/55_room_templates.sql
  const [roomTemplateCriteria, setRoomTemplateCriteria] = useState([]); // that catalog's per-template discipline-criteria defaults
  const [constructionStandardRules, setConstructionStandardRules] = useState([]); // room-template-pair -> default construction type (global) — see db/59_construction_standard_rules.sql
  const [analyzing, setAnalyzing]           = useState(false);    // AI prefill job in flight
  const [setDialogOpen, setSetDialogOpen]   = useState(false);    // "Add drawings from a plan set" (drawing storage)
  const prefillPoll  = usePolledJob(); // clears itself on unmount (background-job pattern rule)
  const bcRef        = useRef(null);
  const tabIdRef     = useRef(Math.random().toString(36).slice(2));
  const bcTimerRef   = useRef(null);

  // Live-link across tabs/monitors: two windows on the same revision stay in
  // sync. Each mutating save broadcasts; the other tab debounce-reloads. The
  // sender id filters out our own echoes.
  useEffect(() => {
    if (!activeRevId || typeof BroadcastChannel === "undefined") return;
    const bc = new BroadcastChannel(takeoffChannelName(activeRevId));
    bcRef.current = bc;
    bc.onmessage = (e) => {
      if (!e.data || e.data.from === tabIdRef.current) return;
      clearTimeout(bcTimerRef.current);
      bcTimerRef.current = setTimeout(() => load(), 250);
    };
    return () => { clearTimeout(bcTimerRef.current); bc.close(); bcRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRevId]);

  const broadcastChange = useCallback(() => {
    try { bcRef.current?.postMessage({ from: tabIdRef.current, at: Date.now() }); } catch { /* no-op */ }
  }, []);

  useEffect(() => {
    setLoading(true);
    sqlRead(`EXEC sp_GetEstimateRevisions @project_id=${projectId}`)
      .then(result => {
        const revs = result?.Table1 || result?.value || [];
        setRevisions(revs);
        // A popped-out canvas window carries the main window's revision
        // choice through ?revision= so both windows agree on which one
        // they're showing, rather than each independently defaulting to
        // "the first unlocked revision" (which could differ if the main
        // window had switched revisions via the dropdown).
        const urlRevId = +(searchParams.get("revision") || 0);
        const fromUrl = urlRevId ? revs.find(r => r.RevisionID === urlRevId) : null;
        const active = fromUrl || revs.find(r => !r.IsLocked) || revs[0];
        if (active) setActiveRevId(active.RevisionID);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [projectId]);

  const load = useCallback(() => {
    if (!activeRevId) return;
    const revId = activeRevId;
    sqlRead(`EXEC sp_GetRoomTakeoff @revision_id=${activeRevId}`)
      .then(result => {
        if (activeRevRef.current !== revId) return; // revision changed while this was in flight
        setHeader((result?.Table1 || [])[0] || null);
        setZones(result?.Table2 || []);
        setRooms(result?.Table3 || []);
        // Table4 (TakeoffCanvasSettings) is legacy — sheets (below) replace it.
        const ov = {};
        const ca = {};
        for (const o of result?.Table5 || []) {
          const key = edgeOverrideKey(o.RoomAID, o.RoomBID, o.RunKey);
          if (o.EdgeType) ov[key] = o.EdgeType;
          // Chase detail can exist on a row whose EdgeType is NULL — an
          // auto-detected cavity the estimator never explicitly overrode,
          // just annotated with chase attributes (see sp_SaveChaseAttributes).
          if (o.ChaseType || o.ClearWidthFt != null || o.IsAccessible != null || o.Notes) {
            ca[key] = { chaseType: o.ChaseType || "", clearWidthFt: o.ClearWidthFt ?? "", isAccessible: !!o.IsAccessible, notes: o.Notes || "" };
          }
        }
        setEdgeOverrides(ov);
        setChaseAttrs(ca);
        const cr = {};
        for (const c of result?.Table6 || []) cr[criterionKey(c.Discipline, c.FieldKey, c.RoomID)] = c.FieldValue;
        setCriteria(cr);
        setScopeExclusions(new Set((result?.Table7 || []).map(x => `${x.RoomID}|${x.ScopeItemID}`)));
        dispatchHealth({ type: "ok", revisionId: revId, key: "rooms" });
      })
      .catch(e => {
        console.error(e);
        if (activeRevRef.current === revId) dispatchHealth({ type: "fail", revisionId: revId, key: "rooms", message: e.message });
      });

    // Scope & RACI drives which discipline subsections are active: an item
    // counts when the CleanSpace party holds the R mark and it isn't intake-only.
    sqlRead(`EXEC sp_GetScopeRegister @revision_id=${activeRevId}`)
      .then(result => {
        const parties     = result?.Table2 || [];
        const items       = result?.Table3 || [];
        const assignments = result?.Table4 || [];
        const csPartyIds = new Set(parties.filter(p => p.IsCleanSpace).map(p => p.ScopePartyID));
        const csItemIds  = new Set(assignments.filter(a => a.Mark === "R" && csPartyIds.has(a.ScopePartyID)).map(a => a.ScopeItemID));
        const byDisc = {};
        for (const it of items) {
          if (it.IsIntakeOnly || !csItemIds.has(it.ScopeItemID)) continue;
          (byDisc[it.Discipline] ||= []).push(it);
        }
        setScopeByDiscipline(byDisc);
        setScopeItems(items);
        setScopeApps(result?.Table5 || []);
      })
      .catch(e => console.error("Scope register load failed (criteria sections default to inactive):", e));

    // Condition Library catalog is global (not revision-scoped) — reloaded here
    // too so a type added mid-session (another tab, the admin page) shows up.
    // Merged with every drawable (Style-set) Item/Assembly (Phase B of
    // consolidating Condition Library into Items/Assemblies — see project
    // memory) into one list, each entry tagged with a TypeKey — see that
    // helper's own comment above for why.
    Promise.all([
      sqlRead("EXEC sp_GetTakeoffConditionLibrary"),
      // With the revision so this estimate's one-off conditions come too; a database that predates that parameter (the app
      // is deployed after the database, but a stale one must not blank the picker) falls back to the catalog list.
      sqlRead(`EXEC sp_GetDrawableEstimateEntities @revision_id=${activeRevId}`).catch(() => sqlRead("EXEC sp_GetDrawableEstimateEntities")),
    ])
      .then(([condResult, drawableResult]) => {
        const conditionLibraryTypes = (condResult?.Table1 || []).map(t => ({ ...t, TypeKey: `ct:${t.TakeoffConditionLibraryID}` }));
        const drawableEntities = (drawableResult?.Table1 || []).map(e => ({
          TypeKey: e.Kind === "item" ? `item:${e.ItemID}` : `asm:${e.AssemblyID}`,
          Kind: e.Kind, ItemID: e.ItemID, AssemblyID: e.AssemblyID,
          Code: e.Code, Name: e.Name, Style: e.Style,
          AutoSystem: e.AutoSystem, // 'hepa' | 'light' — the entity the engine generates for that system (autoLayoutEngine.js)
          Quantity1CalcType: e.Quantity1CalcType, Quantity2CalcType: e.Quantity2CalcType, Quantity3CalcType: e.Quantity3CalcType,
          Quantity1Unit: e.Quantity1Unit, Quantity2Unit: e.Quantity2Unit, Quantity3Unit: e.Quantity3Unit,
          DefaultHeightFt: e.DefaultHeightFt, DefaultWidthFt: e.DefaultWidthFt, DefaultThicknessIn: e.DefaultThicknessIn,
          Color: e.Color, Pattern: e.Pattern, Shape: e.Shape, SyncsToCriteriaField: e.SyncsToCriteriaField,
          CostCodeID: e.CostCodeID, CSICode: e.CSICode, CostCodeDescription: e.CostCodeDescription,
          LayerID: e.LayerID, // db/66 Takeoff Layer
          // OST Condition Properties parity (db/30, 2026-09-11) — area tile
          // grid (feeds createAreaCondition's stored '$.grid' result), hatch
          // spacing, linear rounding.
          GridTileWFt: e.GridTileWFt, GridTileHFt: e.GridTileHFt, GridGapIn: e.GridGapIn,
          PatternSpacingIn: e.PatternSpacingIn, RoundToIn: e.RoundToIn, RoundAreaToSf: e.RoundAreaToSf,
          OwnerProjectID: e.OwnerProjectID, // set = a one-off created for this estimate (db/87), not a catalog row
        }));
        setCatalogConditionTypes([...conditionLibraryTypes, ...drawableEntities]);
      })
      .catch(e => console.error("Condition Library load failed (Draw condition tool unavailable):", e));

    // CostCodes catalog is also global — groups the Room Detail Flyout's
    // architectural scope list by cost code (same source Condition Library
    // and Quote Log Setup anchor to).
    sqlRead("EXEC sp_GetCostCodes")
      .then(result => setCostCodes(result?.Table1 || []))
      .catch(e => console.error("Cost codes load failed (scope list grouping unavailable):", e));

    loadClassifications();

    // Room Templates catalog is also global — its criteria defaults feed
    // effectiveCriterion() (via each room's _templateDefaults, attached in
    // roomsWithTemplateDefaults below) for any room that's picked one.
    sqlRead("EXEC sp_GetRoomTemplates")
      .then(result => {
        setRoomTemplates(result?.Table1 || []);
        setRoomTemplateCriteria(result?.Table2 || []);
        setRoomTemplateEquipment(result?.Table3 || []);
      })
      .catch(e => console.error("Room Templates load failed (template defaults unavailable):", e));

    // Construction Standard rules (Room Boundary Intelligence — see project
    // memory) — also global, feeds the auto-wall sync effect below.
    sqlRead("EXEC sp_GetConstructionStandardRules")
      .then(result => setConstructionStandardRules(result?.Table1 || []))
      .catch(e => console.error("Construction Standard rules load failed (auto-wall rule matching unavailable):", e));

    loadConditions();
    loadRoomScopeItems();
    loadSheets();

    // Edge Halo per-revision color overrides — a row may not exist yet
    // (nobody's customized anything), which is the common case and just
    // means every tier uses TakeoffCanvas.jsx's own HALO_STYLE defaults.
    sqlRead(`EXEC sp_GetTakeoffHaloSettings @revision_id=${activeRevId}`)
      .then(result => setHaloColors(result?.Table1?.[0] || {}))
      .catch(e => console.error("Edge Halo color settings load failed (defaults will be used):", e));
    // Track J — acknowledged / waived / assigned state per computed review item.
    sqlRead(`EXEC sp_GetTakeoffReviewStates @revision_id=${activeRevId}`)
      .then(result => setReviewStates(result?.Table1 || []))
      .catch(e => { console.error("Review state load failed (every item shows as open):", e); setReviewStates([]); });
  }, [activeRevId]);

  const loadSheets = useCallback(() => {
    if (!activeRevId) return;
    const revId = activeRevId;
    sqlRead(`EXEC sp_GetTakeoffDrawingSheets @revision_id=${activeRevId}`)
      .then(result => {
        if (activeRevRef.current !== revId) return;
        // A sheet in a stored set has no file of its own: give it a pointer the PDF loader resolves (BackgroundImage is NULL for it).
        const list = (result?.Table1 || []).map(s => (s.SetID && s.SetBlobName && !s.BackgroundImage ? { ...s, BackgroundImage: blobPointer(s.SetBlobName) } : s));
        setSheets(list);
        setActiveSheetId(prev => list.some(s => s.SheetID === prev) ? prev : (list[0]?.SheetID ?? null));
        dispatchHealth({ type: "ok", revisionId: revId, key: "sheets" });
      })
      .catch(e => {
        console.error("Drawing sheets load failed:", e);
        if (activeRevRef.current === revId) dispatchHealth({ type: "fail", revisionId: revId, key: "sheets", message: e.message });
      });
  }, [activeRevId]);

  const loadConditions = useCallback(() => {
    if (!activeRevId) return;
    const revId = activeRevId;
    sqlRead(`EXEC sp_GetTakeoffConditionsDrawn @revision_id=${activeRevId}`)
      .then(conds => {
        if (activeRevRef.current !== revId) return;
        setTakeoffConditions(conds?.Table1 || []);
        dispatchHealth({ type: "ok", revisionId: revId, key: "conditions" });
      })
      .catch(e => {
        console.error("Takeoff conditions load failed:", e);
        if (activeRevRef.current === revId) dispatchHealth({ type: "fail", revisionId: revId, key: "conditions", message: e.message });
      });
  }, [activeRevId]);

  const loadTypeOverrides = useCallback(() => {
    if (!activeRevId) { setTypeOverrides([]); return; }
    const revId = activeRevId;
    sqlRead(`EXEC sp_GetTakeoffConditionOverrides @revision_id=${activeRevId}`)
      .then(r => { if (activeRevRef.current === revId) setTypeOverrides(r?.Table1 || []); })
      .catch(e => console.error("Condition type overrides load failed (catalog settings shown):", e));
  }, [activeRevId]);
  useEffect(() => { setTypeOverrides([]); loadTypeOverrides(); }, [loadTypeOverrides]);

  // A one-off condition (db/87): a real Item owned by this estimate's project — drawn and priced like any Item, invisible to
  // the catalog. Returns the new type's key so the Add Condition window can select it; the type list is re-read to include it.
  async function createOneOff(draft) {
    if (isLocked) return null;
    try {
      const r = await sqlRead(`EXEC sp_CreateOneOffCondition @payload = @payload`, { payload: JSON.stringify(oneOffPayload(activeRevId, draft)) });
      const id = r?.Table1?.[0]?.ItemID;
      if (!id) throw new Error("No Item was created.");
      load(); broadcastChange();
      return `item:${id}`;
    } catch (e) { alert("Error creating the one-off condition: " + e.message); return null; }
  }

  // Save (patch = every override column, null = inherit the catalog) or remove (patch null) THIS estimate's settings for
  // one catalog type. They apply to every run of the type in the revision, so the drawn rows are re-read (the server
  // resolves them override-first) along with the overrides themselves.
  async function saveTypeOverride(type, patch) {
    if (isLocked || !type?.Kind) return false;
    try {
      const payload = { revision_id: activeRevId, kind: type.Kind, item_id: type.ItemID ?? undefined, assembly_id: type.AssemblyID ?? undefined, ...(patch || { reset: 1 }) };
      await sqlRead(`EXEC sp_SaveTakeoffConditionOverride @payload = @payload`, { payload: JSON.stringify(payload) });
      loadTypeOverrides(); loadConditions(); broadcastChange();
      return true;
    } catch (e) { alert("Error saving this estimate's condition settings: " + e.message); return false; }
  }

  // Assumed Equipment + panels / circuits (db/24) — read-only here, for the
  // Phase G canvas routing (2026-09-13): the revision's conceptual distribution
  // route segments (utility-owned Phase G + HVAC-owned Scenarios Phase E/F),
  // drawn on the Layout Canvas → UtilityRouteSegments.GeometryJson → calculated
  // LF + elbow count everywhere the route is evaluated. Only the geometry is
  // written here; nodes / parent / overrides / locks are untouched (the proc
  // is preserve-if-omitted). Design flow never comes from the drawing.
  const loadRouteSegments = useCallback(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetUtilityRouteSegments @revision_id=${activeRevId}`)
      .then(result => setEquipmentRegister(prev => ({ ...prev, routeSegments: result?.Table1 || [] })))
      .catch(e => console.error("Route segments load failed (canvas routes unavailable):", e));
  }, [activeRevId]);

  // Design Schedules tab (P2-D equipment schedule; P1-D panel / circuit
  // schedules); edited on the Assumed Equipment page and the Electrical Estimate.
  const [equipmentRegister, setEquipmentRegister] = useState({ equipment: [], connections: [], panels: [], circuits: [], utilitySystems: [], scenarios: [], systems: [], systemZones: [], alternates: [], routeSegments: [] });
  useEffect(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetEquipmentRegister @revision_id=${activeRevId}`)
      .then(result => setEquipmentRegister(prev => ({ ...prev, equipment: result?.Table1 || [], connections: result?.Table2 || [], panels: result?.Table3 || [], circuits: result?.Table4 || [] })))
      .catch(e => console.error("Equipment register load failed (equipment schedule empty):", e));
    // Utility system design records (Process Utilities Phase E/F) for the process utility schedules.
    sqlRead(`EXEC sp_GetUtilitySystems @revision_id=${activeRevId}`)
      .then(result => setEquipmentRegister(prev => ({ ...prev, utilitySystems: result?.Table1 || [] })))
      .catch(e => console.error("Utility systems load failed (schedule uses defaults):", e));
    // Scenarios Phase J: the design scenarios say which plant records are the
    // selected base's scope (the equipment schedule filters / labels by it).
    sqlRead(`EXEC sp_GetDesignScenarios @revision_id=${activeRevId}`)
      .then(result => setEquipmentRegister(prev => ({ ...prev, scenarios: result?.Table1 || [], systems: result?.Table2 || [], systemZones: result?.Table3 || [] })))
      .catch(e => console.error("Design scenarios load failed (equipment schedule scenario-blind):", e));
    // J-3: the estimate's alternate headers name each published scenario alternate (HVAC Design Alternates schedule).
    sqlRead(`EXEC sp_GetEstimateLineItemAlternates @revision_id=${activeRevId}`)
      .then(result => setEquipmentRegister(prev => ({ ...prev, alternates: result?.Table1 || [] })))
      .catch(e => console.error("Alternates load failed (scenario alternates unnamed):", e));
    loadRouteSegments();
  }, [activeRevId, loadRouteSegments]);
  const ROUTE_COLORS = { chilled_water: "#2563eb", dx: "#16a34a", default: "#7c3aed" };
  const canvasRoutes = useMemo(() => {
    const utilById = new Map(equipmentRegister.utilitySystems.map(u => [u.UtilitySystemID, u]));
    const hvacById = new Map(equipmentRegister.systems.map(h => [h.SystemID, h]));
    const scenarioById = new Map(equipmentRegister.scenarios.map(sc => [sc.ScenarioID, sc]));
    return equipmentRegister.routeSegments.map(seg => {
      let geometry = seg.GeometryJson;
      if (typeof geometry === "string") { try { geometry = JSON.parse(geometry); } catch { geometry = null; } }
      const calculatedLF = polylineLengthFt(seg.GeometryJson);
      const overrideLF = seg.OverrideLF == null || seg.OverrideLF === "" ? null : +seg.OverrideLF;
      const effectiveLF = overrideLF ?? calculatedLF;
      let systemLabel = "Routes", color = ROUTE_COLORS.default;
      if (seg.HvacSystemID != null) {
        const h = hvacById.get(seg.HvacSystemID); const sc = h ? scenarioById.get(h.ScenarioID) : null;
        systemLabel = h ? `${sc?.ScenarioKey ? `${sc.ScenarioKey} ` : ""}${h.SystemKey}` : `HVAC system #${seg.HvacSystemID}`;
        color = h?.ArchitectureType === "chilled_water" ? ROUTE_COLORS.chilled_water : h?.ArchitectureType === "dx" ? ROUTE_COLORS.dx : ROUTE_COLORS.default;
      } else if (seg.UtilitySystemID != null) {
        const u = utilById.get(seg.UtilitySystemID);
        systemLabel = u ? utilityLabel(u.UtilityType) : `Utility system #${seg.UtilitySystemID}`;
        color = u?.UtilityType === "chilled_water" ? ROUTE_COLORS.chilled_water : ROUTE_COLORS.default;
      }
      return {
        segmentId: seg.SegmentID, systemLabel, color, geometry: geometry && Array.isArray(geometry.points) ? geometry : null,
        label: `${LEVEL_BY_KEY[seg.SegmentLevel]?.label || seg.SegmentLevel || "Segment"} ${seg.FromNode || "—"} → ${seg.ToNode || "—"}`,
        calculatedLF, overrideLF, effectiveLF, lfSource: overrideLF !== null ? "override" : (calculatedLF !== null ? "geometry" : "none"), turns: polylineTurns(seg.GeometryJson),
      };
    });
  }, [equipmentRegister]);
  const saveRouteGeometry = useCallback(async (segmentId, geometry) => {
    if (isLocked) return;
    const seg = equipmentRegister.routeSegments.find(x => x.SegmentID === segmentId);
    if (!seg) return;
    try {
      await sqlRead(`EXEC sp_SaveUtilityRouteSegment @payload = '${payloadOf(routeSegmentPayload({ SegmentID: segmentId, GeometryJson: geometry }, { revisionId: activeRevId, utilitySystemId: seg.UtilitySystemID ?? null, hvacSystemId: seg.HvacSystemID ?? null }))}'`);
      loadRouteSegments();
    } catch (e) { alert("Route drawing save failed: " + e.message); }
  }, [isLocked, equipmentRegister, activeRevId, loadRouteSegments]);
  const drawRouteSegment = useCallback(({ segmentId, points, lengthFt }) => saveRouteGeometry(segmentId, { points, lengthFt }), [saveRouteGeometry]);
  const clearRouteGeometry = useCallback((segmentId) => saveRouteGeometry(segmentId, null), [saveRouteGeometry]);
  const scenarioModel = useMemo(() => buildScenarioModel(equipmentRegister.scenarios, equipmentRegister.systems, equipmentRegister.systemZones), [equipmentRegister]);
  const scenarioDispositions = useMemo(() => scenarioModel.length
    ? scenarioEquipmentDispositions(scenarioModel, equipmentRegister.equipment, { alternates: equipmentRegister.alternates })
    : null, [scenarioModel, equipmentRegister]);
  // The Design scenarios panel (inside the Mechanical panel) reloads after every save — keep this copy fresh.
  const onScenarioRowsChange = useCallback((rows) => setEquipmentRegister(prev => ({ ...prev, scenarios: rows.scenarios, systems: rows.systems, systemZones: rows.systemZones })), []);

  // Room Build-Up elements (db/70) — the estimator's per-room "this room has
  // X" decisions. Also the engines' gating input (systemsCtx.roomElementsByRoom).
  const loadRoomElements = useCallback(() => {
    if (!activeRevId) return;
    const revId = activeRevId;
    sqlRead(`EXEC sp_GetRoomElements @revision_id=${activeRevId}`)
      .then(result => {
        if (activeRevRef.current !== revId) return;
        setRoomElements(result?.Table1 || []);
        dispatchHealth({ type: "ok", revisionId: revId, key: "elements" });
      })
      .catch(e => {
        console.error("Room elements load failed:", e);
        if (activeRevRef.current === revId) dispatchHealth({ type: "fail", revisionId: revId, key: "elements", message: e.message });
      });
  }, [activeRevId]);
  useEffect(() => { loadRoomElements(); }, [loadRoomElements]);
  // Global catalog for the flyout's "+ Add Element" picker (Items/Assemblies
  // flagged IsRoomElement under Setup ▸ Estimate Engine). Loaded once.
  useEffect(() => {
    sqlRead("EXEC sp_GetEstimateCostLibrary")
      .then(result => { setCatalogAssemblies(result?.Table2 || []); setCatalogItems(result?.Table3 || []); setCatalogFormulas(result?.Table5 || []); })
      .catch(e => console.error("Estimate catalog load failed (Add Element picker unavailable):", e));
  }, []);
  const roomElementsByRoom = useMemo(() => groupElementsByRoom(roomElements), [roomElements]);

  async function saveRoomElement(payload) {
    if (isLocked) return;
    try {
      // A person's edit of a template-seeded element reviews it (stamped 'corrected' by this save).
      const existing = payload.room_element_id != null ? roomElements.find(e => e.RoomElementID === payload.room_element_id) : null;
      const stamp = existing ? correctionStamp(existing, "element", currentUserEmail) : {};
      await sqlRead(`EXEC sp_SaveRoomElement @payload = '${payloadOf({ ...payload, ...stamp, revision_id: activeRevId })}'`);
      loadRoomElements();
      broadcastChange();
    } catch (e) { alert("Error saving room element: " + e.message); }
  }
  async function deleteRoomElement(roomElementId) {
    if (isLocked) return;
    try {
      await sqlRead(`EXEC sp_SaveRoomElement @payload = '${payloadOf({ room_element_id: roomElementId, revision_id: activeRevId, is_delete: 1 })}'`);
      loadRoomElements();
      broadcastChange();
    } catch (e) { alert("Error removing room element: " + e.message); }
  }
  // Room Templates seed elements: called when a room is created with a
  // template or switched to one. Adds only what the room doesn't already
  // carry; never removes (the estimator prunes).
  async function seedRoomElements(roomId, roomTemplateId) {
    if (isLocked || !roomId || !roomTemplateId) return;
    try {
      await sqlRead(`EXEC sp_SeedRoomElementsFromTemplate @payload = '${payloadOf({ revision_id: activeRevId, room_id: roomId, room_template_id: roomTemplateId })}'`);
      loadRoomElements();
    } catch (e) { console.error("Room element seed from template failed:", e); }
  }

  // Persisted engineering results (db/69) — reset to null on a revision
  // switch so the sync effect below waits for the new revision's rows.
  const autoSyncSigRef = useRef(null);
  const loadEngineering = useCallback(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetTakeoffRoomEngineering @revision_id=${activeRevId}`)
      .then(result => setEngineeringRows(result?.Table1 || []))
      .catch(e => console.error("Room engineering load failed:", e));
  }, [activeRevId]);
  useEffect(() => { setEngineeringRows(null); autoSyncSigRef.current = null; loadEngineering(); }, [loadEngineering]);

  const loadRoomScopeItems = useCallback(() => {
    if (!activeRevId) return;
    const revId = activeRevId;
    sqlRead(`EXEC sp_GetRoomScopeItems @revision_id=${activeRevId}`)
      .then(res => { if (activeRevRef.current === revId) setRoomScopeItems(res?.Table1 || []); })
      .catch(e => console.error("Room scope items load failed:", e));
  }, [activeRevId]);

  // Cleanroom Classification catalog is global (not revision-scoped) — edited
  // on the Mechanical Systems Configuration tab, picked per room in the
  // schedule above. Re-callable so a save on that tab refreshes both places.
  const loadClassifications = useCallback(() => {
    sqlRead("EXEC sp_GetCleanroomClassifications")
      .then(result => setClassifications(result?.Table1 || []))
      .catch(e => console.error("Cleanroom classifications load failed:", e));
  }, []);

  // Basis of Design options are global (not revision-scoped) — edited on the
  // Setup > Basis of Design page, consumed here for the Discipline Criteria
  // dropdowns below. Loaded once; a save on that page only affects future
  // loads (no live cross-tab refresh, matching every other global reference
  // catalog in this app — e.g. classifications above).
  useEffect(() => {
    sqlRead("EXEC sp_GetBasisOfDesignOptions")
      .then(result => setBodOptionRows(result?.Table1 || []))
      .catch(e => console.error("Basis of Design options load failed:", e));
  }, []);
  const bodOptions = useMemo(() => buildBodOptionsMap(bodOptionRows), [bodOptionRows]);
  // Same rows, keyed by option value — the selected filter/fixture option's
  // engineering attributes (DesignAirflowCfm, FixtureLumens, ...) for
  // roomSystemsEngine.js. Inactive rows included on purpose (see that helper).
  const bodAttrs = useMemo(() => buildBodOptionAttrs(bodOptionRows), [bodOptionRows]);

  // Per-revision electrical settings — only VaPerLightingFixture is read
  // here, as the lighting engine's last-resort VA fallback when the selected
  // fixture option has no watts/VA of its own.
  useEffect(() => {
    if (!activeRevId) return;
    sqlRead(`EXEC sp_GetEstimateCostSettings @revision_id=${activeRevId}`)
      .then(result => setCostSettings((result?.Table1 || [])[0] || null))
      .catch(e => console.error("Estimate settings load failed (lighting VA fallback unavailable):", e));
  }, [activeRevId]);

  // Takeoff Layers (Setup > Estimate Engine > Takeoff Layers, db/66) are
  // global — the canvas's Layers dock lists them, and every room/drawn
  // condition/placed symbol whose Room Template/Item/Assembly is assigned
  // to one hides with it. Only active rows are passed through.
  useEffect(() => {
    sqlRead("EXEC sp_GetTakeoffLayers")
      .then(result => setTakeoffLayers((result?.Table1 || []).filter(l => l.IsActive)))
      .catch(e => console.error("Takeoff layers load failed (Layers dock falls back to the built-in list):", e));
  }, []);

  // Takeoff Variables (room-boundary/snap tuning constants — Setup >
  // Estimate Engine > Engine Variables, db/65_takeoff_variables.sql) are
  // global, same loaded-once convention as Basis of Design options/
  // classifications above. Merged over DEFAULT_TUNABLES so a row this
  // session hasn't loaded yet (or one added by a future db/ file the admin
  // hasn't touched) still resolves to its hardcoded default rather than
  // `undefined`.
  useEffect(() => {
    sqlRead("EXEC sp_GetTakeoffVariables")
      .then(result => setTakeoffVariableRows(result?.Table1 || []))
      .catch(e => console.error("Takeoff variables load failed:", e));
  }, []);
  // One merged map for every consumer: room-boundary geometry
  // (DEFAULT_TUNABLES), duct design velocities + HEPA default airflow
  // (DEFAULT_DUCT_TUNABLES), and the HEPA/lighting engine's design assumptions
  // (DEFAULT_SYSTEMS_TUNABLES) — the seeded rows carry every category.
  const tunables = useMemo(() => ({
    ...DEFAULT_TUNABLES,
    ...DEFAULT_DUCT_TUNABLES,
    ...DEFAULT_SYSTEMS_TUNABLES,
    ...DEFAULT_LAYOUT_TUNABLES,
    ...DEFAULT_LEARNING_TUNABLES,
    ...Object.fromEntries(takeoffVariableRows.map(r => [r.VariableKey, +r.Value])),
  }), [takeoffVariableRows]);

  // Wall learning (utils/learning/wallLearning.js, db/86 sp_GetWallLearningModel): aggregates of the walls people
  // accepted or corrected on past estimates. Loaded once; it only ever PRE-FILLS a wall no rule and no wall system
  // decided (computeAutoWallSegments opts.predictWall), and the sync stores that fill 'unreviewed'. A failed load
  // leaves the engine exactly as it was before learning existed.
  const [wallLearningRows, setWallLearningRows] = useState(null);
  useEffect(() => {
    sqlRead("EXEC sp_GetWallLearningModel")
      .then(result => setWallLearningRows({ pairRows: result?.Table1 || [], templateRows: result?.Table2 || [], performanceRows: result?.Table3 || [], totalsRow: result?.Table4?.[0] || null }))
      .catch(e => console.error("Wall learning model load failed (blank walls stay blank):", e));
  }, []);
  const wallLearning = useMemo(() => {
    if (!wallLearningRows) return null;
    const model = buildWallModel(wallLearningRows);
    return { model, tunables, predictWall: (info) => predictWallConstruction(info, model, tunables) };
  }, [wallLearningRows, tunables]);

  // Context object roomSystemsEngine.js's calcRoomSystems() reads — passed to
  // the canvas (Room Detail Flyout's engineering block), the Mechanical
  // Systems tab (lighting heat gain), and deriveRoom() below.
  // roomElementsByRoom gates the engines per room (Room Build-Up: no HEPA
  // element -> no HEPA count for that room).
  const systemsCtx = useMemo(() => ({ bodAttrs, settings: costSettings, tunables, roomElementsByRoom }), [bodAttrs, costSettings, tunables, roomElementsByRoom]);

  useEffect(() => { load(); }, [load]);


  // P1 Phase E — the revision's schedule publication configuration
  // (EstimateRevisions.SchedulePublicationJson): saved through its own proc,
  // then the revisions list is re-read so `header` carries the new value.
  async function saveSchedulePublication(pub) {
    if (isLocked || !activeRevId) return;
    try {
      const payload = payloadOf(publicationPayload(activeRevId, pub));
      await sqlRead(`EXEC sp_SaveRevisionSchedulePublication @payload = '${payload}'`);
      const result = await sqlRead(`EXEC sp_GetEstimateRevisions @project_id=${projectId}`);
      setRevisions(result?.Table1 || result?.value || []);
    } catch (e) { alert("Error saving the schedule publication: " + e.message); }
  }

  // ── Visible tile-count sync ─────────────────────────────────────────────────
  // An area condition whose type defines a tile grid carries the polygon-
  // walked result on its row (GeometryJson '$.grid', written at draw time by
  // createAreaCondition) because SQL can't walk the polygon (see
  // fn_ConditionCalcQuantity's comment, db/37). When an admin later changes
  // the type's grid — or a row predates the grid entirely — the stored
  // result goes stale while the canvas label (computeConditionQuantities,
  // live from the points) is already right; this brings the row back in
  // line so the rollups and pricing agree with what the estimator sees.
  // Write-through on read, same convention as the auto-wall sync effect
  // below; each (row, grid) pair is attempted once per page load so a
  // failed write can't loop.
  const gridSyncAttemptedRef = useRef(new Set());
  useEffect(() => {
    if (isLocked || !activeRevId || !takeoffConditions.length) return;
    const stale = [];
    for (const c of takeoffConditions) {
      if (c.__provisional || c.Style !== "area" || !(c.GridTileWFt > 0 && c.GridTileHFt > 0) || !c.GeometryJson) continue;
      let geo;
      try { geo = JSON.parse(c.GeometryJson); } catch { continue; }
      if (!geo?.points || geo.points.length < 3) continue;
      const live = gridTileTakeoff(geo.points, c.GridTileWFt, c.GridTileHFt, c.GridGapIn, { areaFt2: geo.areaFt2 });
      if (!live || gridResultsEqual(geo.grid, live)) continue;
      const key = `${c.TakeoffConditionDrawnID}:${live.tileWFt}:${live.tileHFt}:${live.gapIn}`;
      if (gridSyncAttemptedRef.current.has(key)) continue;
      gridSyncAttemptedRef.current.add(key);
      stale.push({ c, geo: { ...geo, grid: live } });
    }
    if (!stale.length) return;
    (async () => {
      for (const { c, geo } of stale) {
        // sp_SaveTakeoffConditionDrawn's UPDATE writes RoomID/overrides/
        // Notes/DisplayOrder unconditionally, so the row's current values
        // are resent; the type family is omitted so it's preserved as-is.
        const payload = payloadOf({
          takeoff_condition_id: c.TakeoffConditionDrawnID,
          revision_id:          activeRevId,
          room_id:              c.RoomID ?? null,
          target_type:          c.TargetType || "drawn",
          geometry_json:        JSON.stringify(geo),
          height_ft_override:   c.HeightFtOverride ?? null,
          quantity_override:    c.QuantityOverride ?? null,
          notes:                c.Notes ?? null,
          display_order:        c.DisplayOrder ?? 100,
        });
        await sqlRead(`EXEC sp_SaveTakeoffConditionDrawn @payload = '${payload}'`);
      }
      loadConditions();
    })().catch(e => console.error("Visible tile-count sync failed:", e));
  }, [takeoffConditions, isLocked, activeRevId, loadConditions]);

  // ── Room save/delete/add ──────────────────────────────────────────────────────
  // polygonJson: explicit override for sp_SaveTakeoffRoom's @polygon_json —
  // left undefined (the default) for an ordinary field edit (rename, ACH,
  // etc.), which the proc reads as "preserve whatever's already stored."
  // Callers that need to actually change the shape (moveRoom translating it,
  // editRoomPolygon committing a reshape) pass the new value explicitly;
  // resizeRoom passes nothing on purpose so the proc's own clear-on-resize
  // logic reverts the room to a plain rectangle, matching its documented
  // behavior (db/21_room_takeoff.sql).
  async function saveRoom(room, { polygonJson, skipLocalUpdate = false, touchedKeys, baseline } = {}) {
    if (isLocked) return false;
    // A person's edit of an AI-proposed room accepts it (the frozen proposal stays for comparison).
    const latestRoom = roomsRef.current.find(r => r.RoomID === room.RoomID);
    const reviewPatch = latestRoom && room.DetectionStatus === "proposed" ? roomCorrectionPatch(latestRoom, currentUserEmail) : {};
    if (reviewPatch.DetectionStatus) room = { ...room, ...reviewPatch };
    setSavingId(room.RoomID);
    // What this save changes, and how those fields looked when the editor last saw them — the basis for deciding
    // whether a refused (stale-version) save can be retried or really collides (utils/versionConflict.js).
    const touched = touchedKeys || [...changedFields(latestRoom, room, ROOM_SAVE_KEYS), ...(polygonJson !== undefined ? ["PolygonJson"] : [])];
    const baselineVals = baseline || Object.fromEntries(touched.map(k => [k, latestRoom?.[k] ?? null]));
    try {
      const buildPayload = (room) => payloadOf({
        room_id:         room.RoomID,
        revision_id:     activeRevId,
        room_name:       room.RoomName,
        room_number:     room.RoomNumber || null,
        room_type:       room.RoomType,
        length_ft:       snapToSixteenth(room.LengthFt),   // dimensions are stored to the nearest 1/16" (feet-inches is how they are read)
        width_ft:        snapToSixteenth(room.WidthFt),
        height_ft:       snapToSixteenth(room.HeightFt),
        ach:             room.ACH ?? null,
        pressure_target: room.PressureTarget || null,
        ahu_zone_id:     room.AHUZoneID ?? null,
        classification_id: room.ClassificationID ?? null,
        room_template_id: room.RoomTemplateID ?? null,
        // Wall-system substitution (db/21): always sent from here (the full
        // room row), so clearing it back to the template default persists.
        wall_kind:        room.WallKind || null,
        wall_item_id:     room.WallKind === "item" ? (room.WallItemID ?? null) : null,
        wall_assembly_id: room.WallKind === "assembly" ? (room.WallAssemblyID ?? null) : null,
        // Wall systems by position (db/21 WallSystemsJson): sent as an object (the proc reads it AS JSON); {} clears.
        wall_systems:     JSON.parse(encodeWallSystems(parseWallSystemsJson(room.WallSystemsJson)) || "{}"),
        canvas_x:        room.CanvasX ?? null,
        canvas_y:        room.CanvasY ?? null,
        canvas_rot:      room.CanvasRot ?? null,
        // Drawing ownership (db/21 SheetID): sent only once the room has one, so
        // a save can never clear it; placing a room sets room.SheetID first.
        ...(room.SheetID != null ? { sheet_id: room.SheetID } : {}),
        position_locked: room.PositionLocked ? 1 : 0, // a placed room's position lock (db/21)
        // Track G: the proposal state travels with every save (preserve); accepting stamps the reviewer
        ...(room.DetectionStatus ? { detection_status: room.DetectionStatus, detection_reviewed_by: room.DetectionReviewedBy || currentUserEmail || null } : {}),
        ...(polygonJson !== undefined ? { polygon_json: polygonJson } : {}),
        ...(room.RowVer != null ? { expected_version: room.RowVer } : {}),
      });
      let saved = room, retried = false, res;
      try {
        res = await sqlRead(`EXEC sp_SaveTakeoffRoom @payload = '${buildPayload(room)}'`);
      } catch (e) {
        if (!isVersionConflict(e)) throw e;
        const list = (await sqlRead(`EXEC sp_GetRoomTakeoff @revision_id=${activeRevId}`))?.Table3 || [];
        const fresh = list.find(r => r.RoomID === room.RoomID) || null;
        const plan = planConflictRetry({ touchedKeys: touched, baseline: baselineVals, fresh, mine: room });
        setRooms(list); // what is saved now
        if (!plan.retry) { saveConflictRef.current = true; alert(describeConflict("room", plan)); return false; }
        saved = plan.merged; retried = true;
        res = await sqlRead(`EXEC sp_SaveTakeoffRoom @payload = '${buildPayload(saved)}'`);
      }
      const newVer = (res?.Table1 || [])[0]?.RowVer;
      // A command has already applied its patch (optimistically); replacing the row with a snapshot here
      // could overwrite an edit made to it in the meantime.
      if (retried) setRooms(prev => prev.map(r => r.RoomID === room.RoomID ? { ...saved, RowVer: newVer ?? saved.RowVer } : r));
      else if (!skipLocalUpdate) setRooms(prev => prev.map(r => r.RoomID === room.RoomID ? { ...room, RowVer: newVer ?? room.RowVer } : r));
      else setRooms(prev => prev.map(r => r.RoomID === room.RoomID ? { ...r, ...(reviewPatch.DetectionStatus ? reviewPatch : {}), ...(newVer != null ? { RowVer: newVer } : {}) } : r));
      broadcastChange();
      return true;
    } catch (e) { alert("Error saving room: " + e.message); return false; }
    finally { setSavingId(null); }
  }

  // Change the active drawing's scale and rescale its takeoffs with it. Returns true when it handled the
  // save (rescaled, or the estimator declined), false when there is nothing to move and the caller should
  // just save the scale. The proc applies the new scale and the moved geometry in ONE transaction.
  async function rescaleActiveSheet(partial, prevScale) {
    const sheet = sheets.find(s => s.SheetID === activeSheetId);
    const nextScale = partial.image_ft_per_inch ?? partial.image_scale_ft_per_px;
    const k = scaleFactor(prevScale, nextScale);
    if (!sheet || !isMaterialChange(k)) return false;
    const sheetRooms = rooms.filter(r => r.SheetID === activeSheetId);
    const sheetConds = takeoffConditions.filter(c => c.SheetID === activeSheetId && c.GeometryJson);
    if (!sheetRooms.length && !sheetConds.length) return false;
    const routes = (canvasRoutes || []).filter(r => r.geometry?.points?.length || r.GeometryJson).length;
    const unit = partial.image_ft_per_inch != null ? "ft per paper inch" : "ft per pixel";
    const fmtS = (v) => (+v).toLocaleString(undefined, { maximumFractionDigits: 4 });
    const ok = window.confirm(
      [
        `Change this drawing's scale from ${fmtS(prevScale)} to ${fmtS(nextScale)} ${unit} (x${k.toFixed(3)})?`,
        `Its ${sheetRooms.length} room(s) and ${sheetConds.length} drawn condition(s) will be rescaled with it so they stay over the same part of the drawing and their lengths and areas stay correct. Real-world sizes (door widths, room heights) are not changed.`,
        ...(routes ? [`${routes} utility route segment(s) drawn on this revision are NOT rescaled.`] : []),
        "This clears the undo history and can't be undone.",
      ].join("\n\n")
    );
    if (!ok) return true; // declined: leave the scale and the takeoffs exactly as they are
    try {
      const plan = buildSheetRescale({
        factor: k, origin: { x: sheet.ImageX || 0, y: sheet.ImageY || 0 },
        rooms: sheetRooms, conditions: sheetConds, edgeOverrides, tunables, rulesByPair, wallSystemByRoom,
      });
      const payload = payloadOf({
        revision_id: activeRevId, sheet_id: activeSheetId,
        ...(partial.image_ft_per_inch != null ? { image_ft_per_inch: partial.image_ft_per_inch } : { image_scale_ft_per_px: partial.image_scale_ft_per_px }),
        ...plan,
      });
      await sqlRead(`EXEC sp_RescaleSheetTakeoffs @payload = '${payload}'`);
      setSheets(prev => prev.map(s => s.SheetID === activeSheetId ? { ...s, ...(partial.image_ft_per_inch != null ? { ImageFtPerInch: partial.image_ft_per_inch } : { ImageScaleFtPerPx: partial.image_scale_ft_per_px }) } : s));
      history.clear(); // earlier edits describe geometry that no longer exists at that scale
      load();
      broadcastChange();
    } catch (e) { alert("Error rescaling the drawing: " + e.message); }
    return true;
  }

  // A drawing with an image but no scale cannot be measured (belt and braces: the canvas also
  // refuses to arm a measuring tool). Blank canvases are in native feet and never need one.
  function refuseUnscaled() {
    if (!sheetNeedsScale(sheets.find(s => s.SheetID === activeSheetId))) return false;
    alert(SCALE_REQUIRED_MESSAGE);
    return true;
  }

  // Create + place a room drawn directly on the canvas (dims read from scale).
  // Append optimistically instead of reloading — a full load() would re-fetch
  // (and briefly drop) the multi-MB drawing underlay.
  async function addRoomFromCanvas({ name, lengthFt, widthFt, x, y, polygon }) {
    if (isLocked || !name || refuseUnscaled()) return;
    try {
      const t = roomTemplates.find(rt => rt.RoomTemplateID === newRoom.templateId);
      const height = header?.DefaultCeilingHt || 10;
      const displayOrder = (rooms[rooms.length - 1]?.DisplayOrder || 0) + 10;
      const polygonJson = polygon ? JSON.stringify(polygon) : null;
      const payload = payloadOf({
        revision_id:   activeRevId,
        sheet_id:      activeSheetId ?? null, // drawn on this drawing
        position_locked: 1,                   // drawn by the estimator: locked in place until unlocked
        room_name:     name,
        room_template_id: newRoom.templateId || null,
        length_ft:     lengthFt,
        width_ft:      widthFt,
        height_ft:     height,
        ach:           templateAch(t, classifications),
        classification_id: t?.ClassificationID ?? null,
        canvas_x:      x,
        canvas_y:      y,
        canvas_rot:    0,
        display_order: displayOrder,
        polygon_json:  polygonJson,
      });
      const res = await sqlRead(`EXEC sp_SaveTakeoffRoom @payload = '${payload}'`);
      const newId = (res?.Table1 || [])[0]?.RoomID;
      if (newId) {
        setRooms(prev => [...prev, {
          RoomID: +newId, RevisionID: activeRevId, SheetID: activeSheetId ?? null, RoomName: name, RoomTemplateID: newRoom.templateId || null,
          LengthFt: lengthFt, WidthFt: widthFt, HeightFt: height, ACH: templateAch(t, classifications),
          ClassificationID: t?.ClassificationID ?? null,
          PressureTarget: null, AHUZoneID: null, CanvasX: x, CanvasY: y, CanvasRot: 0,
          DisplayOrder: displayOrder, PolygonJson: polygonJson,
        }]);
        // Room Build-Up: same template seeding as createRoom / changeRoomTemplate
        // — this path skipped it until 2026-09-12, so a room drawn-then-named
        // with a template picked got the template's ACH but none of its
        // Standard Elements.
        if (newRoom.templateId) seedRoomElements(+newId, newRoom.templateId);
      } else {
        load(); // fallback if the id didn't come back
      }
      broadcastChange();
    } catch (e) { alert("Error adding room: " + e.message); }
  }

  // "+ Add Room" set-up-first flow (TakeoffCanvas.startAddRoom): create the
  // room UNPLACED (no dims, no canvas position) so the Room Details flyout can
  // open on a real row — name, template (ACH/classification defaults),
  // height, criteria — before the estimator draws where it goes. Returns the
  // new RoomID; the drawing then lands via placeDrawnRoom below. If the
  // estimator abandons the draw, the room simply stays in the schedule
  // unplaced, same as one added from the schedule tab.
  async function createRoom({ name }) {
    if (isLocked) return null;
    try {
      const t = roomTemplates.find(rt => rt.RoomTemplateID === newRoom.templateId);
      const height = header?.DefaultCeilingHt || 10;
      const displayOrder = (rooms[rooms.length - 1]?.DisplayOrder || 0) + 10;
      const payload = payloadOf({
        revision_id:   activeRevId,
        room_name:     name,
        room_template_id: newRoom.templateId || null,
        height_ft:     height,
        ach:           templateAch(t, classifications),
        classification_id: t?.ClassificationID ?? null,
        display_order: displayOrder,
      });
      const res = await sqlRead(`EXEC sp_SaveTakeoffRoom @payload = '${payload}'`);
      const newId = (res?.Table1 || [])[0]?.RoomID;
      if (!newId) { load(); return null; }
      setRooms(prev => [...prev, {
        RoomID: +newId, RevisionID: activeRevId, RoomName: name, RoomTemplateID: newRoom.templateId || null,
        LengthFt: null, WidthFt: null, HeightFt: height, ACH: templateAch(t, classifications),
        ClassificationID: t?.ClassificationID ?? null,
        PressureTarget: null, AHUZoneID: null, CanvasX: null, CanvasY: null, CanvasRot: 0,
        DisplayOrder: displayOrder, PolygonJson: null, RoomNumber: null,
      }]);
      // Room Build-Up: a template's Standard Elements become this room's
      // elements (db/70) — presence decided at the template, prunable here.
      if (newRoom.templateId) seedRoomElements(+newId, newRoom.templateId);
      broadcastChange();
      return +newId;
    } catch (e) { alert("Error adding room: " + e.message); return null; }
  }

  // Places an existing (unplaced) room from a finished canvas drawing — the
  // drawn extent wins over anything typed for L/W in the flyout, since the
  // drawing is the measured truth.
  function placeDrawnRoom(room, { x, y, lengthFt, widthFt, polygon }) {
    if (refuseUnscaled()) return;
    const polygonJson = polygon ? JSON.stringify(polygon) : null;
    // Placing a room puts it on the drawing it is traced on.
    saveRoom({ ...room, SheetID: activeSheetId ?? room.SheetID ?? null, LengthFt: lengthFt, WidthFt: widthFt, CanvasX: x, CanvasY: y, CanvasRot: 0, PolygonJson: polygonJson, PositionLocked: true }, { polygonJson }); // a room the estimator draws starts locked in place
  }

  // Lock / unlock rooms' POSITION (resize and corner edits stay available). One save per room through saveRoom.
  async function setRoomsPositionLocked(list, locked) {
    if (isLocked) return;
    for (const r of list) {
      const latest = roomsRef.current.find(x => x.RoomID === r.RoomID) || r;
      if (!!latest.PositionLocked === !!locked) continue;
      await saveRoom({ ...latest, PositionLocked: locked ? 1 : 0 });
    }
  }

  async function deleteRoom(room) {
    if (isLocked) return;
    if (!window.confirm(`Remove "${room.RoomName}" from the takeoff?`)) return;
    try {
      const payload = payloadOf({ room_id: room.RoomID, revision_id: activeRevId, is_delete: 1 });
      await sqlRead(`EXEC sp_SaveTakeoffRoom @payload = '${payload}'`);
      setRooms(prev => prev.filter(r => r.RoomID !== room.RoomID));
      // sp_SaveTakeoffRoom's delete path cascades to this room's own
      // TakeoffConditionsDrawn server-side — mirror that locally so a deleted
      // room's doors/wall runs disappear from the canvas immediately
      // instead of lingering until the next full reload.
      setTakeoffConditions(prev => prev.filter(c => c.RoomID !== room.RoomID));
      broadcastChange();
    } catch (e) { alert("Error: " + e.message); }
  }

  // Canvas multi-select delete — one confirm covering the whole group instead
  // of deleteRoom's per-room confirm.
  async function deleteRooms(roomList) {
    if (isLocked || !roomList.length) return;
    if (roomList.length === 1) return deleteRoom(roomList[0]);
    if (!window.confirm(`Remove ${roomList.length} rooms from the takeoff?`)) return;
    try {
      await Promise.all(roomList.map(room => {
        const payload = payloadOf({ room_id: room.RoomID, revision_id: activeRevId, is_delete: 1 });
        return sqlRead(`EXEC sp_SaveTakeoffRoom @payload = '${payload}'`);
      }));
      const ids = new Set(roomList.map(r => r.RoomID));
      setRooms(prev => prev.filter(r => !ids.has(r.RoomID)));
      setTakeoffConditions(prev => prev.filter(c => !ids.has(c.RoomID)));
      broadcastChange();
    } catch (e) { alert("Error: " + e.message); }
  }

  async function addRoom() {
    if (isLocked || !newRoom.name.trim()) return;
    setAdding(true);
    try {
      const t = roomTemplates.find(rt => rt.RoomTemplateID === newRoom.templateId);
      const payload = payloadOf({
        revision_id:   activeRevId,
        room_name:     newRoom.name.trim(),
        room_template_id: newRoom.templateId || null,
        height_ft:     header?.DefaultCeilingHt || 10,
        ach:           templateAch(t, classifications),
        classification_id: t?.ClassificationID ?? null,
        display_order: (rooms[rooms.length - 1]?.DisplayOrder || 0) + 10,
      });
      await sqlRead(`EXEC sp_SaveTakeoffRoom @payload = '${payload}'`);
      setNewRoom({ name: "", templateId: newRoom.templateId });
      load();
    } catch (e) { alert("Error: " + e.message); }
    finally { setAdding(false); }
  }

  // "+ New AHU…" straight from a room row: create the zone, then assign the
  // room to it — saves the round-trip through the + Add AHU button when the
  // takeoff has no zones yet.
  async function createZoneAndAssign(room) {
    if (isLocked) return;
    try {
      const payload = payloadOf({ revision_id: activeRevId, zone_name: `AHU-${zones.length + 1}` });
      const result = await sqlRead(`EXEC sp_SaveTakeoffZone @payload = '${payload}'`);
      const newId = (result?.Table1 || [])[0]?.AHUZoneID;
      if (newId) {
        await saveRoom({ ...room, AHUZoneID: +newId });
      }
      load();
    } catch (e) { alert("Error creating AHU: " + e.message); }
  }

  // Picking a cleanroom classification (Mechanical Systems Configuration's
  // editable table) populates ACH from its TargetACH — same one-time-default
  // UX as Room Template below, not a live join, so the estimator can still
  // fine-tune ACH per room afterward. Clearing the selection ("— none —")
  // leaves the room's current ACH untouched.
  function changeRoomClassification(room, classificationId) {
    const c = classifications.find(x => x.ClassificationID === classificationId);
    saveRoom({
      ...room,
      ClassificationID: classificationId || null,
      ACH: c && c.TargetACHUnit === "AC/HR" ? c.TargetACH : room.ACH,
    });
  }

  // Picking a Room Template (Setup > Estimate Engine > Rooms, db/55)
  // populates ACH and Classification from the template's own defaults — same
  // one-time-default UX as Classification above, not a live join.
  // The template's discipline-criteria defaults are NOT stamped onto the
  // room's own TakeoffCriteria rows here — they resolve live through
  // effectiveCriterion() instead (room._templateDefaults, takeoffCriteria.js),
  // so a later edit to the template itself is reflected immediately on every
  // room that picked it, not frozen at pick-time.
  function changeRoomTemplate(room, roomTemplateId) {
    const t = roomTemplates.find(x => x.RoomTemplateID === roomTemplateId);
    saveRoom({
      ...room,
      RoomTemplateID: roomTemplateId || null,
      ACH: templateAch(t, classifications) ?? room.ACH,
      ClassificationID: t?.ClassificationID != null ? t.ClassificationID : room.ClassificationID,
    });
    // Switching template ADDS the new template's Standard Elements the room
    // doesn't have yet; nothing is removed automatically.
    if (roomTemplateId) seedRoomElements(room.RoomID, roomTemplateId);
  }

  // ── Layout canvas handlers ────────────────────────────────────────────────────
  // PolygonJson stores ABSOLUTE world-feet points (independent of CanvasX/
  // CanvasY), so a plain drag has to translate the stored shape by the same
  // delta or it stays frozen at its old spot while the room's position moves
  // out from under it. Only fires when the room actually has a polygon.
  // Room commands: each is a PATCH of the fields it changes, applied to the screen at once and saved through
  // sp_SaveTakeoffRoom; undo applies the "before" patch over the row as it is now.
  const saveConflictRef = useRef(false); // set by saveRoom when an edit really collided with someone else's change
  async function applyRoomPatch(roomId, patch) {
    const latest = roomsRef.current.find(r => r.RoomID === roomId);
    if (!latest) throw new CommandStale("That room no longer exists.");
    const before = pickFields(latest, Object.keys(patch));
    setRooms(prev => prev.map(r => (r.RoomID === roomId ? { ...r, ...patch } : r)));
    const polygonJson = "PolygonJson" in patch ? (patch.PolygonJson ?? null) : undefined;
    saveConflictRef.current = false;
    const ok = await saveRoom({ ...latest, ...patch }, { polygonJson, skipLocalUpdate: true, touchedKeys: Object.keys(patch), baseline: before });
    if (!ok) {
      // a collision already replaced the rows with what is saved now; any other failure restores the old values
      if (!saveConflictRef.current) setRooms(prev => prev.map(r => (r.RoomID === roomId ? { ...r, ...before } : r)));
      throw new CommandFailed(); // saveRoom already told the estimator
    }
  }
  function roomPatchCommand(label, roomId, keys, after) {
    const latest = roomsRef.current.find(r => r.RoomID === roomId) || {};
    const before = pickFields(latest, keys);
    return { label, run: () => applyRoomPatch(roomId, after), undo: () => applyRoomPatch(roomId, before) };
  }

  // One gesture = one Undo step: dragging a group of rooms moves them all in a single step.
  function moveRooms(items) {
    if (isLocked || !items?.length) return Promise.resolve(false);
    const cmds = items.map(({ room, x, y, rot }) => {
      const latest = roomsRef.current.find(r => r.RoomID === room.RoomID) || room;
      // A room dropped from the "not placed" tray lands on the active drawing.
      const after = roomMovePatch(latest, { x, y, rot }, { activeSheetId, translatePolygon: translatePolygonJsonForSave });
      return roomPatchCommand(`move ${roomLabelOf(latest)}`, latest.RoomID, ROOM_MOVE_KEYS, after);
    });
    return cmds.length === 1 ? history.execute(cmds[0]) : history.batch(`move ${cmds.length} rooms`, cmds);
  }
  function moveRoom(room, x, y, rot) { return moveRooms([{ room, x, y, rot }]); }

  // A resize reverts a polygon room to a plain rectangle (db/21's own
  // documented behavior — the resized box no longer matches whatever shape
  // was traced) — null it out locally too, matching what the server does,
  // so the canvas doesn't keep showing the old shape until the next reload.
  // `pos` is passed when a west / north edge moved the room's anchor too: size and anchor are ONE patch,
  // so one save and one Undo step (they used to be two racing saves).
  function resizeRoom(room, lengthFt, widthFt, pos) {
    if (isLocked) return Promise.resolve(false);
    const latest = roomsRef.current.find(r => r.RoomID === room.RoomID) || room;
    return history.execute(roomPatchCommand(`resize ${roomLabelOf(latest)}`, latest.RoomID, ROOM_RESIZE_KEYS, roomResizePatch(lengthFt, widthFt, pos)));
  }

  // Commits a vertex drag/insert from the canvas (TakeoffCanvas.jsx's
  // startVertexDrag/startMidpointDrag) — points are the room's full,
  // already-edited corner list in world feet. Recomputes the bounding box
  // (LengthFt/WidthFt/CanvasX/CanvasY) from it so every rect-only feature
  // downstream (resize handles, adjacency, wall-symbol snap) still has a
  // correct box to work with, same convention the AI-extraction path uses.
  function editRoomPolygon(room, rawPoints) {
    // Simplify before persisting — same cleanup polygonOf() applies on every
    // read, so a fresh manual edit can't reintroduce/save a new near-
    // duplicate or near-collinear vertex either (see simplifyPolygon's own
    // comment in takeoffGeometry.js).
    const points = simplifyPolygon(rawPoints);
    const xs = points.map(p => p.x), ys = points.map(p => p.y);
    const x = snapToSixteenth(Math.min(...xs)), y = snapToSixteenth(Math.min(...ys));
    const w = snapToSixteenth(Math.max(...xs) - Math.min(...xs));
    const h = snapToSixteenth(Math.max(...ys) - Math.min(...ys));
    const rot90 = room.CanvasRot === 90;
    const polygonJson = JSON.stringify({
      points,
      areaFt2: Math.round(polygonArea(points) * 10) / 10,
      perimeterFt: Math.round(polygonPerimeter(points) * 10) / 10,
    });
    if (isLocked) return Promise.resolve(false);
    const latest = roomsRef.current.find(r => r.RoomID === room.RoomID) || room;
    const after = { CanvasX: x, CanvasY: y, LengthFt: rot90 ? h : w, WidthFt: rot90 ? w : h, PolygonJson: polygonJson };
    return history.execute(roomPatchCommand(`reshape ${roomLabelOf(latest)}`, latest.RoomID, ROOM_RESHAPE_KEYS, after));
  }

  // ── Drawing sheets (multiple named drawings per revision) ──────────────────────
  // Saves against the ACTIVE sheet — creates it (sheet_id omitted) the first
  // time a drawing is loaded with none active yet, else updates it in place.
  // "+ Add sheet" (addSheet, below) always creates a new one regardless of
  // what's active. State is patched locally rather than re-fetched so a small
  // change (opacity, pan) never re-pulls every sheet's multi-MB image blob.
  async function saveActiveSheet(partial, { prevScale } = {}) {
    if (isLocked) return;
    // A scale change on a drawing that already has takeoffs moves them with it (one atomic proc).
    if (partial.background_image === undefined && activeSheetId && (partial.image_ft_per_inch != null || partial.image_scale_ft_per_px != null)) {
      if (await rescaleActiveSheet(partial, prevScale)) return;
    }
    try {
      const { background_image, ...rest } = partial;
      const payloadJson = JSON.stringify({ revision_id: activeRevId, sheet_id: activeSheetId ?? null, ...rest });
      const hasImage = background_image !== undefined;
      const query = hasImage
        ? `EXEC sp_SaveTakeoffDrawingSheet @payload = @payload, @image = @image`
        : `EXEC sp_SaveTakeoffDrawingSheet @payload = @payload`;
      const params = hasImage ? { payload: payloadJson, image: background_image } : { payload: payloadJson };
      const res = await sqlRead(query, params);
      const isNew = !activeSheetId;
      const sheetId = isNew ? +res?.Table1?.[0]?.SheetID : activeSheetId;
      if (!sheetId) throw new Error("Save did not return a sheet id");

      setSheets(prev => {
        const existing = prev.find(s => s.SheetID === sheetId);
        const next = { ...(existing || { SheetID: sheetId, RevisionID: activeRevId, SheetName: partial.sheet_name || "Sheet 1", ImageX: 0, ImageY: 0, ImageOpacity: 0.5 }) };
        if (partial.needs_review !== undefined) next.NeedsReview = !!partial.needs_review;
        if (partial.clear_image) { next.FileHash = null; next.FileName = null; next.NeedsReview = false; next.BackgroundImage = null; next.ImageScaleFtPerPx = null; next.ImageIsPdf = 0; next.ImagePage = null; next.ImageFtPerInch = null; }
        // A new drawing resets the calibration/type (mirrors sp_SaveTakeoffDrawingSheet)
        if (partial.background_image !== undefined) {
          // Mirrors sp_SaveTakeoffDrawingSheet: a replacement of the same kind keeps the scale; only a first
          // image or a PDF <-> image change resets it.
          const hadImage = !!existing?.BackgroundImage;
          const kindChanged = hadImage && !!existing.ImageIsPdf !== !!partial.image_is_pdf;
          next.BackgroundImage = partial.background_image;
          if (!hadImage || kindChanged) {
            next.ImageScaleFtPerPx = partial.image_scale_ft_per_px ?? null;
            next.ImageFtPerInch = partial.image_ft_per_inch ?? null;
          }
          next.FileHash = partial.file_hash ?? null;
          next.FileName = partial.file_name ?? null;
          const r0 = res?.Table1?.[0];
          if (r0) {
            if (r0.VersionNumber != null) next.VersionNumber = +r0.VersionNumber;
            if (r0.NeedsReview != null) next.NeedsReview = !!r0.NeedsReview;
            if (r0.Replaced) next.ReplacedAt = new Date().toISOString();
          }
        } else {
          if (partial.image_scale_ft_per_px !== undefined) next.ImageScaleFtPerPx = partial.image_scale_ft_per_px;
          if (partial.image_ft_per_inch !== undefined) next.ImageFtPerInch = partial.image_ft_per_inch;
        }
        if (partial.image_is_pdf !== undefined) next.ImageIsPdf = partial.image_is_pdf;
        if (partial.image_page !== undefined) next.ImagePage = partial.image_page;
        if (partial.image_rotation !== undefined) next.ImageRotation = partial.image_rotation;
        if (partial.clear_image) next.ImageRotation = 0;
        if (partial.image_x !== undefined) next.ImageX = partial.image_x;
        if (partial.image_y !== undefined) next.ImageY = partial.image_y;
        if (partial.image_opacity !== undefined) next.ImageOpacity = partial.image_opacity;
        if (partial.sheet_name !== undefined) next.SheetName = partial.sheet_name;
        return existing ? prev.map(s => s.SheetID === sheetId ? next : s) : [...prev, next];
      });
      if (isNew) setActiveSheetId(sheetId);
      broadcastChange();
    } catch (e) { alert("Error saving drawing sheet: " + e.message); }
  }

  // Always creates a new sheet, regardless of which one is currently active —
  // the file-reading/size-limit work happens in TakeoffCanvas, this just
  // persists the result and switches to it.
  async function addSheet({ sheetName, backgroundImage, imageIsPdf, sheetNumber = null, discipline = null, fileHash = null, fileName = null }, { activate = true } = {}) {
    if (isLocked) return null;
    try {
      // A3 — sheet_number / discipline are hints parsed from the file name (editable under ⚙ Manage)
      const payloadJson = JSON.stringify({ revision_id: activeRevId, sheet_name: sheetName, image_is_pdf: imageIsPdf ? 1 : 0, image_page: 1, sheet_number: sheetNumber, discipline, file_hash: fileHash, file_name: fileName });
      const res = await sqlRead(`EXEC sp_SaveTakeoffDrawingSheet @payload = @payload, @image = @image`, { payload: payloadJson, image: backgroundImage });
      const sheetId = +res?.Table1?.[0]?.SheetID;
      if (!sheetId) throw new Error("Save did not return a sheet id");
      setSheets(prev => [...prev, {
        SheetID: sheetId, RevisionID: activeRevId, SheetName: sheetName,
        BackgroundImage: backgroundImage, ImageIsPdf: imageIsPdf ? 1 : 0, ImagePage: 1,
        ImageX: 0, ImageY: 0, ImageOpacity: 0.5, ImageScaleFtPerPx: null, ImageFtPerInch: null,
        SheetNumber: sheetNumber, Discipline: discipline, DisplayOrder: Math.max(0, ...prev.map(s => s.DisplayOrder ?? 100)) + 10,
        FileHash: fileHash, FileName: fileName, VersionNumber: 1, NeedsReview: false, ImageRotation: 0,
      }]);
      if (activate) setActiveSheetId(sheetId);
      broadcastChange();
      return sheetId;
    } catch (e) { alert("Error adding sheet: " + e.message); return null; }
  }

  // Duplicate a drawing so a second takeoff can be done on the same file. The copy is a NEW sheet row: it keeps the file (or the
  // stored-set pointer), page, scale, position, opacity and rotation, and starts with no rooms or conditions (they belong to a
  // sheet by SheetID, so nothing carries over). Switches to the copy.
  async function duplicateSheet(sheetId) {
    if (isLocked) return null;
    const src = sheets.find(s => s.SheetID === sheetId);
    if (!canDuplicateSheet(src)) return null;
    try {
      const name = duplicateSheetName(src.SheetName, sheets.map(s => s.SheetName));
      const { payload, image } = duplicateSheetRequest(src, activeRevId, name);
      const res = await sqlRead(image ? "EXEC sp_SaveTakeoffDrawingSheet @payload = @payload, @image = @image" : "EXEC sp_SaveTakeoffDrawingSheet @payload = @payload",
        image ? { payload: JSON.stringify(payload), image } : { payload: JSON.stringify(payload) });
      const newId = +res?.Table1?.[0]?.SheetID;
      if (!newId) throw new Error("Save did not return a sheet id");
      setSheets(prev => [...prev, {
        ...src, SheetID: newId, SheetName: name, IsKeySheet: false, VersionNumber: 1, NeedsReview: false, ReplacedAt: null,
        DisplayOrder: Math.max(0, ...prev.map(s => s.DisplayOrder ?? 100)) + 10,
        ImageRotation: src.ImageRotation ?? 0,
      }]);
      setActiveSheetId(newId);
      broadcastChange();
      return newId;
    } catch (e) { alert("Error duplicating drawing: " + e.message); return null; }
  }

  // A drawing's file history (newest 3 replaced files kept): list without blobs, and restore one.
  const loadSheetVersions = useCallback(async (sheetId) => {
    const r = await sqlRead(`EXEC sp_GetTakeoffDrawingSheetVersions @sheet_id=${+sheetId}`);
    return r?.Table1 || [];
  }, []);
  async function restoreSheetVersion(sheetId, versionId) {
    if (isLocked) return;
    const payload = payloadOf({ revision_id: activeRevId, sheet_id: sheetId, version_id: versionId });
    const res = await sqlRead(`EXEC sp_RestoreTakeoffDrawingSheetVersion @payload = '${payload}'`);
    const row = res?.Table1?.[0];
    if (!row) throw new Error("Restore did not return the drawing");
    setSheets(prev => prev.map(s => s.SheetID === sheetId ? { ...s, ...row } : s));
    broadcastChange();
  }

  // Takeoff snapshots (db/85): save / list / restore / delete named checkpoints of the revision's rooms,
  // drawn conditions, edge overrides and drawing scale+position. The database captures and restores them
  // atomically; a restore saves an automatic snapshot of the current state first.
  const loadSnapshots = useCallback(async () => {
    const r = await sqlRead(`EXEC sp_GetTakeoffSnapshots @revision_id=${+activeRevId}`);
    return r?.Table1 || [];
  }, [activeRevId]);
  async function saveSnapshot({ name, note }) {
    const payload = payloadOf({ revision_id: activeRevId, name, note: note || null, created_by: currentUserEmail });
    const r = await sqlRead(`EXEC sp_SaveTakeoffSnapshot @payload = '${payload}'`);
    return r?.Table1?.[0];
  }
  async function restoreSnapshot(snapshotId) {
    if (isLocked) throw new Error("This revision is locked");
    const payload = payloadOf({ revision_id: activeRevId, snapshot_id: snapshotId, created_by: currentUserEmail });
    const r = await sqlRead(`EXEC sp_RestoreTakeoffSnapshot @payload = '${payload}'`);
    history.clear(); // earlier edits describe rows that were just replaced
    load();
    broadcastChange();
    return r?.Table1?.[0];
  }
  // Takeoff file (utils/takeoffFile.js): export reads the revision's takeoff document from the database (read-only);
  // import replaces this revision's takeoff in one transaction after an automatic snapshot (sp_ImportTakeoffDocument).
  async function exportTakeoffFile() {
    const r = await sqlRead(`EXEC sp_GetTakeoffDocument @revision_id=${activeRevId}`);
    const text = r?.Table1?.[0]?.Document;
    if (!text) throw new Error("The database returned no takeoff for this revision.");
    const rev = revisions.find(x => x.RevisionID === activeRevId);
    const project = `Estimate-${projectId}`, revision = rev ? `Rev ${rev.RevisionNumber}` : "";
    const file = buildTakeoffFile({ document: text, sheets, project, revision, revisionId: activeRevId, host: window.location.host, exportedBy: currentUserEmail });
    return { filename: takeoffFileName({ project, revision }), text: serializeTakeoffFile(file), counts: file.counts };
  }
  // Project archive: the takeoff file plus the chosen drawings' files, in one zip (utils/takeoffArchive.js).
  async function exportTakeoffArchive(selectedIds) {
    const base = await exportTakeoffFile();
    const plan = planArchive({ sheets, selected: new Set(selectedIds) });
    const chosen = sheets.filter(s => plan.items.find(i => i.sheetId === s.SheetID)?.included);
    const { bytes, manifest } = buildArchive({ takeoffText: base.text, sheets: chosen });
    const rev = revisions.find(x => x.RevisionID === activeRevId);
    return { filename: archiveFileName({ project: `Estimate-${projectId}`, revision: rev ? `Rev ${rev.RevisionNumber}` : "" }), bytes, counts: { ...base.counts, files: manifest.drawings.length } };
  }
  // Creates a drawing (sheet) for each archived file the target revision does not have yet. -> { [file's sheetId]: new SheetID }
  async function createSheetsFromArchive(items) {
    if (isLocked) throw new Error("This revision is locked");
    const made = {};
    for (const it of items) {
      const d = it.archive;
      if (d.tooLarge) throw new Error(`"${d.name}" is too large to store as a drawing here.`);
      const dataUrl = d.dataUrl();
      const fileHash = await sha256Hex(dataUrl);
      const fd = it.drawing;
      const id = await addSheet({ sheetName: fd.name || d.name, backgroundImage: dataUrl, imageIsPdf: d.mime === "application/pdf", sheetNumber: fd.number || null, discipline: fd.discipline || null, fileHash, fileName: (fd.fileName || d.fileName || null) }, { activate: false });
      if (!id) throw new Error(`The drawing "${d.name}" could not be added.`);
      made[fd.sheetId] = id;
    }
    return made;
  }
  // Add the ticked pages of a stored plan set as drawings (sheets). Each becomes a sheet that POINTS at the set + page — the file
  // is not copied into the database. Sequential, so a failure part-way leaves exactly the pages that were added.
  async function addSheetsFromSet({ set, pages }) {
    if (isLocked) throw new Error("This revision is locked");
    if (!pages.length) return;
    let firstNew = null;
    for (const pg of pages) {
      const payload = JSON.stringify({ revision_id: activeRevId, sheet_name: pg.name, set_id: set.SetID, image_page: pg.page, sheet_number: pg.number, discipline: pg.discipline, file_name: set.FileName });
      const res = await sqlRead("EXEC sp_SaveTakeoffDrawingSheet @payload = @payload", { payload });
      const sheetId = +res?.Table1?.[0]?.SheetID;
      if (!sheetId) throw new Error(`The drawing for page ${pg.page} was not saved.`);
      firstNew = firstNew ?? sheetId;
      setSheets(prev => [...prev, {
        SheetID: sheetId, RevisionID: activeRevId, SheetName: pg.name, BackgroundImage: blobPointer(set.BlobName), ImageIsPdf: 1, ImagePage: pg.page,
        ImageX: 0, ImageY: 0, ImageOpacity: 0.5, ImageScaleFtPerPx: null, ImageFtPerInch: null,
        SheetNumber: pg.number, Discipline: pg.discipline, DisplayOrder: Math.max(0, ...prev.map(s => s.DisplayOrder ?? 100)) + 10,
        FileHash: set.Sha256, FileName: set.FileName, VersionNumber: 1, NeedsReview: false,
        SetID: set.SetID, SetBlobName: set.BlobName, SetContentType: "application/pdf",
      }]);
    }
    if (firstNew) setActiveSheetId(firstNew);
    broadcastChange();
  }
  async function importTakeoffFile({ document, name }) {
    if (isLocked) throw new Error("This revision is locked");
    const payload = payloadOf({ revision_id: activeRevId, name, created_by: currentUserEmail, document });
    const r = await sqlRead(`EXEC sp_ImportTakeoffDocument @payload = '${payload}'`);
    history.clear(); // earlier edits describe rows that were just replaced
    load();
    broadcastChange();
    return r?.Table1?.[0];
  }
  async function deleteSnapshot(snapshotId) {
    const payload = payloadOf({ revision_id: activeRevId, snapshot_id: snapshotId });
    await sqlRead(`EXEC sp_DeleteTakeoffSnapshot @payload = '${payload}'`);
  }

  // Review gate: accept machine items (AI rooms, generated symbols, auto walls, template elements) in one
  // transaction. Stamps 'accepted' + who + when; anything already reviewed is left as it is. Not an edit,
  // so it is not in the undo history. Unreviewed items price either way.
  async function acceptMachineItems(accept) {
    if (isLocked) throw new Error("This revision is locked");
    const payload = payloadOf({ revision_id: activeRevId, reviewed_by: currentUserEmail, rooms: accept.rooms || [], conditions: accept.conditions || [], elements: accept.elements || [] });
    const r = await sqlRead(`EXEC sp_AcceptMachineItems @payload = '${payload}'`);
    loadConditions(); loadRoomElements(); load(); broadcastChange();
    return r?.Table1?.[0];
  }

  async function renameSheet(sheetId, name) {
    if (isLocked || !name?.trim()) return;
    setSheets(prev => prev.map(s => s.SheetID === sheetId ? { ...s, SheetName: name.trim() } : s));
    try {
      const payloadJson = JSON.stringify({ revision_id: activeRevId, sheet_id: sheetId, sheet_name: name.trim() });
      await sqlRead(`EXEC sp_SaveTakeoffDrawingSheet @payload = @payload`, { payload: payloadJson });
      broadcastChange();
    } catch (e) { alert("Error renaming sheet: " + e.message); }
  }

  // The drawing's legend (db/38 LegendJson) — part of the markup, so it is saved with the sheet. Allowed on a LOCKED
  // revision (presentation, not a quantity), which is exactly when a drawing gets printed for a client. Optimistic.
  async function saveSheetLegend(sheetId, legendJson) {
    setSheets(prev => prev.map(s => s.SheetID === sheetId ? { ...s, LegendJson: legendJson } : s));
    try {
      await sqlRead(`EXEC sp_SaveTakeoffSheetLegend @payload = @payload`, { payload: JSON.stringify({ sheet_id: sheetId, legend_json: legendJson }) });
      broadcastChange();
    } catch (e) {
      // The change is kept on screen (optimistic) but was not stored. Say so once per session, not on every toggle.
      console.error("Legend save failed:", e);
      if (!legendSaveWarned.current) { legendSaveWarned.current = true; alert("The legend is showing, but it could not be saved with the drawing: " + e.message + " (This message appears once.)"); }
    }
  }

  // A3 — sheet number / discipline (db/38 SheetNumber / Discipline); the proc
  // treats a present key as authoritative ('' clears) and an omitted key as preserve.
  async function saveSheetMeta(sheetId, patch) {
    if (isLocked) return;
    setSheets(prev => prev.map(s => s.SheetID === sheetId ? {
      ...s,
      ...(patch.sheet_number !== undefined ? { SheetNumber: patch.sheet_number || null } : {}),
      ...(patch.discipline !== undefined ? { Discipline: patch.discipline || null } : {}),
      ...(patch.is_key_sheet !== undefined ? { IsKeySheet: patch.is_key_sheet ? 1 : 0 } : {}),
    } : s));
    try {
      await sqlRead(`EXEC sp_SaveTakeoffDrawingSheet @payload = @payload`, { payload: JSON.stringify({ revision_id: activeRevId, sheet_id: sheetId, ...patch }) });
      broadcastChange();
    } catch (e) { alert("Error saving sheet details: " + e.message); }
  }

  // Swaps DisplayOrder with whichever sheet currently sits at that position —
  // the Manage Sheets dialog's ▲/▼ reorder buttons. Optimistic locally the
  // same way renameSheet is; both saves happen in parallel since they touch
  // different rows.
  async function reorderSheet(sheetId, direction) {
    if (isLocked) return;
    const ordered = [...sheets].sort((a, b) => (a.DisplayOrder ?? 100) - (b.DisplayOrder ?? 100));
    const i = ordered.findIndex(s => s.SheetID === sheetId);
    const j = i + direction;
    if (i < 0 || j < 0 || j >= ordered.length) return;
    const a = ordered[i], b = ordered[j];
    const aOrder = a.DisplayOrder ?? 100, bOrder = b.DisplayOrder ?? 100;
    setSheets(prev => prev.map(s => {
      if (s.SheetID === a.SheetID) return { ...s, DisplayOrder: bOrder };
      if (s.SheetID === b.SheetID) return { ...s, DisplayOrder: aOrder };
      return s;
    }));
    try {
      await Promise.all([
        sqlRead(`EXEC sp_SaveTakeoffDrawingSheet @payload = @payload`, { payload: JSON.stringify({ revision_id: activeRevId, sheet_id: a.SheetID, display_order: bOrder }) }),
        sqlRead(`EXEC sp_SaveTakeoffDrawingSheet @payload = @payload`, { payload: JSON.stringify({ revision_id: activeRevId, sheet_id: b.SheetID, display_order: aOrder }) }),
      ]);
      broadcastChange();
    } catch (e) { alert("Error reordering sheets: " + e.message); }
  }

  async function deleteSheet(sheetId) {
    if (isLocked) return;
    const sheet = sheets.find(s => s.SheetID === sheetId);
    // Takeoffs belong to a drawing; one that still has some is not removed (the proc refuses too).
    const nRooms = rooms.filter(r => r.SheetID === sheetId).length;
    const nConds = takeoffConditions.filter(c => c.SheetID === sheetId).length;
    if (nRooms || nConds) {
      alert(`"${sheet?.SheetName || "This drawing"}" still has ${nRooms} room(s) and ${nConds} drawn condition(s) on it. Delete them first, then remove the drawing.`);
      return;
    }
    if (!window.confirm(`Remove "${sheet?.SheetName || "this sheet"}"? This can't be undone.`)) return;
    try {
      const payloadJson = JSON.stringify({ revision_id: activeRevId, sheet_id: sheetId, is_delete: 1 });
      await sqlRead(`EXEC sp_SaveTakeoffDrawingSheet @payload = @payload`, { payload: payloadJson });
      setSheets(prev => {
        const next = prev.filter(s => s.SheetID !== sheetId);
        setActiveSheetId(cur => cur === sheetId ? (next[0]?.SheetID ?? null) : cur);
        return next;
      });
      broadcastChange();
    } catch (e) { alert("Error removing sheet: " + e.message); }
  }

  // Sets a joint's override directly — 'shared' | 'cavity' | 'ignore' | null
  // (null clears back to automatic classification). The Review Walls dock
  // panel uses this to resolve an ambiguous joint in one click from a list,
  // without needing to find and click the (sometimes tiny/hard-to-see) joint
  // line on the canvas itself.
  async function setEdgeType(edge, next) {
    if (isLocked) return;
    const key = edgeOverrideKey(edge.a.RoomID, edge.b.RoomID, edge.runKey);
    try {
      // Override capture (db/21 TakeoffEdgeOverrides.Original*): snapshot what
      // the engine inferred BEFORE this override — geometric classification,
      // confidence tier ignoring any existing override, gap, and the
      // Construction Standard rule that would have applied — so override-
      // learning can later ask "what did we keep getting wrong, and for
      // which room pairs?" without reconstructing past geometry.
      const asAuto = { ...edge, override: null, type: edge.auto };
      const tA = edge.a.RoomTemplateID ?? null, tB = edge.b.RoomTemplateID ?? null;
      const rule = tA != null && tB != null ? resolveConstructionRule(asAuto, rulesByPair.get(pairKey(tA, tB))) : null;
      const capture = {
        original_auto:      edge.auto,
        original_tier:      classifyConfidence(asAuto, tunables),
        original_gap_ft:    edge.gapFt ?? null,
        original_rule_id:   rule?.ruleId ?? null,
        room_template_a_id: tA,
        room_template_b_id: tB,
        changed_by:         currentUserEmail,
      };
      const save = (runKey, edgeType) => sqlRead(`EXEC sp_SaveTakeoffEdgeOverride @payload = '${payloadOf({
        revision_id: activeRevId,
        room_a_id:   edge.a.RoomID,
        room_b_id:   edge.b.RoomID,
        run_key:     runKey,
        edge_type:   edgeType,
        ...capture,
      })}'`);
      await save(edge.runKey, next);
      // The override being replaced was found under the legacy '' key
      // (saved before geometric run keys existed) — retire that row now that
      // the geometric one is written, otherwise clearing this override later
      // would just fall back to the stale legacy value again.
      const legacyKey = edge.overrideLegacy ? edgeOverrideKey(edge.a.RoomID, edge.b.RoomID, edge.legacyRunKey) : null;
      if (legacyKey) await save(edge.legacyRunKey, null);
      setEdgeOverrides(prev => {
        const n = { ...prev };
        if (next) n[key] = next; else delete n[key];
        if (legacyKey) delete n[legacyKey];
        return n;
      });
      broadcastChange();
    } catch (e) { alert("Error saving joint override: " + e.message); }
  }

  // Cycle a joint: shared → cavity → ignored → back to automatic
  function cycleEdge(edge) {
    const SEQ = ["shared", "cavity", "ignore"];
    const current = edge.override || null; // already resolved by detectEdges (geometric key, then legacy '' fallback)
    // A conflict (rooms drawn overlapping) isn't a wall to classify — the fix
    // is moving a room. The only override that makes sense is silencing it.
    if (edge.auto === "conflict") return setEdgeType(edge, current ? null : "ignore");
    let next;
    if (!current) next = SEQ[(SEQ.indexOf(edge.auto) + 1) % SEQ.length];
    else if (SEQ.indexOf(current) === SEQ.length - 1) next = null; // back to auto
    else next = SEQ[SEQ.indexOf(current) + 1];
    return setEdgeType(edge, next);
  }

  // Chase detail (type/clear width/accessibility/notes) on a cavity edge run
  // — independent of the shared/cavity/ignore classification above; a
  // separate save call/proc (sp_SaveChaseAttributes) since it's a richer
  // form save, not a one-click cycle.
  async function saveChaseAttributes(edge, attrs) {
    if (isLocked) return;
    const key = edgeOverrideKey(edge.a.RoomID, edge.b.RoomID, edge.runKey);
    try {
      const payload = payloadOf({
        revision_id:    activeRevId,
        room_a_id:      edge.a.RoomID,
        room_b_id:      edge.b.RoomID,
        run_key:        edge.runKey || "",
        chase_type:     attrs.chaseType || "",
        clear_width_ft: attrs.clearWidthFt === "" ? null : attrs.clearWidthFt,
        is_accessible:  attrs.isAccessible ? 1 : 0,
        notes:          attrs.notes || "",
      });
      await sqlRead(`EXEC sp_SaveChaseAttributes @payload = '${payload}'`);
      setChaseAttrs(prev => ({ ...prev, [key]: attrs }));
      broadcastChange();
    } catch (e) { alert("Error saving chase details: " + e.message); }
  }

  // ── Scope runs drawn on the canvas (model-based scope, TargetType='drawn') ─────
  // Existing drawn runs still render and can be deleted; the canvas button that
  // created new ones was retired in favour of Condition Library drawing (2026-09-05).
  async function deleteScopeApp(app) {
    if (isLocked) return;
    setScopeApps(prev => prev.filter(a => a.ScopeApplicationID !== app.ScopeApplicationID));
    try {
      const payload = payloadOf({ scope_application_id: app.ScopeApplicationID, revision_id: activeRevId, is_delete: 1 });
      await sqlRead(`EXEC sp_SaveScopeApplication @payload = '${payload}'`);
      broadcastChange();
    } catch (e) { alert("Error removing drawn scope: " + e.message); }
  }

  // ── Condition Library runs drawn on the canvas (cost-code + room takeoff) ──────
  async function createTakeoffCondition({ conditionTypeId, points, lengthFt, roomId }) {
    if (isLocked || refuseUnscaled()) return;
    try {
      const type = decodeTypeKey(conditionTypeId);
      const payload = payloadOf({
        revision_id:        activeRevId,
        sheet_id:           activeSheetId ?? null, // drawn on this drawing; the condition TYPE stays revision-wide
        condition_type_id:  type.condition_type_id,
        kind:                type.kind,
        item_id:             type.item_id,
        assembly_id:         type.assembly_id,
        room_id:             roomId ?? null,
        target_type:        "drawn",
        geometry_json:       JSON.stringify({ points, lengthFt }),
        display_order:       100,
      });
      const lineType = conditionTypes.find(t => t.TypeKey === conditionTypeId);
      await createDrawnCondition(`draw ${lineType?.Name || "condition"}`, payload, {
        errorPrefix: "Error saving condition",
        optimistic: lineType ? { typeRow: lineType, fields: { RevisionID: activeRevId, SheetID: activeSheetId ?? null, RoomID: roomId ?? null, Kind: type.kind, ItemID: type.item_id, AssemblyID: type.assembly_id, TakeoffConditionLibraryID: type.condition_type_id ?? null, GeometryJson: JSON.stringify({ points, lengthFt }) } } : null,
      });
    } catch (e) { alert("Error saving condition: " + e.message); }
  }

  // Area conditions (Condition Library Style='area') — mirrors
  // createTakeoffCondition above exactly, just with a closed-polygon
  // geometry shape ({points, areaFt2, perimeterFt}, same convention
  // TakeoffRooms.PolygonJson already uses) instead of a lengthFt run.
  async function createAreaCondition({ conditionTypeId, points, areaFt2, perimeterFt, roomId }) {
    if (isLocked || refuseUnscaled()) return;
    try {
      const type = decodeTypeKey(conditionTypeId);
      // A type with a tile grid (GridTileWFt/HFt, db/30) also stores the
      // polygon-walked visible tile count on the row — SQL can't walk the
      // polygon, so this is what sp_GetConditionTakeoffRollup /
      // sp_GetEstimateDesignRollup price from. Kept current by the
      // "Visible tile-count sync" effect below when the type's grid changes.
      const typeRow = conditionTypes.find(t => t.TypeKey === conditionTypeId);
      const grid = typeRow?.GridTileWFt > 0 && typeRow?.GridTileHFt > 0
        ? gridTileTakeoff(points, typeRow.GridTileWFt, typeRow.GridTileHFt, typeRow.GridGapIn, { areaFt2 })
        : null;
      const payload = payloadOf({
        revision_id:        activeRevId,
        sheet_id:           activeSheetId ?? null, // drawn on this drawing
        condition_type_id:  type.condition_type_id,
        kind:                type.kind,
        item_id:             type.item_id,
        assembly_id:         type.assembly_id,
        room_id:             roomId ?? null,
        target_type:        "drawn",
        geometry_json:       JSON.stringify(grid ? { points, areaFt2, perimeterFt, grid } : { points, areaFt2, perimeterFt }),
        display_order:       100,
      });
      await createDrawnCondition(`draw ${typeRow?.Name || "area"}`, payload, {
        errorPrefix: "Error saving area condition",
        optimistic: typeRow ? { typeRow, fields: { RevisionID: activeRevId, SheetID: activeSheetId ?? null, RoomID: roomId ?? null, Kind: type.kind, ItemID: type.item_id, AssemblyID: type.assembly_id, TakeoffConditionLibraryID: type.condition_type_id ?? null, GeometryJson: JSON.stringify(grid ? { points, areaFt2, perimeterFt, grid } : { points, areaFt2, perimeterFt }) } } : null,
      });
    } catch (e) { alert("Error saving area condition: " + e.message); }
  }

  // Back-out (Takeoff Canvas 2.0 D-2): a deduct polygon against one Room
  // Element — a TakeoffConditionsDrawn row with MeasureRole='deduct' and
  // AppliesToRoomElementID and NO catalog entity. It prices nothing itself;
  // roomElements.resolveElementQuantity subtracts it from that element's
  // area (Gross − back-outs = Net). Removed through deleteTakeoffCondition.
  async function createBackOut({ roomElementId, roomId, points, areaFt2 }) {
    if (isLocked || !roomElementId || refuseUnscaled()) return;
    try {
      const payload = payloadOf({
        revision_id: activeRevId, sheet_id: activeSheetId ?? null, room_id: roomId ?? null, target_type: "drawn",
        measure_role: "deduct", applies_to_room_element_id: roomElementId,
        geometry_json: JSON.stringify({ points, areaFt2 }), display_order: 100,
      });
      await createDrawnCondition("draw back-out", payload, { errorPrefix: "Error saving back-out" });
    } catch (e) { alert("Error saving back-out: " + e.message); }
  }

  // The undoable command that deletes one condition — or { engine: true, removeRow } for an engine-generated row
  // (AutoKey / AutoWallKey), which its engine re-syncs, so removing it is not an undoable edit.
  function conditionDeleteCommand(cond) {
    const ref = { id: cond.TakeoffConditionDrawnID };
    // Removes the CURRENT row (a re-created row has a new id). Optimistic; a failed save reloads the truth.
    const removeRow = async () => {
      setTakeoffConditions(prev => prev.filter(c => c.TakeoffConditionDrawnID !== ref.id));
      try {
        await sqlRead(`EXEC sp_SaveTakeoffConditionDrawn @payload = '${payloadOf({ takeoff_condition_id: ref.id, revision_id: activeRevId, is_delete: 1 })}'`);
      } catch (e) {
        loadConditions();
        alert("Error removing condition: " + e.message);
        throw new CommandFailed();
      }
      loadConditions();
      // A door/window symbol's removal can write-through-sync that room's door_count/window_count back
      // down — re-pull criteria too. Cheap no-op for a regular (non-synced) condition delete.
      load();
      broadcastChange();
    };
    if (cond.AutoKey || cond.AutoWallKey) return { engine: true, removeRow };
    const recreate = payloadOf(conditionRowToPayload(cond, activeRevId));
    return {
      label: `delete ${cond.Mark || cond.Code || cond.Name || (cond.MeasureRole === "deduct" ? "back-out" : "condition")}`,
      run: removeRow,
      undo: async () => { ref.id = await createConditionFromPayload(recreate, { reloadRooms: true }); },
    };
  }
  async function deleteTakeoffCondition(cond) {
    if (isLocked) return;
    const cmd = conditionDeleteCommand(cond);
    if (cmd.engine) { try { await cmd.removeRow(); } catch (e) { if (!(e instanceof CommandFailed)) throw e; } return; }
    await history.execute(cmd);
  }
  // Several at once (the canvas's multi-select): ONE undo step. Engine-generated rows are left out.
  async function deleteTakeoffConditions(rows) {
    if (isLocked || !rows?.length) return;
    const cmds = rows.map(conditionDeleteCommand).filter(c => !c.engine);
    if (cmds.length === 1) await history.execute(cmds[0]);
    else if (cmds.length > 1) await history.batch(`delete ${cmds.length} conditions`, cmds);
  }

  // Condition Detail Flyout — general attribute edit (type/room/height-override/
  // quantity-override/notes) on an already-placed condition, drawn or manual.
  // patch carries only the fields actually changed; falls back to the
  // condition's current value for anything omitted so a single-field edit
  // (e.g. just Notes) can't blank out the others. geometry_json is
  // deliberately never sent here — sp_SaveTakeoffConditionDrawn preserves the
  // existing drawn geometry when it's omitted, and this flyout never touches
  // where/how a condition is drawn, only its attributes.
  async function saveConditionAttributes(cond, patch) {
    if (isLocked) return;
    try {
      // Face B (RoomBID's own finish on a shared wall — see
      // TakeoffConditionsDrawn.TakeoffConditionLibraryIDFaceB, db/37_condition_library.sql)
      // needs to tell "untouched" apart from "explicitly cleared back to
      // symmetric" — unlike every other field here, a plain `??` fallback
      // can't do that (null and undefined collapse together), so it's
      // gated on whether the flyout's patch touched the key at all, and an
      // explicit null sends clear_face_b=1 rather than silently resending
      // the prior value.
      const touchesFaceB = "conditionTypeIdFaceB" in patch;
      // Phase B (see project memory): conditionTypeId is now a TypeKey
      // string ("ct:"/"item:"/"asm:") — only decode/send the type-family
      // fields when the patch actually changed it (same presence-check
      // idiom as touchesFaceB above), otherwise omit all four so
      // sp_SaveTakeoffConditionDrawn's "neither sent" branch preserves
      // whichever family the row already has instead of resending a stale
      // legacy TakeoffConditionLibraryID that might not even apply to a
      // Kind-based row.
      const touchesType = "conditionTypeId" in patch;
      const type = touchesType ? decodeTypeKey(patch.conditionTypeId) : null;
      // Phase D-2 (see project memory): Face B can now also be Kind-based,
      // so decode its TypeKey fully (same decodeTypeKey() used for Face A)
      // instead of only pulling condition_type_id back out.
      const faceBType = touchesFaceB ? decodeTypeKey(patch.conditionTypeIdFaceB) : null;
      // A placed symbol keeps its width in its geometry (set at placement from the type's default width). An explicit
      // Width edit changes it; picking another type resets it to that type's default width (when it has one) so the
      // drawn symbol, the schedule and any deduction follow the new type. Geometry is sent only when the width changes.
      const latest = conditionsRef.current.find(c => c.TakeoffConditionDrawnID === cond.TakeoffConditionDrawnID) || cond;
      const typeDefaultW = touchesType ? +(conditionTypes.find(t => t.TypeKey === patch.conditionTypeId)?.DefaultWidthFt) : NaN;
      const newWidth = "widthFt" in patch ? +patch.widthFt : (typeDefaultW > 0 ? typeDefaultW : null);
      const geometryJson = symbolGeometryWithWidth(latest, newWidth) ?? undefined;
      if (geometryJson) setTakeoffConditions(prev => patchRowById(prev, "TakeoffConditionDrawnID", cond.TakeoffConditionDrawnID, { GeometryJson: geometryJson }));
      const payload = payloadOf({
        takeoff_condition_id: cond.TakeoffConditionDrawnID,
        revision_id:          activeRevId,
        geometry_json:        geometryJson,
        condition_type_id:    touchesType ? type.condition_type_id : undefined,
        kind:                  touchesType ? type.kind : undefined,
        item_id:               touchesType ? type.item_id : undefined,
        assembly_id:           touchesType ? type.assembly_id : undefined,
        condition_type_id_face_b: touchesFaceB ? faceBType.condition_type_id : undefined,
        kind_face_b:              touchesFaceB ? faceBType.kind : undefined,
        item_id_face_b:            touchesFaceB ? faceBType.item_id : undefined,
        assembly_id_face_b:        touchesFaceB ? faceBType.assembly_id : undefined,
        clear_face_b:          touchesFaceB && patch.conditionTypeIdFaceB == null ? true : undefined,
        room_id:               "roomId" in patch ? patch.roomId : (cond.RoomID ?? null),
        height_ft_override:    "heightFtOverride" in patch ? patch.heightFtOverride : cond.HeightFtOverride,
        quantity_override:     "quantityOverride" in patch ? patch.quantityOverride : cond.QuantityOverride,
        notes:                  "notes" in patch ? patch.notes : cond.Notes,
        // Opening identity + estimator's "to" room (P1 Phase C) — sent only
        // when the flyout touched them; sp_SaveTakeoffConditionDrawn
        // preserves both when the key is absent (JSON_PATH_EXISTS).
        mark:                   "mark" in patch ? patch.mark : undefined,
        room_b_id:              "roomBId" in patch ? patch.roomBId : undefined,
        // Opening deduction policy (D-3) — same presence-gated idiom; null = inherit.
        deduction_policy:       "deductionPolicy" in patch ? (patch.deductionPolicy ?? "inherit") : undefined,
        display_order:          cond.DisplayOrder ?? 100,
        ...correctionStamp(cond, "condition", currentUserEmail), // a person's edit of a machine row reviews it
      });
      await sqlRead(`EXEC sp_SaveTakeoffConditionDrawn @payload = '${payload}'`);
      loadConditions();
      load(); // a type change can move which criteria field this room syncs to
      broadcastChange();
    } catch (e) { alert("Error saving condition: " + e.message); }
  }

  // "+Add Condition" on the dock — a condition with no drawn geometry at
  // all, for counting something off a spec sheet (e.g. "6 more Personnel
  // Doors") without precisely click-placing each instance on the canvas.
  // QuantityOverride carries the count/length directly since there's no
  // GeometryJson.lengthFt to fall back on; shows up in the dock list and
  // every rollup exactly like a drawn condition, just never on the canvas
  // itself (nothing to render without geometry).
  async function createManualCondition({ conditionTypeId, roomId, quantityOverride, heightFtOverride, notes }) {
    if (isLocked || !conditionTypeId) return;
    try {
      const type = decodeTypeKey(conditionTypeId);
      const payload = payloadOf({
        revision_id:        activeRevId,
        condition_type_id:  type.condition_type_id,
        kind:                type.kind,
        item_id:             type.item_id,
        assembly_id:         type.assembly_id,
        room_id:             roomId ?? null,
        target_type:        "manual",
        quantity_override:   quantityOverride ?? null,
        height_ft_override:  heightFtOverride ?? null,
        notes:                notes || null,
        display_order:        100,
      });
      await sqlRead(`EXEC sp_SaveTakeoffConditionDrawn @payload = '${payload}'`);
      loadConditions();
      load();
      broadcastChange();
    } catch (e) { alert("Error adding condition: " + e.message); }
  }

  // ── Room Detail Flyout scope/quantity list (db/39_room_scope_items.sql) ────────
  // Overrides/dismissals of the client-computed baseline (roomScopeFormulas.js)
  // plus fully custom added lines. sourceKey set + no id lets the proc resolve
  // an existing override via its (RoomID, SourceKey) unique index, so the
  // flyout never has to track an id for a baseline row it hasn't touched yet.
  async function saveRoomScopeItem(item) {
    if (isLocked) return;
    try {
      const payload = payloadOf({
        room_scope_item_id: item.roomScopeItemId ?? null,
        revision_id:        activeRevId,
        room_id:             item.roomId,
        source_key:           item.sourceKey ?? null,
        cost_code_id:         item.costCodeId ?? null,
        description:          item.description,
        unit:                 item.unit,
        quantity:             item.quantity,
        is_removed:           item.isRemoved ? 1 : 0,
        color:                item.color ?? null,
        pattern:              item.pattern ?? null,
        display_order:        item.displayOrder ?? 100,
      });
      await sqlRead(`EXEC sp_SaveRoomScopeItem @payload = '${payload}'`);
      loadRoomScopeItems();
      broadcastChange();
    } catch (e) { alert("Error saving scope item: " + e.message); }
  }

  async function deleteRoomScopeItem(roomScopeItemId) {
    if (isLocked || !roomScopeItemId) return;
    setRoomScopeItems(prev => prev.filter(i => i.RoomScopeItemID !== roomScopeItemId));
    try {
      const payload = payloadOf({ room_scope_item_id: roomScopeItemId, revision_id: activeRevId, is_delete: 1 });
      await sqlRead(`EXEC sp_SaveRoomScopeItem @payload = '${payload}'`);
      broadcastChange();
    } catch (e) { alert("Error removing scope item: " + e.message); loadRoomScopeItems(); }
  }

  // Edge Halo color settings — payload already carries the full 5-tier
  // shape (TakeoffCanvas.jsx builds it from its own current haloColors +
  // the one changed tier), so this is a plain resend-everything upsert,
  // same convention as sp_SaveRoomScopeItem's CostCodeID/Color/Pattern.
  async function saveHaloColors(payload) {
    if (isLocked) return;
    setHaloColors(prev => ({ ...prev, AutoApplyColor: payload.auto_apply_color, SuggestedColor: payload.suggested_color, NeedsReviewColor: payload.needs_review_color, ConflictColor: payload.conflict_color, ManualOverrideColor: payload.manual_override_color }));
    try {
      await sqlRead(`EXEC sp_SaveTakeoffHaloSettings @payload = '${payloadOf({ revision_id: activeRevId, ...payload })}'`);
      broadcastChange();
    } catch (e) { alert("Error saving Edge Halo colors: " + e.message); }
  }

  // Track J — one review-state upsert (or clear) per change, then re-read the
  // revision's rows so the dock reflects exactly what was stored.
  async function saveReviewState(patch) {
    if (isLocked || !activeRevId) return;
    try {
      await sqlRead(`EXEC sp_SaveTakeoffReviewState @payload = '${payloadOf({ revision_id: activeRevId, ...patch })}'`);
      const r = await sqlRead(`EXEC sp_GetTakeoffReviewStates @revision_id=${activeRevId}`);
      setReviewStates(r?.Table1 || []);
      broadcastChange();
    } catch (e) { alert("Error saving review state: " + e.message); }
  }

  // ── Door/window symbols (Condition Library Style='count') ──────────────────────
  // Placement snaps to a wall on the canvas (src/utils/takeoffGeometry.js's
  // snapToNearestWall); a symbol type that names SyncsToCriteriaField keeps
  // that room's Discipline Criteria count in sync server-side
  // (sp_SaveTakeoffConditionDrawn's write-through) — reload criteria after either
  // write since that happens outside this tab's own state.
  async function placeSymbol({ conditionTypeId, x, y, angleDeg, wallNormalDeg, widthFt, roomId }) {
    if (isLocked || refuseUnscaled()) return;
    try {
      const type = decodeTypeKey(conditionTypeId);
      // P1 Phase C: an opening (door / window / pass-through type) gets its
      // identity the moment it is placed — the next free '<D|W|PT>-<room>-<n>'
      // for that room (openings.js). Editable in the flyout, never renumbered.
      const typeRow = conditionTypes.find(t => t.TypeKey === conditionTypeId);
      const openingKind = openingKindOf(typeRow);
      const mark = openingKind ? suggestOpeningMark(openingKind, rooms.find(r => r.RoomID === roomId) || null, takeoffConditions) : undefined;
      const payload = payloadOf({
        mark,
        revision_id:       activeRevId,
        sheet_id:          activeSheetId ?? null, // placed on this drawing
        condition_type_id: type.condition_type_id,
        kind:               type.kind,
        item_id:            type.item_id,
        assembly_id:        type.assembly_id,
        room_id:            roomId ?? null,
        target_type:        "drawn",
        geometry_json:      JSON.stringify({ x, y, angleDeg, wallNormalDeg, widthFt, flip: false }),
        quantity_override:  1,
        display_order:      100,
      });
      await createDrawnCondition(`place ${mark || typeRow?.Name || "symbol"}`, payload, {
        errorPrefix: "Error placing symbol", reloadRooms: true,
        optimistic: typeRow ? { typeRow, fields: { RevisionID: activeRevId, SheetID: activeSheetId ?? null, RoomID: roomId ?? null, Kind: type.kind, ItemID: type.item_id, AssemblyID: type.assembly_id, TakeoffConditionLibraryID: type.condition_type_id ?? null, GeometryJson: JSON.stringify({ x, y, angleDeg, wallNormalDeg, widthFt, flip: false }), QuantityOverride: 1, Mark: mark ?? null } } : null,
      });
    } catch (e) { alert("Error placing symbol: " + e.message); }
  }

  // Count by example: place every ticked find as ONE undoable step. Each symbol is an ordinary placed row on ITS
  // drawing (its room is the one containing it on that drawing, else none), saved one at a time but reloaded
  // once. A failure part-way undoes what was placed and reports it; nothing is recorded unless all of them saved.
  // Create several drawn conditions as ONE undo step (count by example, paste, duplicate). Each is saved one at
  // a time and the lists are reloaded once; a failure part-way undoes what was created and records nothing.
  async function createConditionsBatch(label, payloads) {
    const last = payloads.length - 1;
    const cmds = payloads.map((payload, i) => {
      const ref = { id: null };
      return {
        label,
        run: async () => {
          try {
            const r = await sqlRead(`EXEC sp_SaveTakeoffConditionDrawn @payload = '${payload}'`);
            ref.id = (r?.Table1 || [])[0]?.TakeoffConditionDrawnID ?? null;
          } catch (e) { alert(`Error creating condition ${i + 1} of ${payloads.length}: ${e.message}`); throw new CommandFailed(); }
          if (i === last) { loadConditions(); load(); broadcastChange(); }
        },
        undo: async () => {
          if (ref.id != null) await sqlRead(`EXEC sp_SaveTakeoffConditionDrawn @payload = '${payloadOf({ takeoff_condition_id: ref.id, revision_id: activeRevId, is_delete: 1 })}'`);
          if (i === 0) { loadConditions(); load(); broadcastChange(); }
        },
      };
    });
    const ok = await history.batch(label, cmds);
    if (!ok) { loadConditions(); load(); }
    return ok;
  }

  async function countByExample({ conditionTypeId, placements, widthFt }) {
    if (isLocked || !placements?.length) return { placed: 0 };
    try {
      const type = decodeTypeKey(conditionTypeId);
      const typeRow = conditionTypes.find(t => t.TypeKey === conditionTypeId);
      const openingKind = openingKindOf(typeRow);
      const made = []; // rows created in this batch, so two new openings in one room do not get the same mark
      const payloads = placements.map((p) => {
        const sheetRooms = rooms.filter(r => r.SheetID === p.sheetId || r.SheetID == null);
        const roomId = roomContainingPolygon([{ x: p.x, y: p.y }], sheetRooms);
        const room = rooms.find(r => r.RoomID === roomId) || null;
        const mark = openingKind ? suggestOpeningMark(openingKind, room, [...takeoffConditions, ...made]) : undefined;
        if (mark) made.push({ Mark: mark, RoomID: roomId });
        return payloadOf({
          mark, revision_id: activeRevId, sheet_id: p.sheetId, condition_type_id: type.condition_type_id, kind: type.kind, item_id: type.item_id, assembly_id: type.assembly_id,
          room_id: roomId ?? null, target_type: "drawn",
          geometry_json: JSON.stringify({ x: p.x, y: p.y, angleDeg: p.angleDeg || 0, wallNormalDeg: 0, widthFt, flip: !!p.flip }),
          quantity_override: 1, display_order: 100,
        });
      });
      const ok = await createConditionsBatch(`count by example: ${placements.length} ${typeRow?.Name || "symbols"}`, payloads);
      return { placed: ok ? placements.length : 0 };
    } catch (e) { alert("Error placing symbols: " + e.message); return { placed: 0 }; }
  }

  // Paste / duplicate drawn conditions (utils/conditionClipboard.js decides what and where): each copy keeps its
  // catalog type and shape, lands on THIS drawing (sheetId), gets its room re-detected from where it now is, and a
  // new opening mark — an opening's identity (D-101) is never shared. One undo step.
  async function duplicateConditions({ items, sheetId }) {
    if (isLocked || !items?.length) return { created: 0 };
    try {
      const sheetRooms = rooms.filter(r => r.SheetID === sheetId || r.SheetID == null);
      const made = [];
      const payloads = items.map(({ row, geometryJson }) => {
        const pts = pointsOfRow({ ...row, GeometryJson: geometryJson });
        const roomId = (row.Style === "linear" ? roomsAlongPolyline(pts, sheetRooms).primaryRoomId : roomContainingPolygon(pts, sheetRooms)) ?? null;
        const room = rooms.find(r => r.RoomID === roomId) || null;
        const openingKind = openingKindOf(row);
        const mark = openingKind ? suggestOpeningMark(openingKind, room, [...takeoffConditions, ...made]) : null;
        if (mark) made.push({ Mark: mark, RoomID: roomId });
        return payloadOf({ ...conditionRowToPayload(row, activeRevId), sheet_id: sheetId, geometry_json: geometryJson, room_id: roomId, mark });
      });
      const ok = await createConditionsBatch(`paste ${items.length} condition${items.length === 1 ? "" : "s"}`, payloads);
      return { created: ok ? items.length : 0 };
    } catch (e) { alert("Error pasting conditions: " + e.message); return { created: 0 }; }
  }

  // Drag an already-placed symbol to a new snapped wall position — same
  // upsert as flipSymbol, just replacing x/y/angleDeg/wallNormalDeg/roomId
  // instead of toggling flip. TakeoffCanvas computes the new snap live
  // during the drag (snapToNearestWall) and only calls this once, on release.
  async function moveSymbol(cond, snap) {
    if (isLocked) return;
    const latest = conditionsRef.current.find(c => c.TakeoffConditionDrawnID === cond.TakeoffConditionDrawnID) || cond;
    let geom = {};
    try { geom = JSON.parse(latest.GeometryJson || "{}"); } catch { /* ignore */ }
    const after = { GeometryJson: JSON.stringify({ ...geom, x: snap.x, y: snap.y, angleDeg: snap.angleDeg, wallNormalDeg: snap.wallNormalDeg }), RoomID: snap.roomId ?? null };
    const before = pickFields(latest, ["GeometryJson", "RoomID"]);
    // An engine-generated symbol the estimator drags keeps its identity but freezes its position against
    // future layout syncs (db/37 AutoPositionLocked; autoLayoutEngine.js). Hand-placed rows are not locked.
    await history.execute(conditionPatchCommand(`move ${latest.Mark || latest.Code || latest.Name || "symbol"}`, latest.TakeoffConditionDrawnID, before, after, { lockPosition: !!latest.AutoSystem }));
  }

  // Rotate / spin / flip drawn conditions (utils/rotateConditions.js decides the new geometry): ONE undo step, each row's
  // room re-detected from where it now lies. Returns the notice the canvas shows.
  async function transformConditions({ rows, op, spin }) {
    if (isLocked || !rows?.length || !op) return null;
    const { changes, skipped } = planTransform(rows.map(r => conditionsRef.current.find(c => c.TakeoffConditionDrawnID === r.TakeoffConditionDrawnID) || r), op, { spin });
    const verb = op.type === "rotate" ? (spin ? "Spun" : "Rotated") : op.type === "translate" ? "Moved" : op.type === "scale" ? "Resized" : "Flipped";
    if (!changes.length) return transformNotice(verb, 0, skipped);
    const sheetRooms = rooms.filter(r => r.SheetID === (changes[0].row.SheetID ?? null) || r.SheetID == null);
    const cmds = changes.map(({ row, geometryJson }) => {
      const pts = pointsOfRow({ ...row, GeometryJson: geometryJson });
      const roomId = (row.Style === "linear" ? roomsAlongPolyline(pts, sheetRooms).primaryRoomId : roomContainingPolygon(pts, sheetRooms)) ?? null;
      return conditionPatchCommand(`${verb.toLowerCase()} ${row.Mark || row.Code || row.Name || "condition"}`, row.TakeoffConditionDrawnID,
        pickFields(row, ["GeometryJson", "RoomID"]), { GeometryJson: geometryJson, RoomID: roomId });
    });
    try {
      if (cmds.length === 1) await history.execute(cmds[0]); else await history.batch(`${verb.toLowerCase()} ${cmds.length} conditions`, cmds);
    } catch { return null; } // a failed save reports itself and rolls back
    return transformNotice(verb, changes.length, skipped);
  }

  async function flipSymbol(cond) {
    if (isLocked) return;
    const latest = conditionsRef.current.find(c => c.TakeoffConditionDrawnID === cond.TakeoffConditionDrawnID) || cond;
    let geom = {};
    try { geom = JSON.parse(latest.GeometryJson || "{}"); } catch { /* ignore */ }
    const after = { GeometryJson: JSON.stringify({ ...geom, flip: !geom.flip }) };
    await history.execute(conditionPatchCommand(`flip ${latest.Mark || latest.Code || latest.Name || "symbol"}`, latest.TakeoffConditionDrawnID, pickFields(latest, ["GeometryJson"]), after));
  }

  // ── Discipline criteria ───────────────────────────────────────────────────────
  async function saveCriterion(discipline, fieldKey, roomId, value) {
    if (isLocked) return;
    const key = criterionKey(discipline, fieldKey, roomId);
    const prev = criteria[key];
    // Optimistic: empty value clears the row (per-room = fall back to default)
    setCriteria(p => {
      const n = { ...p };
      if (value === "" || value === null) delete n[key]; else n[key] = value;
      return n;
    });
    try {
      const payload = payloadOf({
        revision_id: activeRevId,
        room_id:     roomId ?? null,
        discipline,
        field_key:   fieldKey,
        field_value: value === "" ? null : value,
      });
      await sqlRead(`EXEC sp_SaveTakeoffCriterion @payload = '${payload}'`);
    } catch (e) {
      setCriteria(p => {
        const n = { ...p };
        if (prev === undefined) delete n[key]; else n[key] = prev;
        return n;
      });
      alert("Error saving criteria: " + e.message);
    }
  }

  // ── AI takeoff prefill ────────────────────────────────────────────────────────
  // 202 + poll background-job pattern (same as contract/schedule analysis).
  // The server extracts rooms + canvas positions and writes them via
  // sp_ApplyTakeoffAnalysis (append-only — never overwrites manual rooms).
  // bodyExtra is either { sheet_id } — re-analyze an already-loaded, already-
  // calibrated drawing sheet, trusting its known scale over an OCR guess — or
  // { pdf_base64, pdf_filename } for a legacy fresh upload that also creates
  // a new sheet. Only sheet_id is reachable from the UI (see the canvas's
  // "Auto-Takeoff" button); pdf_base64 stays supported server-side.
  async function runPrefillJob(bodyExtra) {
    if (isLocked || analyzing) return;
    setAnalyzing(true);
    try {
      const startedAt = Date.now();
      const res = await fetch(TAKEOFF_ANALYSIS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        body: JSON.stringify({ revision_id: activeRevId, ...bodyExtra }),
      });
      if (res.status !== 202 && !res.ok) {
        const detail = await res.json().catch(() => null);
        throw new Error(`Analysis kickoff failed (${res.status})${detail?.error ? `: ${detail.error}` : ""}${detail?.received_keys ? ` [sent: ${detail.received_keys.join(", ")}]` : ""}`);
      }

      prefillPoll.start(async (stop) => {
        try {
          const r = await sqlRead(`EXEC sp_GetTakeoffAnalysisJobStatus @revision_id=${activeRevId}`);
          const job = (r?.Table1 || [])[0];
          // Only react to a job created by this kickoff
          if (!job || new Date(job.CreatedAt).getTime() < startedAt - 60000) return;
          if (job.Status === "Complete") {
            stop();
            setAnalyzing(false);
            load();
            alert(`Takeoff prefill complete — ${job.RoomCount ?? 0} rooms added. Review dims and positions on the Layout Canvas.`);
          } else if (job.Status === "Failed") {
            stop();
            setAnalyzing(false);
            alert("Takeoff analysis failed: " + (job.ErrorMessage || "unknown error"));
          }
        } catch (e) { console.error("Takeoff poll error:", e); }
      }, 5000);
    } catch (e) {
      setAnalyzing(false);
      alert("Error starting analysis: " + e.message);
    }
  }

  function prefillSheet() {
    if (!activeSheetId) return;
    if (isBlobPointer(sheets.find(s => s.SheetID === activeSheetId)?.BackgroundImage)) { alert("AI prefill cannot read drawings kept in drawing storage yet — it needs the drawing loaded the older way."); return; }
    if (activeSheetRotation) { alert("AI prefill does not support a rotated drawing yet. Set the drawing's rotation back to 0° first (only possible before any takeoff is drawn on it)."); return; }
    runPrefillJob({ sheet_id: activeSheetId });
  }

  // Room-level scope applies to every room by default; toggling off stores an
  // exclusion row so line generation can skip that item for that room.
  async function toggleRoomScope(room, scopeItemId, excluded) {
    if (isLocked) return;
    const key = `${room.RoomID}|${scopeItemId}`;
    setScopeExclusions(prev => {
      const n = new Set(prev);
      if (excluded) n.add(key); else n.delete(key);
      return n;
    });
    try {
      const payload = payloadOf({ room_id: room.RoomID, scope_item_id: scopeItemId, excluded: excluded ? 1 : 0 });
      await sqlRead(`EXEC sp_ToggleRoomScopeExclusion @payload = '${payload}'`);
    } catch (e) {
      setScopeExclusions(prev => {
        const n = new Set(prev);
        if (excluded) n.delete(key); else n.add(key);
        return n;
      });
      alert("Error saving scope exclusion: " + e.message);
    }
  }

  // ── Zone save/delete/add ──────────────────────────────────────────────────────
  async function addZone() {
    if (isLocked) return;
    setAddingZone(true);
    try {
      const payload = payloadOf({ revision_id: activeRevId, zone_name: `AHU-${zones.length + 1}` });
      await sqlRead(`EXEC sp_SaveTakeoffZone @payload = '${payload}'`);
      load();
    } catch (e) { alert("Error adding AHU: " + e.message); }
    finally { setAddingZone(false); }
  }

  async function saveZone(zone) {
    if (isLocked) return;
    try {
      const payload = payloadOf({
        ahu_zone_id: zone.AHUZoneID,
        revision_id: activeRevId,
        zone_name:   zone.ZoneName,
        ahu_type:    zone.AHUType,
        qty:              zone.Qty ?? 1,
        standby_units:    zone.StandbyUnits ?? 0,
        served_by_equipment_id: zone.ServedByEquipmentID ?? null,
        clear_served_by:  zone.ServedByEquipmentID ? 0 : 1,
        outside_air_pct:  zone.OutsideAirPct ?? null,
      });
      await sqlRead(`EXEC sp_SaveTakeoffZone @payload = '${payload}'`);
      setZones(prev => prev.map(z => z.AHUZoneID === zone.AHUZoneID ? zone : z));
    } catch (e) { alert("Error saving AHU: " + e.message); }
  }

  async function deleteZone(zone) {
    if (isLocked) return;
    if (!window.confirm(`Remove "${zone.ZoneName}"? Its rooms stay in the takeoff, unassigned.`)) return;
    try {
      const payload = payloadOf({ ahu_zone_id: zone.AHUZoneID, revision_id: activeRevId, is_delete: 1 });
      await sqlRead(`EXEC sp_SaveTakeoffZone @payload = '${payload}'`);
      load();
    } catch (e) { alert("Error: " + e.message); }
  }

  // ── Derived totals ────────────────────────────────────────────────────────────
  // Walls between rooms are a per-DRAWING fact: rooms on different sheets
  // share one coordinate space but not a physical location, so edges are
  // detected within each sheet's rooms and the results combined (totals still
  // cover every sheet). Rooms with no sheet (unplaced / legacy) group together.
  const layoutEdges = [...groupBySheet(rooms).values()].flatMap(group => detectEdges(group, edgeOverrides, tunables));
  const layoutWalls = wallTakeoff(rooms, layoutEdges);

  // Construction Standard rules (Room Boundary Intelligence — see project
  // memory), reshaped into the Map computeAutoWallSegments expects: keyed
  // by pairKey(RoomTemplateAID, RoomTemplateBID) so lookup is order-
  // independent, same as the pair's own storage; the value is every ACTIVE
  // rule for that pair (a pair can carry several, narrowed by relationship/
  // gap range/priority — resolveConstructionRule picks one per Edge Run).
  // sp_GetConstructionStandardRules returns retired rules too, for the admin
  // page's "Reactivate" — they must never fill a wall.
  // Each room's EFFECTIVE wall system — its own substitution else its Room
  // Template's default (db/21 + db/55 Wall*). Feeds the auto-wall fill where
  // no Construction Standard pair rule matches (takeoffGeometry.js
  // computeAutoWallSegments opts.wallSystemByRoom), the Review Walls chooser
  // and the flyout's Wall system field (as room._wallSystem).
  const wallSystemByRoom = useMemo(() => {
    const byId = new Map(roomTemplates.map(t => [t.RoomTemplateID, t]));
    const m = new Map();
    // A room with no per-position data keeps the plain "all walls" system (the shape every consumer
    // had before positions); one with any carries `byPosition`, resolved room > template, position > all.
    for (const r of rooms) {
      const w = effectiveWallSystems(r, byId);
      if (w.hasPositions) m.set(r.RoomID, { ...(w.all || { kind: null, itemId: null, assemblyId: null }), byPosition: w.byPosition });
      else if (w.all) m.set(r.RoomID, w.all);
    }
    return m;
  }, [rooms, roomTemplates]);

  const rulesByPair = useMemo(() => {
    const m = new Map();
    for (const r of constructionStandardRules) {
      if (!r.IsActive) continue;
      const k = pairKey(r.RoomTemplateAID, r.RoomTemplateBID);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push({
        ruleId: r.RuleID, kind: r.Kind, itemId: r.ItemID, assemblyId: r.AssemblyID,
        relationshipType: r.RelationshipType ?? null,
        distanceMinFt: r.DistanceMinFt == null ? null : +r.DistanceMinFt,
        distanceMaxFt: r.DistanceMaxFt == null ? null : +r.DistanceMaxFt,
        priority: r.Priority ?? 100,
      });
    }
    return m;
  }, [constructionStandardRules]);

  // Auto-generated wall-type conditions (2026-08-28) — "marries" the room-
  // adjacency logic above (detectEdges/wallTakeoff, same gap/shared-wall/
  // cavity classification the "Net wall SF" tile already uses) into the
  // Condition Library's own cost-coded/priced condition records, instead of
  // requiring every wall to be manually re-traced. All the geometry stays
  // client-side (computeAutoWallSegments, takeoffGeometry.js); this effect
  // just reconciles the resulting segment list against persisted rows
  // whenever the layout actually changes, via sp_SyncAutoWallConditions
  // (keyed upsert/delete that preserves a manually-assigned TakeoffConditionLibraryID
  // across the change, now also filling a still-blank wall from a matching
  // Construction Standard rule — see that proc's own comment). Debounced so
  // a burst of edits (e.g. dragging several rooms) doesn't fire a sync per
  // intermediate state. Gated on `loading` being false — rooms/edgeOverrides
  // both populate together at the end of load(), and syncing against an
  // empty `rooms` before that would delete every existing auto-wall
  // condition for this revision.
  useEffect(() => {
    if (loading || isLocked || !activeRevId || !rooms.length) return;
    const t = setTimeout(() => {
      // Sheet-scoped: this sync owns only the ACTIVE drawing's auto walls (the proc's
      // NOT MATCHED BY SOURCE delete must not remove another sheet's walls). With no
      // drawings at all it covers the whole revision, as before.
      const sheetRoomIds = new Set(filterToSheet(rooms, activeSheetId).map(r => r.RoomID));
      const sheetRooms = rooms.filter(r => sheetRoomIds.has(r.RoomID));
      const sheetEdges = layoutEdges.filter(e => sheetRoomIds.has(e.a?.RoomID) && sheetRoomIds.has(e.b?.RoomID));
      const segments = computeAutoWallSegments(sheetRooms, sheetEdges, rulesByPair, tunables, { wallSystemByRoom, predictWall: wallLearning?.predictWall || null });
      const payload = payloadOf({
        revision_id: activeRevId,
        sheet_id: activeSheetId ?? null,
        segments: segments.map(s => ({
          key: s.key, legacy_key: s.legacyKey || null, room_id: s.roomId, room_b_id: s.roomBId,
          length_ft: s.lengthFt, height_ft: s.heightFt,
          geometry: s.points ? { points: s.points, lengthFt: s.lengthFt } : null,
          kind: s.kind, item_id: s.itemId, assembly_id: s.assemblyId,
          // what the engine knew + what filled it (db/37 AutoWall* — the wall-learning capture)
          rel: s.rel, gap_ft: s.gapFt, tier: s.tier, tpl_a: s.tplA, tpl_b: s.tplB, source: s.source || null, rule_id: s.ruleId ?? null,
        })),
      });
      sqlRead(`EXEC sp_SyncAutoWallConditions @payload = '${payload}'`)
        .then(() => loadConditions())
        .catch(e => console.error("Auto-wall condition sync failed:", e));
    }, 500);
    return () => clearTimeout(t);
    // rulesByPair included so a newly-saved/edited Construction Standard
    // rule immediately re-syncs the current layout and retroactively fills
    // any matching wall that's still blank, rather than waiting for an
    // unrelated layout edit to trigger the next sync. tunables included the
    // same way — an admin-edited Takeoff Variable value should re-classify
    // the current layout immediately too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, edgeOverrides, activeRevId, activeSheetId, isLocked, loading, rulesByPair, tunables, wallSystemByRoom, wallLearning]);

  // Layout Canvas "snap to real wall": fetch the active sheet's wall
  // linework once per sheet, not per drag-frame — a drag calls snapPosition()
  // dozens of times a second, and this is a network round-trip. Cached by
  // SheetID so switching sheets and back doesn't re-fetch. Silently leaves
  // the cache entry unset (never null) on any failure — the canvas's own
  // wallSegments lookup treats "not yet fetched" and "nothing available"
  // identically (falls back to room-to-room snapping), so there's nothing
  // useful to surface to the estimator either way.
  useEffect(() => {
    if (!activeSheetId || !activeRevId || wallSegmentsBySheet[activeSheetId] !== undefined) return;
    if (activeSheetRotation) return;   // the server reads the PDF unrotated: a rotated drawing has no usable wall linework
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(WALL_SEGMENTS_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(await authHeader()) },
          body: JSON.stringify({ sheet_id: activeSheetId, revision_id: activeRevId }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setWallSegmentsBySheet(prev => ({ ...prev, [activeSheetId]: data.available ? data : null }));
      } catch (e) { console.error("Wall-segment fetch failed:", e); }
    })();
    return () => { cancelled = true; };
  }, [activeSheetId, activeRevId, wallSegmentsBySheet, activeSheetRotation]);

  // Room Templates — a room's own criteria overrides still win, but
  // effectiveCriterion()'s fallback tier resolves off whichever template (if
  // any) the room picked (the legacy hardcoded ROOM_TYPE_DEFAULTS this
  // replaced was retired in Phase F-2). Attached directly on the room object
  // (same "_cfm"-style convention already used for SystemView, not a new
  // function parameter) so every existing effectiveCriterion() call site —
  // roomScopeFormulas/hvacLoadEngine/TakeoffCriteriaPanel/TakeoffViews —
  // picks it up with zero signature changes.
  const templateDefaultsByTemplateId = useMemo(() => {
    const map = {};
    for (const c of roomTemplateCriteria) {
      (map[c.RoomTemplateID] ||= {})[`${c.Discipline}|${c.FieldKey}`] = c.FieldValue;
    }
    return map;
  }, [roomTemplateCriteria]);
  const roomsWithTemplateDefaults = useMemo(
    () => rooms.map(r => ({ ...r, _templateDefaults: r.RoomTemplateID ? (templateDefaultsByTemplateId[r.RoomTemplateID] || null) : null, _wallSystem: wallSystemByRoom.get(r.RoomID) || null })),
    [rooms, templateDefaultsByTemplateId, wallSystemByRoom]
  );

  // What the drawing canvas shows: only the ACTIVE sheet's rooms and drawn
  // conditions. A row with no sheet (an unplaced room in the tray, a manual
  // condition, legacy rows in a revision that had no drawings) shows on every
  // sheet; with no drawings at all everything shows. Schedules, engines and the
  // estimate keep using the full revision-wide lists.
  const canvasRooms = useMemo(() => filterToSheet(roomsWithTemplateDefaults, activeSheetId), [roomsWithTemplateDefaults, activeSheetId]);
  const canvasConditions = useMemo(() => filterToSheet(takeoffConditions, activeSheetId), [takeoffConditions, activeSheetId]);
  // What is drawn on each drawing (all of them, not just the working one) — the canvas shows it on the sheet cards and key-sheet jump.
  const sheetCounts = useMemo(() => sheetTakeoffCounts(roomsWithTemplateDefaults, takeoffConditions), [roomsWithTemplateDefaults, takeoffConditions]);

  const derived = useMemo(
    () => roomsWithTemplateDefaults.map(r => ({ room: r, d: deriveRoom(r, criteria, systemsCtx) })),
    [roomsWithTemplateDefaults, criteria, systemsCtx]
  );
  const complete = derived.filter(x => x.d);

  // ── Engineering results + generated ceiling symbols sync ──────────────────────
  // Phases 3–5 of the HEPA & Lighting engine. On every change to the live
  // calculation: (1) reconcile each placed room's generated HEPA / light
  // symbols against its EFFECTIVE counts (autoLayoutEngine.js — keyed
  // amend-in-place, estimator edits respected, see that module's header);
  // (2) persist the per-room engineering snapshot (db/69) for the rows
  // whose result actually changed, recording how many symbols were
  // generated. Write-through on read, same convention as the auto-wall and
  // tile-count syncs; a signature of the exact write is remembered so a
  // failed or no-op write can't loop, and nothing runs until this
  // revision's stored rows have loaded (engineeringRows === null).
  useEffect(() => {
    if (isLocked || !activeRevId || loading || engineeringRows === null) return;
    const engByRoom = new Map(engineeringRows.map(r => [r.RoomID, r]));
    const upserts = [], deleteKeys = [], changedRows = [], keepIds = [];
    for (const { room, d } of derived) {
      if (!d) continue;
      keepIds.push(room.RoomID);
      const s = d.systems;
      const engRow = engByRoom.get(room.RoomID);
      let generated = { hepa: engRow?.HEPAGeneratedCount ?? null, light: engRow?.LightingGeneratedCount ?? null };
      if (room.CanvasX != null && room.CanvasX !== "") {
        const entities = Object.fromEntries(AUTO_SYSTEMS.map(sys => [sys, resolveAutoSystemEntity(sys, room, roomTemplateEquipment, conditionTypes, roomElements)]));
        const plan = computeAutoSystemPlan({
          room, entities, engRow, tunables, existingRows: takeoffConditions,
          effective: { hepa: s.hvac.hepa.effectiveQuantity, light: s.electrical.lighting.effectiveQuantity },
        });
        upserts.push(...plan.upserts);
        deleteKeys.push(...plan.deleteKeys);
        generated = plan.generated;
      }
      const row = buildEngineeringRow(room, s, generated);
      if (engineeringRowDiffers(row, engRow)) changedRows.push(row);
    }
    const prune = engineeringRows.some(r => !keepIds.includes(r.RoomID));
    if (!upserts.length && !deleteKeys.length && !changedRows.length && !prune) return;
    const sig = JSON.stringify({ upserts, deleteKeys, changedRows, keepIds });
    if (autoSyncSigRef.current === sig) return;
    autoSyncSigRef.current = sig;
    (async () => {
      if (upserts.length || deleteKeys.length) {
        await sqlRead(`EXEC sp_SyncAutoSystemConditions @payload = '${payloadOf({ revision_id: activeRevId, upserts, delete_keys: deleteKeys })}'`);
      }
      if (changedRows.length || prune) {
        await sqlRead(`EXEC sp_SaveTakeoffRoomEngineering @payload = '${payloadOf({ revision_id: activeRevId, rooms: changedRows, keep_room_ids: keepIds })}'`);
      }
      if (upserts.length || deleteKeys.length) { loadConditions(); broadcastChange(); }
      loadEngineering();
    })().catch(e => console.error("Engineering / generated-symbol sync failed:", e));
  }, [derived, takeoffConditions, engineeringRows, conditionTypes, roomTemplateEquipment, roomElements, tunables, isLocked, activeRevId, loading, loadConditions, loadEngineering]);

  // Phase 5 "Regenerate layout": drop one room+system's generated symbols and
  // its generated-count marker, so the sync above lays it out fresh (and any
  // dragged positions are forgotten — that's the point of the button).
  async function resetAutoLayout(room, system) {
    if (isLocked || !room || !AUTO_SYSTEMS.includes(system)) return;
    try {
      await sqlRead(`EXEC sp_SyncAutoSystemConditions @payload = '${payloadOf({ revision_id: activeRevId, reset: [{ room_id: room.RoomID, system }] })}'`);
      autoSyncSigRef.current = null;
      loadConditions();
      loadEngineering();
      broadcastChange();
    } catch (e) { alert("Error regenerating layout: " + e.message); }
  }

  // ── By Room header click-to-sort ──────────────────────────────────────────────
  function scheduleSortValue(key, { room, d }) {
    switch (key) {
      case "roomNumber": { const n = Number(room.RoomNumber); return Number.isFinite(n) && room.RoomNumber !== "" ? n : (room.RoomNumber || ""); }
      case "room":   return (room.RoomName || "").toLowerCase();
      case "class": {
        const c = classifications.find(x => x.ClassificationID === room.ClassificationID);
        return c ? Number(c.TargetACH) || 0 : -1;
      }
      case "template": return (roomTemplates.find(t => t.RoomTemplateID === room.RoomTemplateID)?.Name || "").toLowerCase();
      case "length": return Number(room.LengthFt) || 0;
      case "width":  return Number(room.WidthFt) || 0;
      case "height": return Number(room.HeightFt) || 0;
      case "ach":    return Number(room.ACH) || 0;
      case "ahu":    return (zones.find(z => z.AHUZoneID === room.AHUZoneID)?.ZoneName || "").toLowerCase();
      case "derived": return d?.cfm ?? -1;
      default: return 0;
    }
  }
  function toggleScheduleSort(key) {
    if (!key) return;
    setScheduleSort(prev => prev.key === key ? { key, dir: -prev.dir } : { key, dir: 1 });
  }
  const sortedDerived = scheduleSort.key
    ? [...derived].sort((a, b) => {
        const va = scheduleSortValue(scheduleSort.key, a), vb = scheduleSortValue(scheduleSort.key, b);
        const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb));
        return cmp * scheduleSort.dir;
      })
    : derived;
  const totals = complete.reduce((acc, { d }) => ({
    floorSF:     acc.floorSF + d.floorSF,
    grossWallSF: acc.grossWallSF + d.grossWallSF,
    vol:         acc.vol + (d.vol || 0),
    cfm:         acc.cfm + (d.cfm || 0),
    hepas:       acc.hepas + (d.hepaCount || 0),
    lights:      acc.lights + (d.lightCount || 0),
  }), { floorSF: 0, grossWallSF: 0, vol: 0, cfm: 0, hepas: 0, lights: 0 });

  const zoneStats = zones.map(z => {
    const zr = complete.filter(({ room }) => room.AHUZoneID === z.AHUZoneID);
    const cfm = zr.reduce((s, { d }) => s + (d.cfm || 0), 0);
    return {
      zone: z,
      roomCount: rooms.filter(r => r.AHUZoneID === z.AHUZoneID).length,
      cfm,
      // OutsideAirPct (Mechanical Systems tab) overrides the flat 30% guess
      // once an estimator sets it per AHU; oa100 zones stay 100% regardless.
      oaCfm: z.AHUType === "oa100" ? cfm : cfm * (z.OutsideAirPct ?? 0.3),
      hepas: zr.reduce((s, { d }) => s + (d.hepaCount || 0), 0),
      returnDia: cfm > 0 ? roundDia(cfm, RETURN_FPM) : null,
    };
  });
  const unassignedCount = rooms.filter(r => !r.AHUZoneID).length;

  const cellNum = {
    width: "100%", padding: "4px 6px", fontSize: 12.5, textAlign: "right",
    border: "1px solid var(--cs-border)", borderRadius: 5,
    background: "var(--surface)", color: "var(--text)",
  };
  const gridCols = "70px minmax(160px, 1.3fr) 120px 140px 64px 64px 64px 64px 130px minmax(170px, 1fr) 34px";

  if (loading) return <div style={{ color: "var(--text3)", fontSize: 13 }}>Loading room takeoff…</div>;

  const loadBanner = loadState.failed.length > 0 ? (
    <div role="alert" style={{ margin: "0 0 10px", padding: "8px 12px", border: "1px solid #dc2626", borderRadius: 6, background: "rgba(220,38,38,0.08)", color: "#dc2626", fontSize: 12, fontWeight: 600, display: "flex", gap: 12, alignItems: "center" }}>
      <span style={{ flex: 1 }}>⚠️ {describeLoadFailure(loadState)}</span>
      <button onClick={() => { load(); loadRoomElements(); }} style={{ fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 6, border: "1px solid #dc2626", background: "var(--surface)", color: "#dc2626", cursor: "pointer" }}>Retry</button>
    </div>
  ) : null;

  if (revisions.length === 0) return (
    <div style={{ maxWidth: 560 }}>
      <div className="panel-title">Arch Takeoff</div>
      <div className="panel-sub">No revisions yet. Create a revision from the Revisions tab to start the takeoff.</div>
    </div>
  );

  if (canvasOnly) return (
    // A 10px margin around the canvas so its own bordered container reads as
    // a framed drawing area instead of sitting flush against the dedicated
    // window's edge — with no chrome/padding of any kind here (this bypasses
    // EstimateDetail entirely, per the canvasOnly comment above), a border at
    // x=0 is easy to miss and background-drawing content panned near the
    // edge looks like it's bleeding off the screen.
    // The canvas window is its own page root, so it carries the interface theme itself (data-theme) and paints its own ground.
    <div data-theme={uiTheme} style={{ height: "100vh", padding: 10, boxSizing: "border-box", overflowY: "auto", ...(uiTheme === "dark" ? { background: "var(--bg)", color: "var(--text)" } : null) }}>
      {loadBanner}
      <TakeoffCanvas
        rooms={canvasRooms}
        zones={zones}
        sheets={sheets}
        activeSheetId={activeSheetId}
        wallSegments={activeSheetRotation ? null : wallSegmentsBySheet[activeSheetId]}
        onSelectSheet={setActiveSheetId}
        onAddSheet={addSheet}
        onDuplicateSheet={duplicateSheet}
        onLoadSheetVersions={loadSheetVersions}
        onRestoreSheetVersion={restoreSheetVersion}
        onRenameSheet={renameSheet}
        onReorderSheet={reorderSheet}
        onDeleteSheet={deleteSheet}
        onSaveSheetMeta={saveSheetMeta}
        onSaveSheetLegend={saveSheetLegend}
        sheetTakeoffCounts={sheetCounts}
        onPrefillSheet={prefillSheet}
        analyzing={analyzing}
        overrides={edgeOverrides}
        tunables={tunables}
        chaseAttrs={chaseAttrs}
        rulesByPair={rulesByPair}
        wallLearning={wallLearning}
        isLocked={isLocked}
        onMoveRoom={moveRoom}
        onMoveRooms={moveRooms}
        onAcceptMachine={acceptMachineItems}
        onResizeRoom={resizeRoom}
        onDeleteRooms={deleteRooms}
        onEditRoomPolygon={editRoomPolygon}
        onSaveCanvas={saveActiveSheet}
        onCycleEdge={cycleEdge}
        onSetEdgeType={setEdgeType}
        onSaveChaseAttributes={saveChaseAttributes}
        onAddRoom={addRoomFromCanvas}
        onCreateRoom={createRoom}
        onPlaceDrawnRoom={placeDrawnRoom}
        scopeItems={scopeItems.filter(i => optionSetForItem(i))}
        scopeApps={scopeApps}
        onDeleteScopeApp={deleteScopeApp}
        conditionTypes={conditionTypes}
        rawConditionTypes={catalogConditionTypes}
        onCreateOneOff={createOneOff}
        onSaveTypeOverride={saveTypeOverride}
        takeoffConditions={canvasConditions}
        routes={canvasRoutes} onDrawRouteSegment={drawRouteSegment} onClearRouteGeometry={clearRouteGeometry}
        onCreateTakeoffCondition={createTakeoffCondition}
        onCreateAreaCondition={createAreaCondition}
        onCreateBackOut={createBackOut}
        undoInfo={historyView}
        onUndo={undoLast} onRedo={redoLast}
        onDeleteTakeoffCondition={deleteTakeoffCondition}
        onDeleteTakeoffConditions={deleteTakeoffConditions}
        onDuplicateConditions={duplicateConditions}
        onTransformConditions={transformConditions}
        onSetRoomLocked={setRoomsPositionLocked}
        onSaveTakeoffCondition={saveConditionAttributes}
        onCreateManualCondition={createManualCondition}
        onPlaceSymbol={placeSymbol}
        saving={savingId != null}
        revisionId={activeRevId}
        sweepConditions={takeoffConditions}
        onCountByExample={countByExample}
        snapshotsSlot={<TakeoffSnapshotsMenu disabled={!activeRevId} isLocked={isLocked} loadSnapshots={loadSnapshots} onSave={saveSnapshot} onRestore={restoreSnapshot} onDelete={deleteSnapshot} />}
        onFlipSymbol={flipSymbol}
        onMoveSymbol={moveSymbol}
        criteria={criteria}
        scopeByDiscipline={scopeByDiscipline}
        scopeExclusions={scopeExclusions}
        onSaveRoom={saveRoom}
        onSaveCriterion={saveCriterion}
        onToggleRoomScope={toggleRoomScope}
        roomScopeItems={roomScopeItems}
        onSaveRoomScopeItem={saveRoomScopeItem}
        onDeleteRoomScopeItem={deleteRoomScopeItem}
        costCodes={costCodes}
        roomTemplates={roomTemplates}
        onChangeRoomTemplate={changeRoomTemplate}
        systemsCtx={systemsCtx}
        onResetAutoLayout={resetAutoLayout}
        roomElements={roomElements}
        roomTemplateEquipment={roomTemplateEquipment}
        catalog={{ items: catalogItems, assemblies: catalogAssemblies, formulas: catalogFormulas }}
        onSaveRoomElement={saveRoomElement}
        onDeleteRoomElement={deleteRoomElement}
        layers={takeoffLayers}
        haloColors={haloColors}
        onSaveHaloColors={saveHaloColors}
        reviewStates={reviewStates}
        onSaveReviewState={saveReviewState}
        currentUserEmail={currentUserEmail}
      />
    </div>
  );

  return (
    <div data-theme={uiTheme} style={{ maxWidth: "none", ...(uiTheme === "dark" ? { background: "var(--bg)", color: "var(--text)", borderRadius: 8, padding: 8 } : null) }}>
      {loadBanner}
      {reportOpen && (
        <TakeoffReport
          rooms={rooms} conditions={takeoffConditions} sheets={sheets}
          project={`Estimate-${projectId}`}
          revision={(() => { const r = revisions.find(x => x.RevisionID === activeRevId); return r ? `Rev ${r.RevisionNumber}` : ""; })()}
          unreviewedText={describeUnreviewed(summarizeUnreviewed({ rooms, conditions: takeoffConditions, elements: roomElements }).counts)}
          onClose={() => setReportOpen(false)}
        />
      )}
      {/* Header: title left, revision right */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 12 }}>
        <div style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "var(--text)", letterSpacing: "-0.01em" }}>Takeoff &amp; Design</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {isLocked && (
            <span style={{ fontSize: 11, color: "#d97706", fontWeight: 600, padding: "2px 8px", borderRadius: 100, background: "var(--tint-amber)" }}>
              Locked — create new revision to edit
            </span>
          )}
          <span style={{ fontSize: 12, color: "var(--text3)" }}>Revision:</span>
          <select
            value={activeRevId || ""}
            onChange={e => setActiveRevId(+e.target.value)}
            style={{ height: 30, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 8px", fontSize: 12, color: "var(--text)", background: "var(--surface)", outline: "none" }}
          >
            {revisions.map(r => (
              <option key={r.RevisionID} value={r.RevisionID}>
                Rev {r.RevisionNumber}: {r.RevisionLabel}{r.IsLocked ? " 🔒" : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* One aligned row of captioned cards: what to open and which view (left), files / checkpoints / reports (right).
          Every card shares one style so captions and buttons line up on a single baseline. */}
      {(() => {
        const GROUPS = [
          ["Enter & review rooms", [["grid", "▦ Grid"], ["schedule", "📋 By Room"], ["discipline", "🛠 By Discipline"], ["system", "🌀 By System"]]],
          ["Engineering", [["mechanical", "❄️ Mechanical Systems"], ["engineering", "📐 Engineering"]]],
          ["Outputs & history", [["schedules", "📑 Design Schedules"], ["compare", "🔀 Compare revisions"]]],
        ];
        const cap = { fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em" };
        const card = (caption, kids) => (
          <div key={caption} style={{ display: "flex", flexDirection: "column", gap: 4, padding: "5px 8px 7px", border: "1px solid var(--cs-border)", borderRadius: 8, background: "var(--surface)" }}>
            <span style={cap}>{caption}</span>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", minHeight: 30 }}>{kids}</div>
          </div>
        );
        const tab = (on) => ({
          height: 30, padding: "0 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
          background: on ? "var(--cs-blue)" : "var(--surface)",
          color: on ? "#fff" : "var(--text2)",
          border: `1px solid ${on ? "var(--cs-blue)" : "var(--cs-border)"}`,
          borderRadius: 999,
        });
        const hb = (off) => ({ height: 30, padding: "0 12px", whiteSpace: "nowrap", fontSize: 12, fontWeight: 700, cursor: off ? "default" : "pointer", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", background: "var(--surface)", color: "var(--text2)" });
        const exportRevision = (() => { const r = revisions.find(x => x.RevisionID === activeRevId); return r ? `Rev ${r.RevisionNumber}` : ""; })();
        return (
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "stretch" }}>
            {/* Layout Canvas is an ACTION (open/focus its own window), not a
                view toggle — it always lives in a dedicated window sized for a
                second monitor, never inline here (see canvasOnly above). The
                fixed window name "takeoff-canvas" means a second click refocuses
                the existing window instead of opening a duplicate. */}
            {card("Draw & count", (
              <button
                onClick={() => {
                  const w = Math.round(window.screen.availWidth * 0.9);
                  const h = Math.round(window.screen.availHeight * 0.9);
                  const url = `${window.location.pathname}?section=room-takeoff&view=canvas${activeRevId ? `&revision=${activeRevId}` : ""}`;
                  window.open(url, "takeoff-canvas", `width=${w},height=${h},menubar=no,toolbar=no,location=no,status=no`);
                }}
                style={{ height: 30, padding: "0 16px", fontSize: 13, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap", background: "#5b21b6", color: "#fff", border: "1px solid #5b21b6", borderRadius: 999 }}
                title="Opens the Takeoff Engine in its own window — drag it to a second monitor. Edits stay in sync live."
              >
                📐 Open Takeoff Engine ↗
              </button>
            ))}
            {GROUPS.map(([caption, tabs]) => card(caption, tabs.map(([v, label]) => (
              <button key={v} onClick={() => setView(v)} style={tab(view === v)}>{label}</button>
            ))))}
            <div style={{ display: "flex", gap: 8, flexBasis: "100%", flexWrap: "wrap", alignItems: "stretch" }}>
              {card("Drawings", <>
                {drawingsConfigured() && (
                  <button type="button" onClick={() => setSetDialogOpen(true)} disabled={!activeRevId || isLocked} title="Upload a PDF plan set once and pick the pages to take off" style={hb(!activeRevId || isLocked)}>
                    📚 Add from plan set
                  </button>
                )}
                <TakeoffFileMenu
                  disabled={!activeRevId} isLocked={isLocked} sheets={sheets} revisionId={activeRevId}
                  roomCount={rooms.length} conditionCount={takeoffConditions.length} host={window.location.host}
                  onExport={exportTakeoffFile} onImport={importTakeoffFile}
                  onExportArchive={exportTakeoffArchive} onCreateSheets={createSheetsFromArchive}
                />
              </>)}
              {card("Checkpoints", (
                <TakeoffSnapshotsMenu
                  disabled={!activeRevId} isLocked={isLocked}
                  loadSnapshots={loadSnapshots} onSave={saveSnapshot} onRestore={restoreSnapshot} onDelete={deleteSnapshot}
                />
              ))}
              {card("Reports", <>
                <button type="button" onClick={() => setReportOpen(true)} disabled={!rooms.length && !takeoffConditions.length}
                  title="A printable takeoff report — by condition, drawing or room, in your display units, with the columns you choose"
                  style={hb(!rooms.length && !takeoffConditions.length)}>📄 Takeoff report</button>
                <TakeoffExportMenu
                  rooms={rooms} conditions={takeoffConditions} sheets={sheets}
                  project={`Estimate-${projectId}`}
                  revision={exportRevision}
                />
              </>)}
            </div>
            {setDialogOpen && <DrawingSetDialog existingSheets={sheets} currentUser={currentUserEmail} isLocked={isLocked} onAdd={addSheetsFromSet} onClose={() => setSetDialogOpen(false)} />}
          </div>
        );
      })()}

      {/* Totals strip */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        {[
          ["Rooms", fmt(rooms.length)],
          ["Floor SF", fmt(totals.floorSF)],
          ["Ceiling SF", fmt(totals.floorSF)],
          ...(layoutWalls.placedCount > 0
            ? [["Net wall SF", fmt(layoutWalls.netWallSF), "shared walls deduped from the layout; cavity faces kept"]]
            : [["Gross wall SF", fmt(totals.grossWallSF), "before shared-wall dedup — place rooms on the layout canvas"]]),
          ["Volume CF", fmt(totals.vol)],
          ["Supply CFM", fmt(totals.cfm)],
          ["HEPAs / FFUs", fmt(totals.hepas)],
          ["Light fixtures", fmt(totals.lights)],
        ].map(([label, value, hint]) => (
          <div key={label} title={hint || ""} style={{ background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 8, padding: "8px 14px", minWidth: 90 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{value}{hint ? <span style={{ fontSize: 11, fontWeight: 600, color: "#d97706" }}> *</span> : null}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
          </div>
        ))}
      </div>

      {/* AHU zones */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "stretch" }}>
        {zoneStats.map(({ zone, roomCount, cfm, oaCfm, hepas, returnDia }) => (
          <div key={zone.AHUZoneID} style={{ background: "var(--surface)", border: "1px solid var(--cs-border)", borderLeft: "3px solid var(--cs-blue)", borderRadius: 8, padding: "8px 12px", minWidth: 210, position: "relative" }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
              <input
                defaultValue={zone.ZoneName}
                disabled={isLocked}
                onBlur={e => { const v = e.target.value.trim(); if (v && v !== zone.ZoneName) saveZone({ ...zone, ZoneName: v }); else e.target.value = zone.ZoneName; }}
                style={{ border: "none", background: "transparent", fontSize: 13, fontWeight: 700, color: "var(--text)", outline: "none", width: 90, fontFamily: "inherit" }}
              />
              <select
                value={zone.AHUType}
                disabled={isLocked}
                onChange={e => saveZone({ ...zone, AHUType: e.target.value })}
                style={{ fontSize: 11, border: "1px solid var(--cs-border)", borderRadius: 5, padding: "2px 4px", background: "var(--surface)", color: "var(--text2)" }}
              >
                {AHU_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {!isLocked && (
                <button onClick={() => deleteZone(zone)} title="Remove this AHU" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "#d1d5db", marginLeft: "auto", padding: 2 }}
                  onMouseEnter={e => e.currentTarget.style.color = "#dc2626"} onMouseLeave={e => e.currentTarget.style.color = "#d1d5db"}>✕</button>
              )}
            </div>
            <div style={{ fontSize: 11, color: "var(--text2)", display: "flex", gap: 10, flexWrap: "wrap", fontVariantNumeric: "tabular-nums" }}>
              <span>{roomCount} {roomCount === 1 ? "room" : "rooms"}</span>
              <span><strong>{fmt(cfm)}</strong> CFM</span>
              <span>{fmt(oaCfm)} OA</span>
              <span>{fmt(hepas)} HEPAs</span>
              {returnDia && <span>{returnDia}&Prime; return</span>}
            </div>
          </div>
        ))}
        {!isLocked && (
          <button
            onClick={addZone}
            disabled={addingZone}
            style={{ padding: "8px 16px", fontSize: 12, fontWeight: 700, background: "var(--surface)", color: "var(--text2)", border: "1px dashed var(--cs-border)", borderRadius: 8, cursor: "pointer", opacity: addingZone ? 0.5 : 1 }}
          >
            {addingZone ? "Adding…" : "+ Add AHU"}
          </button>
        )}
        {unassignedCount > 0 && zones.length > 0 && (
          <div style={{ alignSelf: "center", fontSize: 11, color: "#d97706", fontWeight: 600 }}>
            {unassignedCount} {unassignedCount === 1 ? "room" : "rooms"} not assigned to an AHU
          </div>
        )}
      </div>

      {/* Alternate WBS views — pivots over the same rooms/criteria/scope state */}
      {view === "discipline" && (
        <DisciplineView
          rooms={roomsWithTemplateDefaults}
          criteria={criteria}
          scopeByDiscipline={scopeByDiscipline}
          scopeExclusions={scopeExclusions}
          isLocked={isLocked}
          onSaveCriterion={saveCriterion}
          onToggleScope={toggleRoomScope}
        />
      )}
      {view === "system" && (
        <SystemView
          rooms={derived.map(({ room, d }) => ({ ...room, _cfm: d?.cfm ?? null }))}
          zones={zones}
          zoneStats={zoneStats}
          criteria={criteria}
          isLocked={isLocked}
          onSaveRoom={saveRoom}
          onSaveCriterion={saveCriterion}
          roomTemplates={roomTemplates}
        />
      )}
      {view === "grid" && (rooms.length === 0 ? (
        <div style={{ padding: "18px 20px", background: "var(--tint-blue)", border: "1px solid #bae6fd", borderRadius: 10, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0369a1", marginBottom: 4 }}>📏 Start the room schedule</div>
          <div style={{ fontSize: 12, color: "#1e3a5f", marginBottom: 10 }}>
            Add rooms first — pick a type to set the ACH default, enter dimensions, and this
            spreadsheet fills in as you go.
          </div>
          <button
            onClick={() => setView("schedule")}
            style={{ fontSize: 12, fontWeight: 600, padding: "6px 12px", background: "var(--cs-blue)", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}
          >
            📋 Go to By Room to add your first room
          </button>
        </div>
      ) : (
        <GridView
          rooms={roomsWithTemplateDefaults}
          criteria={criteria}
          isLocked={isLocked}
          onSaveRoom={saveRoom}
          onSaveCriterion={saveCriterion}
          roomTemplates={roomTemplates}
          onChangeRoomTemplate={changeRoomTemplate}
        />
      ))}
      {view === "mechanical" && (
        <MechanicalSystemsPanel
          activeRevId={activeRevId}
          isLocked={isLocked}
          rooms={roomsWithTemplateDefaults}
          zones={zones}
          criteria={criteria}
          saveZone={saveZone}
          classifications={classifications}
          systemsCtx={systemsCtx}
          scenarioDispositions={scenarioDispositions}
          onScenarioRowsChange={onScenarioRowsChange}
          currentUserEmail={currentUserEmail}
        />
      )}

      {/* Design Schedules (P1, src/config/designSchedules.js) — generated,
          read-only, publication-level-aware views of the canonical data this
          page already holds. Props only; nothing persisted. */}
      {view === "schedules" && (
        <DesignSchedulesView
          header={header}
          publication={header?.SchedulePublicationJson ?? null}
          onSavePublication={isLocked ? null : saveSchedulePublication}
          ctx={{ rooms: roomsWithTemplateDefaults, criteria, zones, classifications, roomTemplates, systemsCtx, takeoffConditions, equipment: equipmentRegister.equipment, connections: equipmentRegister.connections, panels: equipmentRegister.panels, circuits: equipmentRegister.circuits, utilitySystems: equipmentRegister.utilitySystems, scenarioDispositions, scenarioModel }}
        />
      )}

      {/* Track K — Compare revisions (src/utils/revisionCompare.js): what changed
          between a prior revision and this one across rooms, drawn conditions,
          Room Elements, engineering rows and manual estimate lines. Loads both
          sides itself through the same read procs; nothing persisted. */}
      {view === "compare" && <RevisionCompare revisions={revisions} activeRevId={activeRevId} />}

      {/* Engineering audit — the persisted per-room record (TakeoffRoomEngineering,
          db/69) of what the HEPA & Lighting engine computed, so the estimate can
          explain every quantity: calculated vs override vs effective, how many
          symbols were generated vs what's actually drawn, warnings, calc
          version and timestamp. Live values come from the same calculation the
          schedule shows; "Calculated at" is the stored snapshot's write time. */}
      {view === "engineering" && (() => {
        const engByRoom = new Map((engineeringRows || []).map(r => [r.RoomID, r]));
        const drawnCount = (roomId, system) => takeoffConditions.filter(c => c.RoomID === roomId && c.Style === "count" && c.EntityAutoSystem === system).length;
        const n = (v, d = 0) => v == null || !Number.isFinite(+v) ? "—" : (+v).toLocaleString(undefined, { maximumFractionDigits: d });
        const cell = { padding: "5px 8px", borderBottom: "1px solid var(--cs-border)", fontSize: 11.5, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
        const head = { ...cell, fontWeight: 700, color: "var(--text3)", fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.04em", background: "var(--surface)" };
        const hasEntity = (sys) => conditionTypes.some(t => t.AutoSystem === sys && t.Style === "count");
        return (
          <div>
            <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 8 }}>
              Per-room engineering record — calculated / override / effective for HEPAs and light fixtures, the symbols the engine
              generated on the canvas vs. what's drawn now, and warnings. Persisted on every change (calc v{complete[0]?.d.systems.version ?? 1});
              set overrides in a room's Detail Flyout. {!hasEntity("hepa") && <span style={{ color: "#b45309" }}>No catalog Item is tagged as the generated HEPA yet (Setup ▸ Estimate Engine ▸ Items ▸ Generated system) — HEPA symbols won't be generated. </span>}
              {!hasEntity("light") && <span style={{ color: "#b45309" }}>No catalog Item is tagged as the generated light fixture yet.</span>}
            </div>
            <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={head}>#</th><th style={{ ...head, textAlign: "left" }}>Room</th><th style={head}>SF</th><th style={head}>CFM</th>
                    <th style={{ ...head, borderLeft: "2px solid var(--cs-border)" }} colSpan={6}>HEPA — calc · ovr · eff · cfm/unit · gen · drawn</th>
                    <th style={{ ...head, borderLeft: "2px solid var(--cs-border)" }} colSpan={6}>Lights — calc · ovr · eff · W · gen · drawn</th>
                    <th style={{ ...head, borderLeft: "2px solid var(--cs-border)" }}>⚠</th><th style={head}>Calculated at (UTC)</th>
                  </tr>
                </thead>
                <tbody>
                  {complete.map(({ room, d }) => {
                    const s = d.systems, h = s.hvac.hepa, l = s.electrical.lighting, e = engByRoom.get(room.RoomID);
                    const gH = e?.HEPAGeneratedCount, gL = e?.LightingGeneratedCount;
                    const dH = drawnCount(room.RoomID, "hepa"), dL = drawnCount(room.RoomID, "light");
                    const warn = (cond) => cond ? { color: "#b45309", fontWeight: 700 } : {};
                    return (
                      <tr key={room.RoomID} title={s.warnings.map(w => w.message).join("\n")}>
                        <td style={cell}>{room.RoomNumber || "—"}</td>
                        <td style={{ ...cell, textAlign: "left", whiteSpace: "normal" }}>{room.RoomName}</td>
                        <td style={cell}>{n(s.geometry.floorAreaSf)}</td>
                        <td style={cell}>{n(h.requiredCfm)}</td>
                        <td style={{ ...cell, borderLeft: "2px solid var(--cs-border)" }}>{n(h.calculatedQuantity)}</td>
                        <td style={{ ...cell, ...warn(h.overrideQuantity != null) }}>{h.overrideQuantity ?? "—"}</td>
                        <td style={{ ...cell, fontWeight: 700 }}>{n(h.effectiveQuantity)}</td>
                        <td style={cell}>{n(h.actualAirflowPerUnit)}</td>
                        <td style={cell}>{gH ?? "—"}</td>
                        <td style={{ ...cell, ...warn(dH !== h.effectiveQuantity && (dH > 0 || gH != null)) }}>{dH}</td>
                        <td style={{ ...cell, borderLeft: "2px solid var(--cs-border)" }}>{l.calculatedQuantity ?? "—"}</td>
                        <td style={{ ...cell, ...warn(l.overrideQuantity != null) }}>{l.overrideQuantity ?? "—"}</td>
                        <td style={{ ...cell, fontWeight: 700 }}>{n(l.effectiveQuantity)}{l.quantitySource === "template" || l.quantitySource === "project" ? <span style={{ color: "var(--text3)", fontWeight: 400 }}> ({l.quantitySource})</span> : null}</td>
                        <td style={cell}>{n(l.totalWatts)}</td>
                        <td style={cell}>{gL ?? "—"}</td>
                        <td style={{ ...cell, ...warn(dL !== l.effectiveQuantity && (dL > 0 || gL != null)) }}>{dL}</td>
                        <td style={{ ...cell, borderLeft: "2px solid var(--cs-border)", ...warn(s.warnings.length > 0) }}>{s.warnings.length || "—"}</td>
                        <td style={{ ...cell, color: "var(--text3)" }}>{e?.CalculatedAt ? String(e.CalculatedAt).replace("T", " ").slice(0, 19) : "not yet saved"}</td>
                      </tr>
                    );
                  })}
                  {!complete.length && <tr><td style={cell} colSpan={18}>No rooms with complete dimensions yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Room schedule */}
      {view === "schedule" && (rooms.length === 0 ? (
        <div style={{ padding: "18px 20px", background: "var(--tint-blue)", border: "1px solid #bae6fd", borderRadius: 10, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0369a1", marginBottom: 4 }}>📏 Start the room schedule</div>
          <div style={{ fontSize: 12, color: "#1e3a5f" }}>
            Add rooms below — pick a template to set the ACH default, enter dimensions, and the
            takeoff quantities compute as you go. Group rooms under AHUs to see per-unit loads.
          </div>
        </div>
      ) : (
        <div style={{ border: "1px solid var(--cs-border)", borderRadius: 8, overflowX: "auto", marginBottom: 12 }}>
          <div style={{ minWidth: 940 }}>
            <div style={{ display: "grid", gridTemplateColumns: gridCols, background: "var(--surface2)", borderBottom: "1px solid var(--cs-border)" }}>
              {SCHEDULE_COLUMNS.map(({ label, key }, i) => (
                <div
                  key={i}
                  onClick={() => toggleScheduleSort(key)}
                  title={key ? "Click to sort" : undefined}
                  style={{
                    padding: "8px 10px", fontSize: 11, fontWeight: 700, color: "var(--text3)",
                    textTransform: "uppercase", letterSpacing: "0.06em", textAlign: i >= 4 && i <= 7 ? "right" : "left",
                    cursor: key ? "pointer" : "default", userSelect: "none",
                    display: "flex", alignItems: "center", gap: 3, justifyContent: i >= 4 && i <= 7 ? "flex-end" : "flex-start",
                  }}
                >
                  {label}
                  {scheduleSort.key === key && key && <span style={{ fontSize: 9 }}>{scheduleSort.dir === 1 ? "▲" : "▼"}</span>}
                </div>
              ))}
            </div>
            {sortedDerived.map(({ room, d }, i) => (
              <div key={room.RoomID}>
              <div style={{
                display: "grid", gridTemplateColumns: gridCols, alignItems: "center",
                borderBottom: (i < rooms.length - 1 || expandedRooms[room.RoomID]) ? "1px solid var(--cs-border)" : "none",
                background: i % 2 === 0 ? "transparent" : "var(--surface2)",
                opacity: savingId === room.RoomID ? 0.55 : 1,
              }}>
                <div style={{ padding: "6px 6px" }}>
                  <input
                    defaultValue={room.RoomNumber || ""}
                    disabled={isLocked}
                    title="Room number — auto-assigned, overwrite with anything (e.g. a drawing's own numbering)"
                    onBlur={e => { const v = e.target.value.trim(); if (v !== (room.RoomNumber || "")) saveRoom({ ...room, RoomNumber: v || null }); }}
                    style={{ width: "100%", border: "1px solid var(--cs-border)", borderRadius: 5, padding: "4px 6px", fontSize: 12, color: "var(--text)", background: "var(--surface)" }}
                  />
                </div>
                <div style={{ padding: "6px 10px", display: "flex", alignItems: "center", gap: 6 }}>
                  <button
                    onClick={() => setExpandedRooms(p => ({ ...p, [room.RoomID]: !p[room.RoomID] }))}
                    title={expandedRooms[room.RoomID] ? "Collapse discipline criteria" : "Expand discipline criteria (arch / mech / elec / process)"}
                    style={{
                      width: 18, height: 18, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 700, lineHeight: 1, cursor: "pointer",
                      color: expandedRooms[room.RoomID] ? "#fff" : "var(--cs-blue)",
                      background: expandedRooms[room.RoomID] ? "var(--cs-blue)" : "var(--cs-blue-lt)",
                      border: "none", borderRadius: 4,
                    }}
                  >
                    {expandedRooms[room.RoomID] ? "−" : "+"}
                  </button>
                  <input
                    defaultValue={room.RoomName}
                    disabled={isLocked}
                    onBlur={e => { const v = e.target.value.trim(); if (v && v !== room.RoomName) saveRoom({ ...room, RoomName: v }); else e.target.value = room.RoomName; }}
                    style={{ width: "100%", border: "none", background: "transparent", fontSize: 12.5, fontWeight: 600, color: "var(--text)", outline: "none", fontFamily: "inherit" }}
                  />
                </div>
                <div style={{ padding: "5px 6px" }}>
                  <select
                    value={room.ClassificationID ?? ""}
                    disabled={isLocked}
                    title="Cleanroom classification (ISPE/EU/FDA grade) — sets ACH from its target; edit the table on Mechanical Systems Configuration"
                    onChange={e => changeRoomClassification(room, e.target.value ? +e.target.value : null)}
                    style={{ width: "100%", fontSize: 11.5, border: "1px solid var(--cs-border)", borderRadius: 5, padding: "4px 4px", background: "var(--surface)", color: room.ClassificationID ? "var(--text)" : "var(--text3)" }}
                  >
                    <option value="">— none —</option>
                    {classifications
                      .filter(c => c.IsActive || c.ClassificationID === room.ClassificationID)
                      .map(c => (
                        <option key={c.ClassificationID} value={c.ClassificationID}>
                          {c.FDAGrade || c.ISPEGrade || c.EUGrade} ({c.TargetACH} {c.TargetACHUnit}){!c.IsActive ? " (retired)" : ""}
                        </option>
                      ))}
                  </select>
                </div>
                <div style={{ padding: "5px 6px" }}>
                  <select
                    value={room.RoomTemplateID ?? ""}
                    disabled={isLocked}
                    title="Standard room template (Setup > Estimate Engine > Rooms) — sets ACH/classification once, and feeds discipline-criteria defaults for any field this room hasn't overridden itself"
                    onChange={e => changeRoomTemplate(room, e.target.value ? +e.target.value : null)}
                    style={{ width: "100%", fontSize: 11.5, border: "1px solid var(--cs-border)", borderRadius: 5, padding: "4px 4px", background: "var(--surface)", color: room.RoomTemplateID ? "var(--text)" : "var(--text3)" }}
                  >
                    <option value="">— none —</option>
                    {roomTemplates
                      .filter(t => t.IsActive || t.RoomTemplateID === room.RoomTemplateID)
                      .map(t => <option key={t.RoomTemplateID} value={t.RoomTemplateID}>{t.Name}</option>)}
                  </select>
                </div>
                {["LengthFt", "WidthFt", "HeightFt", "ACH"].map(k => {
                  const mm = k === "ACH" ? achMismatch(room, classifications) : null;
                  return (
                  <div key={k} style={{ padding: "5px 6px" }}>
                    {k === "ACH" ? (
                      <input
                        key={`ach-${room.ACH ?? ""}`}
                        type="number"
                        title={mm ? `This room's class targets ${mm.target} ACH` : undefined}
                        defaultValue={room[k] ?? ""}
                        disabled={isLocked}
                        min={0}
                        onBlur={e => { const v = e.target.value === "" ? null : +e.target.value; if (v !== (room[k] ?? null)) saveRoom({ ...room, [k]: v }); }}
                        placeholder="—"
                        style={mm ? { ...cellNum, outline: "2px solid #f59e0b", outlineOffset: -1 } : cellNum}
                      />
                    ) : (
                      <FeetInchesInput valueFt={room[k]} disabled={isLocked} ariaLabel={k === "LengthFt" ? "Length" : k === "WidthFt" ? "Width" : "Height"}
                        style={cellNum} onCommit={v => saveRoom({ ...room, [k]: v })} />
                    )}
                    {mm && !isLocked && (
                      <button type="button" onClick={() => saveRoom({ ...room, ACH: mm.target })} title={`Set to the class target (${mm.target} ACH)`}
                        style={{ marginTop: 2, fontSize: 10, padding: "0 4px", background: "none", border: "none", color: "#b45309", cursor: "pointer", textDecoration: "underline" }}>
                        class: {mm.target}
                      </button>
                    )}
                  </div>
                  );
                })}
                <div style={{ padding: "5px 6px" }}>
                  <select
                    value={room.AHUZoneID ?? ""}
                    disabled={isLocked}
                    onChange={e => {
                      if (e.target.value === "__new") { createZoneAndAssign(room); return; }
                      saveRoom({ ...room, AHUZoneID: e.target.value ? +e.target.value : null });
                    }}
                    style={{ width: "100%", fontSize: 11.5, border: "1px solid var(--cs-border)", borderRadius: 5, padding: "4px 4px", background: "var(--surface)", color: room.AHUZoneID ? "var(--text)" : "#d97706" }}
                  >
                    <option value="">— unassigned —</option>
                    {zones.map(z => <option key={z.AHUZoneID} value={z.AHUZoneID}>{z.ZoneName}</option>)}
                    {!isLocked && <option value="__new">+ New AHU…</option>}
                  </select>
                </div>
                <div style={{ padding: "6px 10px", fontSize: 11.5, color: "var(--text2)", fontVariantNumeric: "tabular-nums" }}>
                  {d ? (
                    <>
                      <strong>{fmt(d.floorSF)}</strong> SF · <strong>{fmt(d.vol)}</strong> CF · <strong>{fmt(d.cfm)}</strong> CFM
                      {d.hepaCount ? <> · <strong>{d.hepaCount}</strong> HEPA{d.hepaCount > 1 ? "s" : ""}{d.systems?.hvac.hepa.overrideQuantity != null ? <span title={`Estimator override — calculated ${d.systems.hvac.hepa.calculatedQuantity}`} style={{ color: "#d97706" }}>*</span> : null}</> : null}
                      {d.lightCount ? <> · <strong>{d.lightCount}</strong> light{d.lightCount > 1 ? "s" : ""}{d.systems?.electrical.lighting.quantitySource === "override" ? <span title={`Estimator override — calculated ${d.systems.electrical.lighting.calculatedQuantity ?? "n/a"}`} style={{ color: "#d97706" }}>*</span> : null}</> : null}
                      {d.systems?.warnings?.length ? <span title={d.systems.warnings.map(w => w.message).join("\n")} style={{ marginLeft: 4, cursor: "help" }}>⚠️</span> : null}
                    </>
                  ) : (
                    <span style={{ color: "var(--text3)" }}>enter dimensions…</span>
                  )}
                </div>
                <div style={{ padding: "5px 4px", textAlign: "center" }}>
                  {!isLocked && (
                    <button onClick={() => deleteRoom(room)} title="Remove room" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#d1d5db", padding: "2px 4px" }}
                      onMouseEnter={e => e.currentTarget.style.color = "#dc2626"} onMouseLeave={e => e.currentTarget.style.color = "#d1d5db"}>🗑</button>
                  )}
                </div>
              </div>
              {expandedRooms[room.RoomID] && (
                <RoomCriteriaExpand
                  room={room}
                  criteria={criteria}
                  scopeByDiscipline={scopeByDiscipline}
                  scopeExclusions={scopeExclusions}
                  isLocked={isLocked}
                  onSave={saveCriterion}
                  onToggleScope={toggleRoomScope}
                />
              )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Add room */}
      {view === "schedule" && !isLocked && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            value={newRoom.name}
            onChange={e => setNewRoom({ ...newRoom, name: e.target.value })}
            onKeyDown={e => e.key === "Enter" && addRoom()}
            placeholder="Add room… (e.g. Sterile Suite 1)"
            style={{ flex: "1 1 220px", padding: "6px 10px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }}
          />
          <select
            value={newRoom.templateId ?? ""}
            onChange={e => setNewRoom({ ...newRoom, templateId: e.target.value ? +e.target.value : null })}
            style={{ padding: "6px 8px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: newRoom.templateId ? "var(--text)" : "var(--text3)" }}
          >
            <option value="">— no template —</option>
            {roomTemplates.filter(t => t.IsActive).map(t => <option key={t.RoomTemplateID} value={t.RoomTemplateID}>{t.Name}</option>)}
          </select>
          <button
            onClick={addRoom}
            disabled={adding || !newRoom.name.trim()}
            style={{
              padding: "6px 16px", fontSize: 13, fontWeight: 700,
              background: "var(--cs-blue)", color: "#fff",
              border: "none", borderRadius: 6, cursor: "pointer",
              opacity: (adding || !newRoom.name.trim()) ? 0.5 : 1,
              whiteSpace: "nowrap",
            }}
          >
            {adding ? "Adding…" : "+ Add Room"}
          </button>
        </div>
      )}

      {/* Discipline criteria subsections — Architectural / Mechanical / Electrical / Process */}
      {view === "schedule" && (
        <TakeoffCriteriaPanel
          rooms={roomsWithTemplateDefaults}
          criteria={criteria}
          scopeByDiscipline={scopeByDiscipline}
          isLocked={isLocked}
          onSave={saveCriterion}
          bodOptions={bodOptions}
        />
      )}
    </div>
  );
}
